import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import jsPDF from "jspdf";
import {
  AlertCircle, ArrowDown, BarChart3, BookMarked, BookOpen, Check, ChevronRight, Compass,
  Copy, Crown, Download, Edit2, Edit3, Eye, Feather, FileText, Headphones, Lightbulb,
  Loader2, Lock, LogOut, Map, MapPin, Menu, Moon, Navigation, Pause, PenTool, Play, PlayCircle, Plus,
  RefreshCw, RotateCcw, RotateCw, Save, Search, Send, Shield, ShieldCheck, SkipBack, SkipForward, Sparkles,
  Square, Sun, Tag, Target, Trash2, Upload, UserPlus, Users, Wand2, X, Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import ShweebumCat from "../components/ShweebumCat";
import ImportDialog from "../components/ImportDialog";
import SpeechToTextButton from "../components/SpeechToTextButton";
import CharacterEditor from "../components/CharacterEditor";
import { useAuth } from "../components/Auth/AuthProvider";
import { API } from "../lib/api";
import { generateEpub } from "../lib/epubGenerator";
import { playTick, playComplete } from "../sounds";
import { BackCoverBlurbModal } from "../components/writing/BackCoverBlurbModal";
import { BookCoverDesignModal } from "../components/writing/BookCoverDesignModal";
import { ManuscriptAnalysisModal } from "../components/writing/ManuscriptAnalysisModal";
import { StoryBibleModal } from "../components/writing/StoryBibleModal";
import { WritingToolsModal } from "../components/writing/WritingToolsModal";
import { ChapterPlanningModal } from "../components/writing/ChapterPlanningModal";
import { TensionCurveModal } from "../components/writing/TensionCurveModal";
import { StoryNavigatorModal } from "../components/writing/StoryNavigatorModal";
import StoryArchitectModal from "../components/writing/story-architect/StoryArchitectModal";
import { StoryDirectionModal } from "../components/writing/StoryDirectionModal";
import { SuggestCharactersModal } from "../components/writing/SuggestCharactersModal";
import { StoryDoctorModal } from "../components/writing/StoryDoctorModal";
import { WritingToolbar } from "../components/writing/WritingToolbar";
import { SmartRefineModal } from "../components/writing/SmartRefineModal";
import AIDetectionModal from "../components/writing/AIDetectionModal";
import SynopsisLadderModal from "../components/writing/SynopsisLadderModal";
import CanonLockModal from "../components/writing/CanonLockModal";
import CharacterPortraitModal from "../components/writing/CharacterPortraitModal";
import LocalEditorModal from "../components/writing/LocalEditorModal";
import ExportModal from "../components/writing/ExportModal";
import FindFixModal from "../components/writing/FindFixModal";
import QuickFixModal from "../components/writing/QuickFixModal";
import ContactSupportModal from "../components/writing/ContactSupportModal";

// Helper: Find position of chapter N in content (handles multiple formats)
const findChapterPosition = (project, chapterNum) => {
  const content = project?.content || '';
  if (!content) return -1;
  // Try multiple patterns
  const patterns = [
    // HTML: <h1>Chapter 1: ... or <h1>Chapter 1 ...
    new RegExp(`<h[1-3][^>]*>\\s*Chapter\\s+${chapterNum}\\b`, 'i'),
    // Markdown: # Chapter 1 or ## Chapter 1
    new RegExp(`^#{1,3}\\s*Chapter\\s+${chapterNum}\\b`, 'im'),
    // Delimiter: --- CHAPTER 1
    new RegExp(`---\\s*CHAPTER\\s+${chapterNum}\\b`, 'i'),
    // Plain: Chapter 1: or CHAPTER 1:
    new RegExp(`(?:^|\\n)\\s*Chapter\\s+${chapterNum}\\s*[:\\-]`, 'im'),
  ];
  // Try chapter_outline title
  const chTitle = project?.chapter_outline?.[chapterNum - 1]?.title;
  if (chTitle && content.includes(chTitle)) {
    return content.indexOf(chTitle);
  }
  // Try patterns
  for (const regex of patterns) {
    const match = regex.exec(content);
    if (match) return match.index;
  }
  return -1;
};


const getChapterSegments = (content) => {
  if (!content) return [];
  const markers = [...content.matchAll(/---\s*CHAPTER\s+(\d+)\s*:?\s*([^\n]*?)\s*---/gi)];
  return markers.map((m, i) => ({
    number: parseInt(m[1]),
    title: (m[2] || '').trim(),
    start: m.index,
    end: i + 1 < markers.length ? markers[i + 1].index : content.length,
  }));
};

const WritingPage = () => {
  const { user, logout } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [editType, setEditType] = useState("improve");
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(false);
  const [generateOptions, setGenerateOptions] = useState({
    prompt: "",
    length_type: "paragraphs",
    length_value: 3
  });
  const contentRef = useRef(null);
  const typingPauseTimerRef = useRef(null);
  const wowVisibleRef = useRef(false);
  
  // Wow Factor - typing pause feedback
  const [wowMessage, setWowMessage] = useState(null);
  
  // Focus Mode (paid editor)
  const [focusMode, setFocusMode] = useState(false);
  const [kindleMode, setKindleMode] = useState(false);
  
  // Right Panel tab
  const [rightPanelTab, setRightPanelTab] = useState('chapters'); // 'chapters' | 'improve' | 'story'
  
  // Text formatting state
  const [fontFamily, setFontFamily] = useState("Georgia, serif");
  const [fontSize, setFontSize] = useState("18px");
  const [textAlign, setTextAlign] = useState("left");
  const [lineHeight, setLineHeight] = useState("1.8");
  
  // Audio reading state
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [audioChapter, setAudioChapter] = useState(1);
  const [audioVoice, setAudioVoice] = useState("aria");
  const [audioSpeed, setAudioSpeed] = useState(1.0);
  const [showAudioPanel, setShowAudioPanel] = useState(false);
  const audioRef = useRef(null);
  const [audioLastPosition, setAudioLastPosition] = useState({ chapter: 1, chunkIndex: 0 }); // Track last position for resume
  const [canResumeAudio, setCanResumeAudio] = useState(false); // Show resume button when playback interrupted
  const [isAudioPaused, setIsAudioPaused] = useState(false); // True pause state (keeps audio URLs alive)
  const [audioProgress, setAudioProgress] = useState({ currentTime: 0, duration: 0, segment: 0, totalSegments: 0 });
  
  // TTS Phase 1: Sentence-level sync data
  const audioSentenceTimingRef = useRef([]); // Array of arrays: per-chunk sentence timing [{text, start, end}]
  const audioChapterBoundsRef = useRef({ startPos: 0, endPos: 0 }); // Chapter char range in full content
  const userScrolledRef = useRef(false); // User scroll override flag
  const userScrollTimeoutRef = useRef(null); // Timeout to resume auto-scroll
  const programmaticScrollRef = useRef(false); // True when WE set scrollTop (ignore in onScroll)
  // TTS Phase 2: Mirror-div caret measurement (accurate scroll target with text wrapping)
  const mirrorRef = useRef(null);
  // TTS Phase 1.5: Word-level highlight overlay
  const audioWordTimingRef = useRef([]); // Per-chunk array of {text, start, end, fullStartChar, fullEndChar}
  const wordHighlightRef = useRef(null);  // Visible overlay div for active word
  
  // Assistant feature modals
  const [showDirectionModal, setShowDirectionModal] = useState(false);
  const [showNamesModal, setShowNamesModal] = useState(false);
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const [showCharacterEditor, setShowCharacterEditor] = useState(false); // For editing existing characters
  const [showAddChapter, setShowAddChapter] = useState(false);
  const [editingChapterTitle, setEditingChapterTitle] = useState(null);
  const [addChapterTitle, setAddChapterTitle] = useState("");
  const [addChapterSummary, setAddChapterSummary] = useState("");
  const [addingChapter, setAddingChapter] = useState(false);
  const [showPlotModal, setShowPlotModal] = useState(false);
  const [directionType, setDirectionType] = useState("plot_twist");
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantResults, setAssistantResults] = useState(null);
  const [directionSuggestions, setDirectionSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [showDirectionChoice, setShowDirectionChoice] = useState(false);
  const [directionMode, setDirectionMode] = useState(null); // 'continue' or 'new_chapter'
  const [showChapterNameChoice, setShowChapterNameChoice] = useState(false);
  const [customChapterName, setCustomChapterName] = useState('');
  const [generatingFromDirection, setGeneratingFromDirection] = useState(false);
  const [directionGenProgress, setDirectionGenProgress] = useState('');
  const [suggestedNames, setSuggestedNames] = useState([]);
  const [nameToReplace, setNameToReplace] = useState("");
  const [newNameValue, setNewNameValue] = useState("");
  
  // Custom character creation state
  const [characterMode, setCharacterMode] = useState('choose'); // 'choose', 'create', 'suggest'
  const [customCharacter, setCustomCharacter] = useState({
    name: '',
    age: '',
    sex: '',
    characteristics: '',
    introductionMethod: ''
  });
  const [characterChapter, setCharacterChapter] = useState('');
  const [characterInsertProgress, setCharacterInsertProgress] = useState({ part: 0, total: 5, message: '' });
  
  // Story Doctor state (replaced by local Find & Fix)
  const [showStoryDoctor, setShowStoryDoctor] = useState(false);
  const [showFindFix, setShowFindFix] = useState(false);
  const [showNewQuickFix, setShowNewQuickFix] = useState(false);
  const [showSmartRefine, setShowSmartRefine] = useState(false);
  const [showFinalEditor, setShowFinalEditor] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [scanning, setScanning] = useState(false);
  const [issues, setIssues] = useState([]);
  const [fixingIssue, setFixingIssue] = useState(null);
  const [deepFixing, setDeepFixing] = useState(false);
  
  // New Feature Modals
  const [showAIDetection, setShowAIDetection] = useState(false);
  const [showSynopsisLadder, setShowSynopsisLadder] = useState(false);
  const [showCanonLock, setShowCanonLock] = useState(false);
  const [showCharacterPortraits, setShowCharacterPortraits] = useState(false);
  
  // Smart Story Doctor state (enhanced)
  const [smartAnalysis, setSmartAnalysis] = useState(null);
  const [analyzingMode, setAnalyzingMode] = useState(''); // '', 'smart', 'characters'
  const [previewFix, setPreviewFix] = useState(null);
  const [applyingFixIdx, setApplyingFixIdx] = useState(null); // Track WHICH issue is being fixed (null = none, number = that index)
  const [doctorTab, setDoctorTab] = useState('overview'); // 'overview', 'chapters', 'issues', 'characters', 'plot'
  const [analysisJobId, setAnalysisJobId] = useState(null); // For tracking current analysis job
  const [applyingAllFixes, setApplyingAllFixes] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState(new Set()); // For multi-select fix
  
  // Manuscript Analysis Dashboard state (Consistency + Readability)
  const [showAnalysisDashboard, setShowAnalysisDashboard] = useState(false);
  
  // Chapter Outline state
  const [showChapterOutline, setShowChapterOutline] = useState(true);  // Default to visible so users can see/edit chapters
  const [generatingChapter, setGeneratingChapter] = useState(null);
  const [chapterProgress, setChapterProgress] = useState({ part: 0, total: 3, status: 'idle', message: '' }); // idle, generating, syncing, error, done
  const chapterAbortRef = useRef(null);
  const [selectedChapterNum, setSelectedChapterNum] = useState(1); // Currently selected chapter for new feature modals
  
  // Scroll writing area to a specific chapter
  const scrollToChapter = useCallback((chapterNum) => {
    if (!contentRef.current || !project?.content) return;
    const pos = findChapterPosition(project, chapterNum);
    if (pos < 0) {
      toast.info(`Chapter ${chapterNum} not found in content`);
      return;
    }
    const ta = contentRef.current;
    const content = project.content;
    
    // Use character ratio × scrollHeight for accurate positioning with wrapped text
    const ratio = pos / content.length;
    ta.scrollTop = Math.max(0, (ratio * ta.scrollHeight) - 40);
    
    // Make sure textarea is visible on the page
    ta.scrollIntoView({ behavior: 'instant', block: 'start' });
    
    // Highlight the chapter heading
    setTimeout(() => {
      ta.focus({ preventScroll: true });
      ta.setSelectionRange(pos, pos + 40);
    }, 100);
  }, [project]);
  
  // Story Bible & Chapter Planning state
  const [showStoryBible, setShowStoryBible] = useState(false);
  const [storyBible, setStoryBible] = useState(null);
  const [loadingBible, setLoadingBible] = useState(false);
  const [showChapterPlanning, setShowChapterPlanning] = useState(false);
  const [chapterPlan, setChapterPlan] = useState(null);
  const [planningChapter, setPlanningChapter] = useState(null);
  const [pendingProposal, setPendingProposal] = useState(null); // For "Ask Before Adding"
  
  // Back Cover Blurb state
  const [showBackCover, setShowBackCover] = useState(false);
  
  // Book Cover Design state
  const [showCoverDesign, setShowCoverDesign] = useState(false);
  
  // Writing Enhancement Tools state
  const [showWritingTools, setShowWritingTools] = useState(false);
  const [rewriteResult, setRewriteResult] = useState(null);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  
  // Quick Fix state
  const [showQuickFix, setShowQuickFix] = useState(false);
  const [quickFixChapter, setQuickFixChapter] = useState(1);
  const [quickFixInstruction, setQuickFixInstruction] = useState('');
  const [quickFixLoading, setQuickFixLoading] = useState(false);
  const [quickFixResult, setQuickFixResult] = useState(null); // { fixedText, originalText, chapterNumber, jobId, locatedRange }
  const [quickFixJobId, setQuickFixJobId] = useState(null);
  const [quickFixProgress, setQuickFixProgress] = useState({ status: '', progress: 0, message: '' });
  
  // Chapter Editor state
  const [showChapterEditor, setShowChapterEditor] = useState(false);
  const [chapterEditorNum, setChapterEditorNum] = useState(1);
  const [chapterEditorText, setChapterEditorText] = useState('');
  const [chapterEditorSaving, setChapterEditorSaving] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const isFreePlan = subscription?.subscription === 'free' || !subscription;
  
  // Story Navigator state
  const [showStoryNavigator, setShowStoryNavigator] = useState(false);
  const [showStoryArchitect, setShowStoryArchitect] = useState(false);
  const [storyArchitectEnabled, setStoryArchitectEnabled] = useState(false);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/feature-flags`)
      .then((res) => setStoryArchitectEnabled(!!res.data?.flags?.story_architect))
      .catch(() => {});
  }, []);
  const [storyAnalysis, setStoryAnalysis] = useState(null);
  const [analyzingStory, setAnalyzingStory] = useState(false);
  const [weaveDirection, setWeaveDirection] = useState("");
  const [weaveLength, setWeaveLength] = useState({ type: "paragraphs", value: 3 });
  const [weaving, setWeaving] = useState(false);
  
  // Activity Log
  const [activityLog, setActivityLog] = useState([]);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportSending, setReportSending] = useState(false);

  // Auto-save state
  const [lastSavedContent, setLastSavedContent] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const autoSaveTimeoutRef = useRef(null);

  // Privacy / No Training
  const privacyEnabled = project?.privacy_no_training || false;
  const [showFidelityReport, setShowFidelityReport] = useState(false);  // P0 #3
  const togglePrivacy = async () => {
    try {
      const newVal = !privacyEnabled;
      await axios.put(`${API}/project/${project.id}/privacy`, { no_training: newVal });
      setProject(prev => ({ ...prev, privacy_no_training: newVal }));
    } catch (err) {
      console.error('Failed to toggle privacy:', err);
    }
  };
  
  const addToLog = (type, message, details = null) => {
    const entry = {
      id: Date.now(),
      type, // 'scan', 'fix', 'analyze', 'suggest', 'error', 'audio'
      message,
      details: details ? String(details) : null,
      timestamp: new Date().toLocaleTimeString()
    };
    setActivityLog(prev => [entry, ...prev].slice(0, 50)); // Keep last 50 entries
  };
  
  // Check if there are any errors in the log
  const hasErrors = activityLog.some(entry => entry.type === 'error');
  
  // Chapter Extension state
  const [extensionDirectives, setExtensionDirectives] = useState('');
  const [extensionType, setExtensionType] = useState('half_chapter'); // chapter_ending, half_chapter, full_extension
  const [generatingExtension, setGeneratingExtension] = useState(false);
  const [extensionProgress, setExtensionProgress] = useState({ part: 0, total: 1, status: 'idle', word_count: 0, message: '', error_message: '' });
  
  // Full Book Generation state
  const [generatingFullBook, setGeneratingFullBook] = useState(false);
  const [fullBookJobId, setFullBookJobId] = useState(null);
  const [cancellingFullBook, setCancellingFullBook] = useState('idle'); // idle, requested, confirmed
  const [fullBookProgress, setFullBookProgress] = useState({
    status: 'idle', // idle, PENDING, PROCESSING, COMPLETED, FAILED, CANCELLED
    totalChapters: 0,
    chaptersCompleted: 0,
    currentChapter: 0,
    chapterStatus: '',
    estimatedTimeRemaining: null,
    error: null
  });
  
  // Resume Book Generation state
  const [resumeInfo, setResumeInfo] = useState(null); // { lastChapter, totalChapters, isLastChapterIncomplete, lastChapterWordCount }
  
  // Promo generation message rotation
  const promoGenCountRef = useRef(0);
  const promoMessages = [
    "This chapter was generated using Premium AI writing.",
    "You're experiencing Nova-level storytelling quality.",
    "Notice the difference? This is Premium AI writing.",
    "Your story is being written with our most advanced engine.",
  ];
  
  // Handle full book generation using job-based system
  const handleGenerateFullBook = async (startFromChapter = 1) => {
    // Check subscription for Full Book Generation access
    const canUseFullBookGen = subscription?.limits?.can_full_book_gen || 
                              subscription?.subscription === 'author' || 
                              subscription?.subscription === 'family';
    if (!canUseFullBookGen) {
      toast.error("Full Book Generation requires Shweebum Premium ($20/mo). Upgrade to unlock!");
      return;
    }
    
    if (!project?.chapter_outline?.length) {
      toast.error("No chapter outline found. Please create an outline first.");
      return;
    }
    
    const totalChapters = project.chapter_outline.length;
    const confirmMessage = startFromChapter === 1 
      ? `Generate all ${totalChapters} chapters? This may take ${totalChapters * 1}-${totalChapters * 2} minutes.`
      : `Continue generating from chapter ${startFromChapter}? (${totalChapters - startFromChapter + 1} chapters remaining)`;
    
    if (!window.confirm(confirmMessage)) return;
    
    setGeneratingFullBook(true);
    setCancellingFullBook('idle');
    setFullBookProgress({
      status: 'PENDING',
      totalChapters,
      chaptersCompleted: startFromChapter - 1,
      currentChapter: 0,
      chapterStatus: 'Starting...',
      estimatedTimeRemaining: (totalChapters - startFromChapter + 1) * 60,
      error: null
    });
    addToLog('generate', `Starting full book generation: ${totalChapters} chapters`);
    
    try {
      // Create the full book job
      const jobResponse = await axios.post(`${API}/full-book-jobs`, {
        project_id: id,
        start_chapter: startFromChapter
      });
      
      const jobId = jobResponse.data.job_id;
      setFullBookJobId(jobId);
      addToLog('generate', `Full book job created: ${jobId.slice(0, 8)}...`);
      toast.success(`Generating ${totalChapters} chapters. You can leave this page and come back!`);
      
      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/full-book-jobs/${jobId}/status`);
          const job = statusRes.data;
          
          const completedCount = job.chapters_completed || 0;
          const remainingChapters = job.total_chapters - completedCount;
          const estimatedSeconds = remainingChapters * 60; // ~1 min per chapter
          
          setFullBookProgress({
            status: job.status,
            totalChapters: job.total_chapters,
            chaptersCompleted: completedCount,
            currentChapter: job.current_chapter || 0,
            chapterStatus: job.chapter_status || '',
            estimatedTimeRemaining: estimatedSeconds,
            error: job.error_message
          });
          
          // Refresh project to get new content
          if (completedCount > 0) {
            const projectRes = await axios.get(`${API}/project/${id}`);
            setProject(projectRes.data);
          }
          
          if (job.status === 'COMPLETED') {
            clearInterval(pollInterval);
            setGeneratingFullBook(false);
            setFullBookJobId(null);
            setCancellingFullBook('idle');
            toast.success(`Book complete! All ${job.total_chapters} chapters generated!`);
            addToLog('complete', `Full book generation complete: ${job.total_chapters} chapters, ${project?.word_count || 0} words`);
            // Final refresh
            const finalProject = await axios.get(`${API}/project/${id}`);
            setProject(finalProject.data);
          } else if (job.status === 'FAILED') {
            clearInterval(pollInterval);
            setGeneratingFullBook(false);
            setCancellingFullBook('idle');
            toast.error(`Generation failed at chapter ${job.current_chapter}: ${job.error_message}`);
            addToLog('error', `Full book generation failed`, job.error_message);
          } else if (job.status === 'CANCELLED') {
            clearInterval(pollInterval);
            setGeneratingFullBook(false);
            setFullBookJobId(null);
            setCancellingFullBook('idle');
            toast.info(`Generation stopped. ${completedCount} of ${job.total_chapters} chapters saved.`);
            addToLog('info', `Full book generation cancelled after chapter ${completedCount}`);
            // Refresh to show saved content
            const cancelledProject = await axios.get(`${API}/project/${id}`);
            setProject(cancelledProject.data);
          }
        } catch (pollError) {
          console.error('Poll error:', pollError);
        }
      }, 3000); // Poll every 3 seconds
      
      // Store interval ID for cleanup
      return () => clearInterval(pollInterval);
      
    } catch (error) {
      console.error('Failed to start full book generation:', error);
      toast.error("Failed to start book generation. Please try again.");
      setGeneratingFullBook(false);
      addToLog('error', 'Failed to start full book generation', error.message);
    }
  };
  
  // Cancel full book generation
  const handleCancelFullBook = async () => {
    if (!fullBookJobId || cancellingFullBook !== 'idle') return;
    
    if (!window.confirm("Cancel book generation? Completed chapters will be saved.")) return;
    
    setCancellingFullBook('requested');
    toast.info("Cancellation requested — waiting for current chapter to finish...");
    
    try {
      const res = await axios.post(`${API}/full-book-jobs/${fullBookJobId}/cancel`);
      setCancellingFullBook('confirmed');
      toast.success(`Cancellation confirmed. ${res.data.chapters_completed || 0} chapters saved.`);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 400 && detail.includes('Cannot cancel')) {
        // Job already finished or failed
        toast.info(detail);
        setCancellingFullBook('idle');
      } else if (error.response?.status === 404) {
        toast.error("Job not found — it may have already finished.");
        setCancellingFullBook('idle');
      } else {
        toast.error("Cancel request failed. Try again.");
        setCancellingFullBook('idle');
      }
    }
  };

  // Chapter length mode (auto/medium/large) — editable from the writing screen
  const chapterLengthMode = project?.story_bible?.chapter_size
    || (project?.story_bible?.chapter_word_target === 900 ? "medium"
        : project?.story_bible?.chapter_word_target ? "large" : "auto");

  const handleSetChapterLength = async (size) => {
    try {
      await axios.post(`${API}/project-settings/${id}/chapter-length`, { size });
      setProject(prev => ({
        ...prev,
        story_bible: {
          ...(prev.story_bible || {}),
          chapter_size: size,
          chapter_word_target: size === "auto" ? null : (size === "medium" ? 900 : 2000),
        },
      }));
      toast.success(size === "auto"
        ? "Auto length on — each chapter will fit its outline"
        : `Chapter length set to ${size === "medium" ? "~900" : "~2,000"} words`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update chapter length");
    }
  };

  const chapterLengthPicker = (
    <div className="mt-2" data-testid="chapter-length-picker">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Chapter Length</p>
      <div className="grid grid-cols-3 gap-1">
        {[
          { key: "auto", label: "Auto ✨", hint: "Fits outline" },
          { key: "medium", label: "Medium", hint: "~900 words" },
          { key: "large", label: "Large", hint: "~2,000 words" },
        ].map(opt => (
          <button
            key={opt.key}
            onClick={() => handleSetChapterLength(opt.key)}
            data-testid={`chapter-length-${opt.key}`}
            className={`px-1 py-1.5 rounded-md border text-center transition-colors ${
              chapterLengthMode === opt.key
                ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
                : "border-slate-600 text-muted-foreground hover:border-slate-400"
            }`}
          >
            <span className="block text-xs font-medium">{opt.label}</span>
            <span className="block text-[9px] opacity-80">{opt.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Append a new chapter outline entry (author can keep growing the story over time)
  const handleAddChapterOutline = async () => {
    const summary = addChapterSummary.trim();
    if (summary.length < 10) {
      toast.error("Please write the chapter outline (at least 10 characters)");
      return;
    }
    setAddingChapter(true);
    try {
      const res = await axios.post(`${API}/chapter-outline/${id}/add`, {
        title: addChapterTitle.trim() || null,
        summary,
      });
      const { chapter_number, title } = res.data;
      setProject(prev => ({
        ...prev,
        chapter_outline: [...(prev.chapter_outline || []), { number: chapter_number, title, summary }],
      }));
      toast.success(`Chapter ${chapter_number} added to your outline`);
      setShowAddChapter(false);
      setAddChapterTitle("");
      setAddChapterSummary("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add chapter");
    } finally {
      setAddingChapter(false);
    }
  };

  // Erase the last chapter and optionally regenerate from there
  const handleEraseLastChapterAndRegenerate = async (regenerate = false) => {
    if (!project?.content) return;
    
    const content = project.content;
    const chapterMatches = [...content.matchAll(/---\s*CHAPTER\s+(\d+)[^-]*---/gi)];
    
    if (chapterMatches.length === 0) {
      toast.error("No chapters found to erase");
      return;
    }
    
    const lastChapterNum = Math.max(...chapterMatches.map(m => parseInt(m[1])));
    const lastChapterIndex = chapterMatches.findIndex(m => parseInt(m[1]) === lastChapterNum);
    
    if (lastChapterIndex === -1) return;
    
    // Find start position (include the newlines before the chapter header)
    let startPos = chapterMatches[lastChapterIndex].index;
    while (startPos > 0 && content[startPos - 1] === '\n') startPos--;
    if (startPos > 0) startPos++; // Keep one newline
    
    const newContent = content.substring(0, startPos).trim();
    
    const actionLabel = regenerate 
      ? `Erase Chapter ${lastChapterNum} and regenerate?`
      : `Erase Chapter ${lastChapterNum}? This cannot be undone.`;
    
    if (!window.confirm(actionLabel)) return;
    
    try {
      // Update local state
      setProject(prev => ({
        ...prev,
        content: newContent,
        word_count: newContent.split(/\s+/).filter(Boolean).length
      }));
      
      // Save to backend
      await axios.put(`${API}/project/${id}`, {
        content: newContent,
        word_count: newContent.split(/\s+/).filter(Boolean).length
      });
      
      toast.success(`Chapter ${lastChapterNum} erased`);
      addToLog('edit', `Erased Chapter ${lastChapterNum}`);
      
      // If regenerate, start from that chapter
      if (regenerate) {
        setTimeout(() => {
          handleGenerateFullBook(lastChapterNum);
        }, 500);
      }
    } catch (e) {
      toast.error("Failed to save - please try again");
      addToLog('error', 'Failed to erase chapter', e.message);
    }
  };

  const handleDeleteChapterOutline = async (idx) => {
    const outline = project?.chapter_outline || [];
    const chapter = outline[idx];
    if (!chapter) return;
    const num = chapter.number;
    const isWritten = new RegExp(`---\\s*CHAPTER\\s+${num}\\b`, 'i').test(project.content || '');
    const msg = isWritten
      ? `Delete Chapter ${num} outline AND its written text? Later chapters will be renumbered. This cannot be undone.`
      : `Delete Chapter ${num} outline? Later chapters will be renumbered.`;
    if (!window.confirm(msg)) return;

    const newOutline = outline.filter((_, i) => i !== idx).map((ch, i) => ({ ...ch, number: i + 1 }));

    let newContent = project.content || '';
    if (isWritten) {
      const matches = [...newContent.matchAll(/---\s*CHAPTER\s+(\d+)[^\n]*?---/gi)];
      const targetIdx = matches.findIndex(m => parseInt(m[1]) === num);
      if (targetIdx !== -1) {
        const target = matches[targetIdx];
        let start = target.index;
        while (start > 0 && newContent[start - 1] === '\n') start--;
        const next = matches[targetIdx + 1];
        const end = next ? next.index : newContent.length;
        newContent = (newContent.substring(0, start) + '\n\n' + newContent.substring(end)).trim();
      }
    }
    newContent = newContent.replace(/(---\s*CHAPTER\s+)(\d+)/gi, (full, pre, n) => {
      const v = parseInt(n);
      return v > num ? `${pre}${v - 1}` : full;
    });

    const wc = newContent.split(/\s+/).filter(Boolean).length;
    try {
      setProject(prev => ({ ...prev, chapter_outline: newOutline, content: newContent, word_count: wc }));
      await axios.put(`${API}/project/${id}`, { chapter_outline: newOutline, content: newContent, word_count: wc });
      toast.success(`Chapter ${num} deleted`);
      addToLog('edit', `Deleted Chapter ${num} outline`);
    } catch (e) {
      toast.error('Failed to delete - please try again');
    }
  };

  const handleRewriteChapter = async (chapterNum) => {
    if (!window.confirm(`Rewrite Chapter ${chapterNum}? Its current text will be deleted and rewritten in place using your updated outline.`)) return;
    await handleGenerateChapter(chapterNum, true);
  };

  // Handle chapter extension using job-based system
  const handleGenerateExtension = async () => {
    // Use extensionDirectives which is set from generateOptions.prompt before calling
    const directives = extensionDirectives || generateOptions.prompt;
    if (!directives?.trim()) {
      toast.error("Please describe what should happen in the 'What should happen next?' box");
      return;
    }
    
    setGeneratingExtension(true);
    setExtensionProgress({ part: 0, total: extensionType === 'chapter_ending' ? 1 : extensionType === 'half_chapter' ? 2 : 3, status: 'PENDING', word_count: 0, message: 'Creating job...', error_message: '' });
    addToLog('generate', `Starting chapter extension (${extensionType})...`);
    
    const startWordCount = project?.word_count || 0;
    
    try {
      // Create the extension job
      const jobResponse = await axios.post(`${API}/extension-jobs`, {
        project_id: id,
        directives: directives,
        extension_type: extensionType
      });
      
      const jobId = jobResponse.data.job_id;
      const totalParts = jobResponse.data.total_parts;
      addToLog('generate', `Extension job created: ${jobId.slice(0, 8)}...`);
      setExtensionProgress(prev => ({ ...prev, total: totalParts, status: 'PENDING', message: 'Job started...' }));
      
      // Poll for status
      let attempts = 0;
      const maxAttempts = 120; // 6 minutes max
      let lastStatus = '';
      
      while (attempts < maxAttempts) {
        attempts++;
        await new Promise(r => setTimeout(r, 3000));
        
        try {
          const statusRes = await axios.get(`${API}/extension-jobs/${jobId}/status`);
          const { status, current_part, word_count, error_message, total_parts } = statusRes.data;
          
          if (status !== lastStatus) {
            addToLog('generate', `Status: ${status} (Part ${current_part}/${total_parts}, ${word_count} words)`);
            lastStatus = status;
          }
          
          setExtensionProgress({ 
            part: current_part, 
            total: total_parts, 
            status, 
            word_count, 
            message: status.replace(/_/g, ' '),
            error_message: error_message || ''
          });
          
          if (status === 'COMPLETE') {
            const finalRes = await axios.get(`${API}/project/${id}`);
            setProject(finalRes.data);
            
            const wordsAdded = (finalRes.data.word_count || 0) - startWordCount;
            addToLog('sync', `Extension complete! ${wordsAdded} words added.`);
            toast.success(`Chapter extended with ${wordsAdded} words!`);
            // Clear the prompt after success
            setGenerateOptions(prev => ({ ...prev, prompt: '' }));
            setExtensionDirectives('');
            
            setTimeout(() => {
              setGeneratingExtension(false);
              setExtensionProgress({ part: 0, total: 1, status: 'idle', word_count: 0, message: '', error_message: '' });
            }, 2000);
            return;
          }
          
          if (status === 'FAILED') {
            addToLog('error', `Extension failed: ${error_message || 'Unknown error'}`);
            toast.error(`Extension failed: ${error_message || 'Unknown error'}`);
            setGeneratingExtension(false);
            return;
          }
          
          // Refresh project to show partial progress
          if (status.includes('PART_') && status.includes('COMPLETE')) {
            try {
              const partialRes = await axios.get(`${API}/project/${id}`);
              if (partialRes.data.word_count > (project?.word_count || 0)) {
                setProject(partialRes.data);
              }
            } catch (e) {}
          }
          
        } catch (pollErr) {
          addToLog('sync', `Poll attempt ${attempts} failed, retrying...`);
        }
      }
      
      // Timeout
      addToLog('error', 'Extension timed out after 6 minutes');
      toast.error('Extension timed out. Check your content - partial work may be saved.');
      
      const finalRes = await axios.get(`${API}/project/${id}`);
      setProject(finalRes.data);
      
    } catch (error) {
      const errorMsg = String(error?.response?.data?.detail || error?.message || 'Extension failed');
      addToLog('error', errorMsg);
      toast.error(errorMsg);
    } finally {
      setGeneratingExtension(false);
      setTimeout(() => setExtensionProgress({ part: 0, total: 1, status: 'idle', word_count: 0, message: '', error_message: '' }), 3000);
    }
  };
  
  // Send issue report
  const sendIssueReport = async () => {
    setReportSending(true);
    try {
      const logText = activityLog.map(entry => 
        `[${entry.timestamp}] ${entry.type.toUpperCase()}: ${entry.message}${entry.details ? '\n  ' + entry.details : ''}`
      ).join('\n\n');
      
      const response = await axios.post(`${API}/report-issue`, {
        user_email: user?.email || '',
        activity_log: logText,
        error_type: activityLog.find(e => e.type === 'error')?.message || 'Unknown Error',
        browser_info: navigator.userAgent,
        project_title: project?.title || ''
      });
      
      if (response.data.success) {
        toast.success("Issue reported! We'll look into it.");
        setShowReportDialog(false);
      } else {
        toast.error("Failed to send report: " + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      toast.error("Failed to send report: " + err.message);
    } finally {
      setReportSending(false);
    }
  };

  // Analyze chapter state for resume functionality
  const analyzeChapterState = (content, chapterOutline) => {
    if (!content || !chapterOutline?.length) return null;
    
    // Find all chapter markers in content
    const chapterMatches = [...content.matchAll(/---\s*CHAPTER\s+(\d+)[^-]*---/gi)];
    if (chapterMatches.length === 0) return null;
    
    const writtenChapterNumbers = chapterMatches.map(m => parseInt(m[1]));
    const lastWrittenChapter = Math.max(...writtenChapterNumbers);
    const totalChapters = chapterOutline.length;
    
    // Check if book is complete
    if (lastWrittenChapter >= totalChapters) return null;
    
    // Get the content of the last chapter
    const lastChapterIndex = chapterMatches.findIndex(m => parseInt(m[1]) === lastWrittenChapter);
    const lastChapterStart = chapterMatches[lastChapterIndex].index;
    const nextChapterMatch = chapterMatches[lastChapterIndex + 1];
    const lastChapterEnd = nextChapterMatch ? nextChapterMatch.index : content.length;
    const lastChapterContent = content.substring(lastChapterStart, lastChapterEnd);
    
    // Count words in last chapter (excluding the header)
    const lastChapterWords = lastChapterContent
      .replace(/---\s*CHAPTER\s+\d+[^-]*---/gi, '')
      .split(/\s+/)
      .filter(Boolean).length;
    
    // A chapter is considered "incomplete" if it has less than 500 words (typical chapter has 1500+)
    const isLastChapterIncomplete = lastChapterWords < 500;
    
    return {
      lastChapter: lastWrittenChapter,
      totalChapters,
      remainingChapters: totalChapters - lastWrittenChapter,
      isLastChapterIncomplete,
      lastChapterWordCount: lastChapterWords
    };
  };
  
  // Extract content for a specific chapter from project.content
  const extractChapterContent = (chapterNum) => {
    if (!project?.content) return '';
    
    // Match chapter header and content until next chapter or end
    // Handles both "--- CHAPTER N: TITLE ---" and "--- CHAPTER N: TITLE\n" formats
    const chapterRegex = new RegExp(
      `---\\s*CHAPTER\\s+${chapterNum}\\b[^\\n]*(?:---)?\\n?([\\s\\S]*?)(?=---\\s*CHAPTER\\s+\\d+\\b|$)`,
      'i'
    );
    const match = project.content.match(chapterRegex);
    
    return match ? match[1].trim() : '';
  };

  // Update content for a specific chapter
  const updateChapterContent = async (chapterNum, newContent) => {
    if (!project?.content) return;
    
    try {
      // Rebuild the content with the updated chapter - handle both delimiter formats
      const chapterRegex = new RegExp(
        `(---\\s*CHAPTER\\s+${chapterNum}\\b[^\\n]*(?:---)?\\n?)[\\s\\S]*?(?=---\\s*CHAPTER\\s+\\d+\\b|$)`,
        'i'
      );
      const match = project.content.match(chapterRegex);
      
      if (!match) {
        toast.error(`Could not locate chapter ${chapterNum}`);
        return;
      }
      
      const chapterHeader = match[1];
      const endIdx = match.index + match[0].length;
      const startIdx = match.index;
      
      // Find what comes after this chapter (if anything)
      const afterContent = project.content.substring(endIdx);
      const beforeContent = project.content.substring(0, startIdx);
      
      const updatedContent = beforeContent + chapterHeader + newContent + '\n\n' + afterContent;
      
      // Save to database
      await axios.put(`${API}/projects/${project.id}`, { content: updatedContent.trim() });
      setProject(prev => ({ ...prev, content: updatedContent.trim() }));
      
      toast.success(`Chapter ${chapterNum} updated!`);
      addToLog('edit', `Updated Chapter ${chapterNum} content`);
    } catch (err) {
      toast.error("Failed to update chapter: " + (err.message || "Unknown error"));
    }
  };

  // Update resume info whenever project changes
  useEffect(() => {
    if (project?.content && project?.chapter_outline?.length > 0) {
      const info = analyzeChapterState(project.content, project.chapter_outline);
      setResumeInfo(info);
    } else {
      setResumeInfo(null);
    }
  }, [project?.content, project?.chapter_outline]);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchProject();
    fetchSubscription();
  }, [id, user, navigate]);

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/user/${user.id}/subscription`);
      setSubscription(res.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  };

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/project/${id}`, { timeout: 60000 });
      // Ensure older projects have default values for new fields
      const projectData = {
        ...res.data,
        outline_status: res.data.outline_status || 'complete', // Default to complete for older projects
        chapter_outline: res.data.chapter_outline || [],
        protagonists: res.data.protagonists || []
      };
      setProject(projectData);
      setLoading(false);
      
      // If outline is still generating, poll again after 3 seconds
      if (projectData.outline_status === 'generating') {
        setTimeout(() => {
          fetchProject();
        }, 3000);
      } else if (projectData.outline_status === 'complete' && project?.outline_status === 'generating') {
        toast.success("Chapter outline ready!");
      }
    } catch (error) {
      console.error("Failed to fetch project:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Loading timed out. Retrying...");
        setTimeout(() => fetchProject(), 2000);
      } else {
        toast.error("Project not found");
        navigate("/dashboard");
      }
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await axios.put(`${API}/project/${id}`, {
        content: project.content,
        plot_points: project.plot_points,
        characters: project.characters,
        chapter_outline: project.chapter_outline
      });
      setLastSavedContent(project.content);
      setLastSaveTime(new Date());
      toast.success("Saved!");
    } catch (error) {
      toast.error("Failed to save");
      console.error("Save error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save function (silent, no toast)
  const autoSave = async () => {
    if (!project?.id || !project?.content) return;
    if (project.content === lastSavedContent) return; // No changes
    
    try {
      setIsSaving(true);
      await axios.put(`${API}/project/${id}`, {
        content: project.content,
        plot_points: project.plot_points,
        characters: project.characters,
        chapter_outline: project.chapter_outline
      });
      setLastSavedContent(project.content);
      setLastSaveTime(new Date());
      console.log("Auto-saved at", new Date().toLocaleTimeString());
    } catch (error) {
      console.error("Auto-save failed:", error);
      // Don't show error toast for auto-save to avoid spam
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save effect: triggers 3 seconds after last content change
  useEffect(() => {
    if (!project?.content || !id) return;
    
    // Clear any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Set initial content on first load
    if (lastSavedContent === null && project.content) {
      setLastSavedContent(project.content);
      return;
    }
    
    // Only auto-save if content has changed
    if (project.content !== lastSavedContent) {
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 3000); // Auto-save 3 seconds after changes stop
    }
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [project?.content, project?.chapter_outline, id]);

  // Save before leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (project?.content && project.content !== lastSavedContent) {
        // Try to save
        navigator.sendBeacon(`${API}/project/${id}/beacon-save`, JSON.stringify({
          content: project.content,
          chapter_outline: project.chapter_outline
        }));
        
        // Show confirmation dialog
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [project?.content, lastSavedContent, id]);

  const handleGenerate = async () => {
    if (!generateOptions.prompt) {
      toast.error("Please describe what should happen next");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/generate?user_id=${user.id}`, {
        project_id: id,
        prompt: generateOptions.prompt,
        length_type: generateOptions.length_type,
        length_value: generateOptions.length_value
      });
      setProject(prev => ({
        ...prev,
        content: prev.content ? prev.content + "\n\n" + res.data.generated_text : res.data.generated_text,
        word_count: res.data.total_words
      }));
      setGenerateOptions({ ...generateOptions, prompt: "" });
      toast.success(`Generated! Total words: ${res.data.total_words}`);
    } catch (error) {
      if (error?.response?.status === 403) {
        showUpgradePrompt('AI Content Generation');
      } else {
        toast.error("Generation failed. Please try again.");
      }
    } finally {
      setGenerating(false);
    }
  };

  // Show upgrade modal for premium features
  const showUpgradePrompt = (feature) => {
    setUpgradeFeature(feature);
    setShowUpgradeModal(true);
  };

  // Generate Chapter from Outline (job-based: survives connection drops & proxy timeouts)
  const handleGenerateChapter = async (chapterNum, isRewrite = false) => {
    if (!project?.chapter_outline?.length) {
      toast.error("No chapter outline available");
      return;
    }

    // IMPORTANT: Save any pending chapter outline changes to database BEFORE generating
    try {
      await axios.put(`${API}/project/${id}`, {
        chapter_outline: project.chapter_outline
      });
    } catch (e) {
      console.error("Failed to save chapter outline before generating:", e);
    }

    setGeneratingChapter(chapterNum);
    setChapterProgress({ part: 0, total: 3, status: 'generating' });
    addToLog('generate', `${isRewrite ? 'Rewriting' : 'Generating'} Chapter ${chapterNum}...`);
    playTick(); // Sound on start

    let cancelled = false;
    chapterAbortRef.current = { abort: () => { cancelled = true; } };

    try {
      // Start the server-side job — generation runs in the backend, immune to HTTP timeouts
      const jobRes = await axios.post(`${API}/idea-to-book/chapter-job`, {
        project_id: id,
        chapter_number: chapterNum,
        user_id: user?.id || '',
        rewrite: isRewrite,
      });
      const jobId = jobRes.data.job_id;
      addToLog('generate', `Server job started (${jobId.slice(0, 8)}). Big chapters may take several minutes.`);

      let job = null;
      let pollCount = 0;
      const maxPolls = 600; // 600 * 4s = 40 minutes
      while (pollCount < maxPolls) {
        await new Promise(r => setTimeout(r, 4000));
        pollCount++;
        if (cancelled) {
          addToLog('info', `Stopped watching Chapter ${chapterNum} — generation continues on the server.`);
          toast.info("Stopped watching. Generation continues on the server — refresh the page in a few minutes.");
          return;
        }
        try {
          const st = await axios.get(`${API}/idea-to-book/chapter-job/${jobId}`);
          job = st.data;
        } catch (e) {
          continue; // transient network issue — keep polling
        }
        setChapterProgress({ part: job.part || 0, total: job.total || 3, status: 'generating' });
        // Refresh content every other poll so the chapter grows live in the editor
        if (pollCount % 2 === 0) {
          try {
            const res = await axios.get(`${API}/project/${id}`);
            if (res.data) {
              setProject(prev => ({ ...res.data, chapter_outline: prev?.chapter_outline || res.data.chapter_outline }));
            }
          } catch (e) { /* silent */ }
        }
        if (job.status === 'complete' || job.status === 'failed') break;
      }

      // Final sync from server
      try {
        const res = await axios.get(`${API}/project/${id}`);
        if (res.data) setProject(res.data);
      } catch (e) { /* silent */ }

      if (job?.status === 'complete') {
        setChapterProgress({ part: job.total || 3, total: job.total || 3, status: 'done' });
        playComplete();
        addToLog('generate', `Chapter ${chapterNum} ${isRewrite ? 'rewritten' : 'complete'}!`, `${job.words || 0} words`);
        if (job.error) {
          toast.warning(`Chapter ${chapterNum} finished but one part timed out — review the ending and press Rewrite if needed.`, { duration: 7000 });
        } else {
          toast.success(isRewrite ? `Chapter ${chapterNum} rewritten in place!` : `Chapter ${chapterNum} generated! Story continuity updated for future chapters.`);
        }
        if (isRewrite) {
          setProject(prev => {
            const cleared = (prev.chapter_outline || []).map(ch =>
              ch.number === chapterNum ? { ...ch, edited_after_write: false } : ch
            );
            axios.put(`${API}/project/${id}`, { chapter_outline: cleared }).catch(() => {});
            return { ...prev, chapter_outline: cleared };
          });
        }
      } else {
        setChapterProgress({ part: 0, total: 3, status: 'error' });
        const errorMsg = job?.error || 'Generation timed out';
        addToLog('error', `Failed to generate Chapter ${chapterNum}`, errorMsg);
        if (errorMsg.includes('PROMO_EXPIRED')) {
          toast.error("Your Shweebum Promo has ended. Upgrade to continue writing with high-quality AI storytelling.", { duration: 6000 });
          showUpgradePrompt('Promo Expired');
        } else if (errorMsg.includes('PROMO_LIMIT')) {
          toast.error("You've reached your Promo limit. Upgrade to continue writing with Premium AI quality.", { duration: 6000 });
          showUpgradePrompt('Promo Limit');
        } else if (errorMsg.includes('subscription') || errorMsg.includes('requires Pro') || errorMsg.includes('Upgrade') || errorMsg.includes('limit reached')) {
          showUpgradePrompt('Chapter Generation');
        } else {
          toast.error(`Chapter generation failed: ${errorMsg}`);
        }
      }
    } catch (error) {
      setChapterProgress({ part: 0, total: 3, status: 'error' });
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      addToLog('error', `Failed to start Chapter ${chapterNum} generation`, detail);
      if (error?.response?.status === 403 || `${detail}`.includes('limit')) {
        showUpgradePrompt('Chapter Generation');
      } else {
        toast.error(`Failed to start generation: ${detail}`);
      }
    } finally {
      chapterAbortRef.current = null;
      setGeneratingChapter(null);
      // Reset progress after a delay
      setTimeout(() => setChapterProgress({ part: 0, total: 3, status: 'idle' }), 3000);
    }
  };

  // Cancel chapter generation
  const handleCancelChapterGeneration = () => {
    if (chapterAbortRef.current) {
      chapterAbortRef.current.abort();
      chapterAbortRef.current = null;
    }
  };

  // Story Navigator functions
  const handleAnalyzeStoryPosition = async () => {
    setAnalyzingStory(true);
    setStoryAnalysis(null);
    addToLog('analyze', 'Analyzing story position...');
    try {
      const res = await axios.post(`${API}/story-navigator/analyze?user_id=${user?.id || ''}`, {
        project_id: id
      });
      setStoryAnalysis(res.data);
      addToLog('analyze', 'Story analysis complete', `${res.data.word_count} words, ${res.data.story_progress}% progress`);
    } catch (error) {
      addToLog('error', 'Story analysis failed', error.message);
      toast.error("Analysis failed - try again");
    } finally {
      setAnalyzingStory(false);
    }
  };

  const handleWeaveContent = async () => {
    if (!weaveDirection.trim()) {
      toast.error("Please describe what should happen next");
      return;
    }
    setWeaving(true);
    addToLog('generate', `Weaving content: "${weaveDirection.substring(0, 50)}..."`);
    try {
      const res = await axios.post(`${API}/story-navigator/weave?user_id=${user?.id || ''}`, {
        project_id: id,
        user_direction: weaveDirection,
        length_type: weaveLength.type,
        length_value: weaveLength.value
      });
      
      setProject(prev => ({
        ...prev,
        content: prev.content + "\n\n" + res.data.generated_text,
        word_count: res.data.total_words
      }));
      
      addToLog('generate', 'Content woven successfully', `+${res.data.words_added} words`);
      toast.success(`Added ${res.data.words_added} words!`);
      setWeaveDirection("");
      // Refresh analysis after adding content
      handleAnalyzeStoryPosition();
    } catch (error) {
      if (error?.response?.status === 403) {
        showUpgradePrompt('AI Weave Generation');
      } else {
        toast.error("Generation failed. Please try again.");
      }
      addToLog('error', 'Weave failed', error?.message || 'Unknown error');
    } finally {
      setWeaving(false);
    }
  };

  // Quick Fix — AI-powered surgical chapter editing
  const handleQuickFix = async () => {
    if (!quickFixInstruction.trim()) {
      toast.error("Please describe what you want to fix");
      return;
    }
    if (!project.content) {
      toast.error("No content to fix");
      return;
    }
    
    setQuickFixLoading(true);
    setQuickFixResult(null);
    setQuickFixJobId(null);
    setQuickFixProgress({ status: 'starting', progress: 0, message: 'Creating fix job...' });
    addToLog('quickfix', `Applying Quick Fix to Chapter ${quickFixChapter}`, quickFixInstruction);
    
    try {
      // Step 1: Create the async job
      const createResponse = await axios.post(`${API}/idea-to-book/quick-fix/jobs`, {
        project_id: project.id,
        chapter_number: quickFixChapter,
        instruction: quickFixInstruction,
        user_id: user?.id
      });
      
      const jobId = createResponse.data.job_id;
      setQuickFixJobId(jobId);
      setQuickFixProgress({ status: 'queued', progress: 5, message: 'Job queued...' });
      
      // Step 2: Poll for completion
      const pollInterval = 1500; // 1.5 seconds
      const maxPolls = 120; // 3 minutes max
      let pollCount = 0;
      
      const pollJob = async () => {
        pollCount++;
        if (pollCount > maxPolls) {
          throw new Error('Job timed out. Please try again with a simpler instruction.');
        }
        
        const statusResponse = await axios.get(`${API}/idea-to-book/quick-fix/jobs/${jobId}`);
        const job = statusResponse.data;
        
        // Update progress UI
        const statusMessages = {
          'queued': 'Waiting to start...',
          'locating': 'Locating the scene to fix...',
          'rewriting': 'Surgically rewriting the passage...',
          'completed': 'Fix complete!',
          'failed': job.error || 'Fix failed'
        };
        setQuickFixProgress({ 
          status: job.status, 
          progress: job.progress || 0, 
          message: statusMessages[job.status] || job.status 
        });
        
        if (job.status === 'completed') {
          // Show the result for review
          setQuickFixResult({
            fixedText: job.rewritten_text,
            originalText: job.original_section,
            chapterNumber: quickFixChapter,
            jobId: jobId,
            locatedRange: job.located_range
          });
          setQuickFixLoading(false);
          addToLog('quickfix', `Fix ready for Chapter ${quickFixChapter} — review before accepting`);
          return;
        }
        
        if (job.status === 'failed') {
          throw new Error(job.error || 'Fix failed');
        }
        
        // Keep polling
        setTimeout(pollJob, pollInterval);
      };
      
      await pollJob();
      
    } catch (error) {
      const errMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Quick Fix failed: ${errMsg}`);
      addToLog('error', 'Quick Fix failed', errMsg);
      setQuickFixProgress({ status: 'failed', progress: 0, message: errMsg });
      setQuickFixLoading(false);
    }
  };
  
  // Accept the Quick Fix result
  const handleAcceptQuickFix = async () => {
    if (!quickFixResult) {
      toast.error("No fix to accept");
      return;
    }
    
    const jobId = quickFixResult.jobId || quickFixJobId;
    if (!jobId) {
      toast.error("Job ID not found - please try again");
      console.error("QuickFix Accept failed: no jobId", { quickFixResult, quickFixJobId });
      return;
    }
    
    try {
      // Call the accept endpoint to apply the fix to the database
      const acceptResponse = await axios.post(`${API}/idea-to-book/quick-fix/jobs/${jobId}/accept`);
      
      if (acceptResponse.data?.success) {
        // Refresh the project content
        const updatedProject = await axios.get(`${API}/project/${project.id}`);
        if (updatedProject.data) {
          setProject(prev => ({ ...prev, content: updatedProject.data.content || prev.content }));
        }
        toast.success(`Chapter ${quickFixResult.chapterNumber} updated!`, { duration: 4000 });
        addToLog('quickfix', `Accepted fix for Chapter ${quickFixResult.chapterNumber}`);
        setShowQuickFix(false);
        setQuickFixResult(null);
        setQuickFixInstruction('');
        setQuickFixJobId(null);
        setQuickFixProgress({ status: '', progress: 0, message: '' });
      } else {
        throw new Error(acceptResponse.data?.message || 'Accept returned no success');
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.message || 'Failed to apply fix';
      toast.error(`Failed to accept fix: ${errMsg}`);
      console.error("QuickFix Accept error:", err);
    }
  };
  
  // Reject the Quick Fix — simply discard the result
  const handleRejectQuickFix = () => {
    if (!quickFixResult) return;
    
    toast.info("Fix rejected", { duration: 2000 });
    addToLog('quickfix', `Rejected fix for Chapter ${quickFixResult.chapterNumber}`);
    setQuickFixResult(null);
    setQuickFixJobId(null);
    setQuickFixProgress({ status: '', progress: 0, message: '' });
    // Keep the dialog open so user can try again
  };

  // ═══════════════════════════════════════════════════════════════════
  //  CHAPTER EDITOR — Direct text editing of a specific chapter
  // ═══════════════════════════════════════════════════════════════════
  
  const openChapterEditor = (chapterNum) => {
    if (!project.content) {
      toast.error("No content available");
      return;
    }
    
    // Extract the chapter text - handle both "--- CHAPTER N: TITLE ---" and "--- CHAPTER N: TITLE\n" formats
    // Look for this chapter and stop at the next "--- CHAPTER" delimiter (any number)
    const chapterRegex = new RegExp(
      `(---\\s*CHAPTER\\s+${chapterNum}\\b[^\\n]*(?:---)?\\n?)([\\s\\S]*?)(?=---\\s*CHAPTER\\s+\\d+\\b|$)`,
      'i'
    );
    const match = project.content.match(chapterRegex);
    
    if (match) {
      setChapterEditorNum(chapterNum);
      setChapterEditorText(match[2].trim());
      setShowChapterEditor(true);
    } else {
      toast.error(`Chapter ${chapterNum} not found`);
    }
  };
  
  const handleSaveChapterEdit = async () => {
    if (!project.content || !chapterEditorText) return;
    
    setChapterEditorSaving(true);
    try {
      // Rebuild the content with the edited chapter - handle both delimiter formats
      const chapterRegex = new RegExp(
        `(---\\s*CHAPTER\\s+${chapterEditorNum}\\b[^\\n]*(?:---)?\\n?)[\\s\\S]*?(?=---\\s*CHAPTER\\s+\\d+\\b|$)`,
        'i'
      );
      const match = project.content.match(chapterRegex);
      
      if (!match) {
        toast.error("Could not locate chapter");
        return;
      }
      
      const chapterHeader = match[1];
      const endIdx = match.index + match[0].length;
      const startIdx = match.index;
      
      // Find what comes after this chapter (if anything)
      const afterContent = project.content.substring(endIdx);
      const beforeContent = project.content.substring(0, startIdx);
      
      const updatedContent = beforeContent + chapterHeader + chapterEditorText + '\n\n' + afterContent;
      
      // Save to database
      await axios.put(`${API}/projects/${project.id}`, { content: updatedContent.trim() });
      setProject(prev => ({ ...prev, content: updatedContent.trim() }));
      
      toast.success(`Chapter ${chapterEditorNum} saved!`);
      addToLog('edit', `Manually edited Chapter ${chapterEditorNum}`);
      setShowChapterEditor(false);
    } catch (err) {
      toast.error("Failed to save chapter: " + (err.message || "Unknown error"));
    } finally {
      setChapterEditorSaving(false);
    }
  };


  const handleAnalyze = async () => {
    if (!project.content) {
      toast.error("No content to analyze yet");
      return;
    }
    setAnalyzing(true);
    addToLog('analyze', 'Starting story analysis...');
    try {
      // For long stories, only analyze a portion
      const textToAnalyze = project.content.length > 50000 
        ? project.content.substring(0, 25000) + "\n\n[...middle section...]\n\n" + project.content.substring(project.content.length - 25000)
        : project.content;
      
      const res = await axios.post(`${API}/analyze`, {
        project_id: id,
        text: textToAnalyze
      });
      setSuggestions(res.data.analysis);
      addToLog('analyze', 'Analysis complete', res.data.analysis?.substring(0, 200) + '...');
      toast.success("Analysis complete!");
    } catch (error) {
      addToLog('error', 'Analysis failed', error.message);
      toast.error("Analysis failed - try again");
    } finally {
      setAnalyzing(false);
    }
  };

  // Handle tension change from interactive timeline
  const handleTensionChange = async (chapterNumber, newTensionLevel) => {
    if (!storyBible) {
      toast.error("No story bible loaded. Generate one first.");
      return;
    }
    
    // Get tension name for the new level
    const tensionNames = {
      0: "None", 1: "Calm", 2: "Intrigue", 3: "Unease", 4: "Danger",
      5: "Crisis", 6: "Despair", 7: "Climax", 8: "Resolution", 9: "Aftermath", 10: "Transcendence"
    };
    
    // Update local state immediately for responsive UI
    const currentCurve = storyBible.tension_curve || [];
    const existingEntry = currentCurve.find(t => t.chapter === chapterNumber);
    
    let updatedCurve;
    if (existingEntry) {
      updatedCurve = currentCurve.map(t => 
        t.chapter === chapterNumber 
          ? { ...t, tension_level: newTensionLevel, tension_name: tensionNames[newTensionLevel] || `Level ${newTensionLevel}` }
          : t
      );
    } else {
      updatedCurve = [
        ...currentCurve,
        { chapter: chapterNumber, tension_level: newTensionLevel, tension_name: tensionNames[newTensionLevel] || `Level ${newTensionLevel}` }
      ].sort((a, b) => a.chapter - b.chapter);
    }
    
    // Update local state
    setStoryBible(prev => ({ ...prev, tension_curve: updatedCurve }));
    
    // Show feedback toast
    toast.success(`Chapter ${chapterNumber} tension: ${tensionNames[newTensionLevel]} (${newTensionLevel}/10)`);
    addToLog('edit', `Adjusted Chapter ${chapterNumber} tension to ${newTensionLevel}/10`);
    
    // Save to backend (fire and forget, with error handling)
    try {
      await axios.post(`${API}/tension/save-curve/${id}`, {
        tension_curve: updatedCurve,
        sync_to_outlines: false // Don't auto-sync on each drag - user can do it manually
      });
    } catch (error) {
      console.error("Failed to save tension:", error);
      // Don't show error toast for minor save failures - the local state is updated
    }
  };

  // Story Direction with mood options
  const handleStoryDirection = async (moodOverride) => {
    if (!project.content) {
      toast.error("No content to analyze");
      return;
    }
    const moodToUse = moodOverride || directionType;
    setAssistantLoading(true);
    setAssistantResults(null);
    try {
      const res = await axios.post(`${API}/story-direction`, {
        project_id: id,
        direction_type: moodToUse,
        text: project.content.substring(project.content.length - 10000)
      });
      setAssistantResults(res.data.directions);
    } catch (error) {
      toast.error("Failed to generate directions");
    } finally {
      setAssistantLoading(false);
    }
  };

  // Get structured suggestions for "What should happen next?"
  const handleGetStructuredSuggestions = async () => {
    if (!project?.content || project.content.length < 100) {
      toast.error("Write some content first so Shweebum can analyze it");
      return;
    }
    setLoadingSuggestions(true);
    setDirectionSuggestions([]);
    setSelectedDirection(null);
    try {
      const res = await axios.post(`${API}/story-direction/suggestions`, {
        project_id: id,
        direction_type: "plot_twist",
        text: project.content.substring(project.content.length - 10000)
      });
      setDirectionSuggestions(res.data.suggestions || []);
      toast.success("Shweebum has 3 ideas for you!");
    } catch (error) {
      toast.error("Failed to get suggestions");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Handle selecting a direction and choosing mode
  const handleChooseDirection = (suggestion) => {
    setSelectedDirection(suggestion);
    setShowDirectionChoice(true);
    setDirectionMode(null);
  };

  // Generate content from chosen direction via SSE streaming
  const handleGenerateFromDirection = async (mode, chapterName = '') => {
    if (!selectedDirection) return;
    setShowDirectionChoice(false);
    setShowChapterNameChoice(false);
    setGeneratingFromDirection(true);
    setDirectionGenProgress('Starting...');
    setChapterProgress({ part: 0, total: 3, status: 'generating' });

    try {
      const response = await fetch(`${API}/story-direction/generate-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: id,
          direction: selectedDirection,
          mode: mode,
          chapter_name: chapterName,
          user_id: user?.id || ''
        })
      });

      if (!response.ok) {
        let errorMsg = 'Generation failed';
        try { const t = await response.text(); errorMsg = JSON.parse(t).detail || errorMsg; } catch(e) {}
        throw new Error(errorMsg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'progress') {
                setDirectionGenProgress(data.message);
                if (data.part) setChapterProgress(prev => ({ ...prev, part: data.part }));
              } else if (data.type === 'content') {
                setProject(prev => ({
                  ...prev,
                  content: (prev.content || '') + (prev._dirFirst ? '' : (mode === 'new_chapter' && data.part === 1 ? '' : '')) + data.content,
                  _dirFirst: true
                }));
              } else if (data.type === 'chapter_name') {
                setDirectionGenProgress(`New chapter: ${data.name}`);
              } else if (data.type === 'done') {
                toast.success(`Added ${data.total_words?.toLocaleString()} words total!`);
                // Reload project to get fresh data
                try {
                  const projRes = await axios.get(`${API}/project/${id}`);
                  setProject(projRes.data);
                } catch (e) {}
              } else if (data.type === 'error') {
                toast.error(data.message);
              }
            } catch (e) {}
          }
        }
      }
    } catch (error) {
      toast.error(error?.message || "Failed to generate content");
    } finally {
      setGeneratingFromDirection(false);
      setDirectionGenProgress('');
      setChapterProgress({ part: 0, total: 3, status: 'idle' });
      setSelectedDirection(null);
      setDirectionSuggestions([]);
    }
  };

  // Suggest Names with replace functionality
  const handleSuggestNames = async () => {
    if (!project.content) {
      toast.error("No content to analyze");
      return;
    }
    setAssistantLoading(true);
    setSuggestedNames([]);
    try {
      const res = await axios.post(`${API}/suggest-names`, {
        project_id: id,
        text: project.content.substring(0, 15000)
      });
      setSuggestedNames(res.data.names);
    } catch (error) {
      toast.error("Failed to suggest names");
    } finally {
      setAssistantLoading(false);
    }
  };

  const handleReplaceName = async (oldName, newName) => {
    if (!oldName || !newName) {
      toast.error("Please provide both old and new names");
      return;
    }
    try {
      // Replace in content (entire book)
      const updatedContent = project.content.split(oldName).join(newName);
      const count = (project.content.match(new RegExp(oldName, 'g')) || []).length;
      
      // Also update character list if the name exists there
      let updatedCharacters = project.characters || [];
      updatedCharacters = updatedCharacters.map(char => {
        if (char.name === oldName) {
          return { ...char, name: newName };
        }
        return char;
      });
      
      // Also replace in chapter outlines if they exist
      let updatedOutlines = project.chapter_outlines || [];
      updatedOutlines = updatedOutlines.map(outline => {
        if (outline.summary) {
          outline.summary = outline.summary.split(oldName).join(newName);
        }
        if (outline.key_beats) {
          outline.key_beats = outline.key_beats.split(oldName).join(newName);
        }
        return outline;
      });
      
      setProject(prev => ({
        ...prev,
        content: updatedContent,
        characters: updatedCharacters,
        chapter_outlines: updatedOutlines
      }));
      
      // Save to backend
      await axios.put(`${API}/project/${id}`, { 
        content: updatedContent,
        characters: updatedCharacters,
        chapter_outlines: updatedOutlines
      });
      
      toast.success(`Replaced "${oldName}" with "${newName}" throughout entire book (${count} instances)`);
      setNameToReplace("");
      setNewNameValue("");
    } catch (error) {
      toast.error("Failed to replace name");
    }
  };

  // Suggest Characters - deep, meaningful characters
  const handleSuggestCharacters = async () => {
    if (!project.content) {
      toast.error("No content to analyze");
      return;
    }
    setAssistantLoading(true);
    setAssistantResults(null);
    try {
      const res = await axios.post(`${API}/suggest-characters`, {
        project_id: id,
        text: project.content.substring(project.content.length - 15000)
      });
      setAssistantResults(res.data.characters);
    } catch (error) {
      toast.error("Failed to suggest characters");
    } finally {
      setAssistantLoading(false);
    }
  };

  // Insert character into story
  const handleInsertCharacter = async (character) => {
    // Use streaming endpoint for reliability
    setAssistantLoading(true);
    setCharacterInsertProgress({ part: 0, total: 5, message: 'Starting...' });
    
    try {
      const response = await fetch(`${API}/insert-character-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: id,
          character: character,
          current_content: project.content
        })
      });
      
      if (!response.ok) {
        let errorMsg = 'Failed to insert character';
        try { const t = await response.text(); errorMsg = JSON.parse(t).detail || errorMsg; } catch(e) {}
        throw new Error(errorMsg);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let addedContent = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'progress') {
                setCharacterInsertProgress({ part: data.part, total: data.total, message: data.message });
              } else if (data.type === 'content') {
                addedContent += data.text + '\n\n';
                // Update content in real-time
                setProject(prev => ({
                  ...prev,
                  content: prev.content + '\n\n' + data.text
                }));
              } else if (data.type === 'done') {
                setProject(prev => ({ ...prev, word_count: data.word_count }));
                toast.success(`${character.name} has been woven into your story! (${data.words_added} words added)`);
                setShowCharacterModal(false);
                setCharacterMode('choose');
                setCustomCharacter({ name: '', age: '', sex: '', characteristics: '', introductionMethod: '' });
                setCharacterChapter('');
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (e) {}
          }
        }
      }
    } catch (error) {
      const errorMsg = error?.message || 'Unknown error';
      toast.error(`Failed to insert character: ${errorMsg}`);
    } finally {
      setAssistantLoading(false);
      setCharacterInsertProgress({ part: 0, total: 5, message: '' });
    }
  };

  // Handle custom character creation
  const handleInsertCustomCharacter = async () => {
    if (!customCharacter.name || !customCharacter.characteristics) {
      toast.error("Please fill in at least the name and characteristics");
      return;
    }
    
    const charToInsert = {
      name: customCharacter.name,
      role: "supporting",
      description: `${customCharacter.age ? customCharacter.age + ' year old ' : ''}${customCharacter.sex ? customCharacter.sex + '. ' : ''}${customCharacter.characteristics}`,
      backstory: "A new character entering the story",
      motivation: "To be revealed through their actions",
      conflict: "Their role in the story will unfold",
      introduction_idea: customCharacter.introductionMethod || "Introduce naturally as the story progresses",
      chapter_hint: characterChapter
    };
    
    await handleInsertCharacter(charToInsert);
  };

  // Suggest Plot Points - powerful story moments
  const handleSuggestPlotPoints = async () => {
    if (!project.content) {
      toast.error("No content to analyze");
      return;
    }
    setAssistantLoading(true);
    setAssistantResults(null);
    try {
      const res = await axios.post(`${API}/suggest-plot-points`, {
        project_id: id,
        text: project.content
      });
      setAssistantResults(res.data.plot_points);
    } catch (error) {
      toast.error("Failed to suggest plot points");
    } finally {
      setAssistantLoading(false);
    }
  };

  const handleGetSuggestions = async (type) => {
    // Redirect to specific modals
    if (type === "direction") {
      setShowDirectionModal(true);
      return;
    }
    if (type === "names") {
      setShowNamesModal(true);
      handleSuggestNames();
      return;
    }
    if (type === "character") {
      setShowCharacterModal(true);
      handleSuggestCharacters();
      return;
    }
    if (type === "plot") {
      setShowPlotModal(true);
      handleSuggestPlotPoints();
      return;
    }
    
    // Fallback to old behavior
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/suggest`, {
        project_id: id,
        suggestion_type: type
      });
      setSuggestions(res.data.suggestions);
    } catch (error) {
      toast.error("Failed to get suggestions");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleExport = async (format) => {
    if (!project.content) {
      toast.error("No content to export");
      return;
    }

    // Check subscription for export access
    const canExport = subscription?.limits?.can_export || 
                      subscription?.subscription === 'storyteller' ||
                      subscription?.subscription === 'pro' ||
                      subscription?.subscription === 'author' || 
                      subscription?.subscription === 'family';
    if (!canExport) {
      toast.error("Export requires Shweebum Plus ($10/mo) or higher. Upgrade to unlock!");
      return;
    }

    // Get the full content from the project
    const fullContent = project.content;
    const title = project.title || "My Story";

    if (format === "pdf") {
      const doc = new jsPDF();
      doc.setFont("times", "normal");
      
      // Title page
      doc.setFontSize(24);
      doc.text(title, 105, 100, { align: "center" });
      doc.setFontSize(12);
      doc.text(`${fullContent.split(/\s+/).filter(Boolean).length} words`, 105, 115, { align: "center" });
      doc.addPage();
      
      // Content pages
      doc.setFontSize(12);
      const lines = doc.splitTextToSize(fullContent, 170);
      let y = 25;
      let pageNum = 1;
      
      // Add page numbers
      const addPageNumber = () => {
        doc.setFontSize(10);
        doc.text(`${pageNum}`, 105, 290, { align: "center" });
        doc.setFontSize(12);
      };
      
      lines.forEach((line, idx) => {
        if (y > 270) {
          addPageNumber();
          doc.addPage();
          pageNum++;
          y = 25;
        }
        doc.text(line, 20, y);
        y += 7;
      });
      addPageNumber();
      
      doc.save(`${title}.pdf`);
      toast.success(`PDF exported! (${fullContent.split(/\s+/).filter(Boolean).length} words)`);
    } else if (format === "txt") {
      // Export as plain text with proper formatting
      const textContent = `${title}\n${'='.repeat(title.length)}\n\n${fullContent}`;
      const blob = new Blob([textContent], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Text file exported! (${fullContent.split(/\s+/).filter(Boolean).length} words)`);
    } else if (format === "epub") {
      try {
        // Use backend EPUB generation for professional quality
        const response = await axios.get(`${API}/export/epub/${id}`, {
          responseType: 'blob',
          timeout: 60000
        });
        
        // Create download link
        const blob = new Blob([response.data], { type: 'application/epub+zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${title.replace(/[^a-zA-Z0-9_-]/g, '_')}.epub`;
        a.click();
        URL.revokeObjectURL(url);
        
        const wordCount = fullContent.split(/\s+/).filter(Boolean).length;
        toast.success(`ePub exported! ${wordCount.toLocaleString()} words - KDP ready!`);
      } catch (err) {
        console.error("ePub export failed:", err);
        toast.error(err.response?.data?.detail || "ePub export failed. Make sure you have content to export.");
      }
    }
    setShowExport(false);
  };

  // ===== AUDIO READING FUNCTIONS =====
  const audioQueueRef = useRef([]); // Queue of audio blob URLs (one per batch)
  const currentAudioIndexRef = useRef(0);
  const isGeneratingAudioRef = useRef(false);
  const audioAbortControllerRef = useRef(null);
  const audioSessionRef = useRef(0);
  // Tracks a chapter that auto-advance queued but the user hasn't actually HEARD yet
  const pendingAutoAdvanceRef = useRef(null);
  const isPlayingRef = useRef(false); // Mutex to prevent overlapping play() calls
  const [useQuickPlay, setUseQuickPlay] = useState(false);
  const quickPlayChunkRef = useRef(0); // Track which chunk we're on for Quick Play pause/resume
  const quickPlayChunksRef = useRef([]); // Store chunks array for Quick Play resume
  const quickPlayChapterRef = useRef(1); // Track which chapter Quick Play is reading
  const audioChunkPositionsRef = useRef([]); // Store [startCharIndex, endCharIndex] for each chunk in original content
  const audioChapterContentRef = useRef({ start: 0, length: 0 }); // Track the chapter's content range

  // Helper: scroll textarea to a specific audio segment position
  const scrollToAudioSegment = useCallback((segmentIndex) => {
    const ta = contentRef.current;
    const content = project?.content;
    const positions = audioChunkPositionsRef.current;
    if (!ta || !content || !positions || segmentIndex >= positions.length) return;
    
    const chapterStart = audioChapterContentRef.current.start;
    const chunkInfo = positions[segmentIndex];
    if (!chunkInfo) return;
    
    const absPos = chapterStart + chunkInfo.originalStart;
    const ratio = absPos / content.length;
    ta.scrollTop = Math.max(0, (ratio * ta.scrollHeight) - (ta.clientHeight * 0.35));
    
    const selEnd = Math.min(absPos + 150, chapterStart + chunkInfo.originalEnd, content.length);
    ta.focus({ preventScroll: true });
    ta.setSelectionRange(absPos, selEnd);
  }, [project]);

  // Auto-scroll text to follow audio playback — continuous smooth tracking
  const audioScrollRAF = useRef(null);
  const isAudioPlayingRef = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    isAudioPlayingRef.current = isAudioPlaying;
  }, [isAudioPlaying]);
  
  // TTS Phase 2 constants — tune for sync feel
  // Negative value means highlight LEADS audio slightly (compensates for perceived lag).
  // Test values: -100, -150, -200, -250, -300
  const TTS_HIGHLIGHT_OFFSET_MS = -200;
  const TTS_VISIBLE_TOP_FRACTION = 0.18;   // sentence is "too high" if above 18% from top
  const TTS_VISIBLE_BOTTOM_FRACTION = 0.65; // sentence is "too low" if below 65% from top
  const TTS_TARGET_SCROLL_FRACTION = 0.35; // when scrolling, place sentence at 35% from top

  // Sync mirror div styles to match the textarea so wrapped-line measurements
  // are accurate. Called once per sentence change (not every frame).
  const syncMirrorToTextarea = (ta, mirror) => {
    const cs = window.getComputedStyle(ta);
    mirror.style.width = ta.clientWidth + 'px';
    mirror.style.fontFamily = cs.fontFamily;
    mirror.style.fontSize = cs.fontSize;
    mirror.style.fontWeight = cs.fontWeight;
    mirror.style.lineHeight = cs.lineHeight;
    mirror.style.letterSpacing = cs.letterSpacing;
    mirror.style.textAlign = cs.textAlign;
    mirror.style.padding = cs.padding;
    mirror.style.border = cs.border;
    mirror.style.boxSizing = cs.boxSizing;
    mirror.style.wordSpacing = cs.wordSpacing;
    mirror.style.textIndent = cs.textIndent;
    mirror.style.tabSize = cs.tabSize;
    mirror.style.textTransform = cs.textTransform;
  };

  useEffect(() => {
    if (!isAudioPlaying) {
      if (audioScrollRAF.current) cancelAnimationFrame(audioScrollRAF.current);
      // Hide word overlay when audio stops
      if (wordHighlightRef.current) wordHighlightRef.current.style.display = 'none';
      return;
    }
    
    let lastSentenceKey = '';      // identifies the currently-highlighted sentence
    let lastWordKey = '';           // identifies the currently-highlighted word
    let lastMirrorSyncMs = 0;       // throttle expensive computed-style read
    let wordVerifyFailCount = 0;    // # of times word verification failed; disable overlay after threshold
    let wordOverlayDisabled = false; // auto-disabled if too many verification failures
    
    const tick = () => {
      if (!isAudioPlayingRef.current) return;
      
      // Crash-proof: any uncaught error inside this RAF MUST NOT stop the loop,
      // otherwise the sentence highlight (which depends on this loop) freezes.
      try {
        const ta = contentRef.current;
        const audio = audioRef.current;
        const content = project?.content;
        const segIdx = currentAudioIndexRef.current;
        const sentenceTimings = audioSentenceTimingRef.current;
        const wordTimings = audioWordTimingRef.current;
        
        if (!ta || !audio || !content || !sentenceTimings) return;
        
        const sentencesForChunk = sentenceTimings[segIdx];
        if (!sentencesForChunk || sentencesForChunk.length === 0) return;
        
        // Find active sentence: largest sentence.start <= (currentTime + offset)
        const adjustedTime = (audio.currentTime || 0) + (TTS_HIGHLIGHT_OFFSET_MS / 1000);
        let activeIdx = 0;
        for (let i = 0; i < sentencesForChunk.length; i++) {
          if (sentencesForChunk[i].start <= adjustedTime) activeIdx = i;
          else break;
        }
        const active = sentencesForChunk[activeIdx];
        if (!active) return;
        
        const sentenceKey = segIdx + ':' + activeIdx;
        const sentenceChanged = sentenceKey !== lastSentenceKey;
        
        const startChar = Math.max(0, Math.min(content.length, active.fullStartChar | 0));
        const endChar = Math.max(startChar, Math.min(content.length, active.fullEndChar | 0));
        
        // ====== Sentence-level work (only on sentence change) — PRIMARY HIGHLIGHT ======
        if (sentenceChanged) {
          lastSentenceKey = sentenceKey;
          
          // Highlight active sentence (browser textarea selection)
          try {
            programmaticScrollRef.current = true;
            ta.focus({ preventScroll: true });
            ta.setSelectionRange(startChar, endChar);
            setTimeout(() => { programmaticScrollRef.current = false; }, 0);
          } catch (e) { /* ignore */ }
          
          // Auto-scroll: only on sentence change, only if outside the comfortable zone.
          if (!userScrolledRef.current && mirrorRef.current) {
            try {
              const mirror = mirrorRef.current;
              const now = performance.now();
              if (now - lastMirrorSyncMs > 500) {
                syncMirrorToTextarea(ta, mirror);
                lastMirrorSyncMs = now;
              }
              mirror.textContent = content.substring(0, startChar);
              const marker = document.createElement('span');
              marker.appendChild(document.createTextNode('\u200B'));
              mirror.appendChild(marker);
              
              const markerTop = marker.offsetTop;
              const viewportTop = ta.scrollTop;
              const viewportH = ta.clientHeight;
              const zoneTop = viewportTop + viewportH * TTS_VISIBLE_TOP_FRACTION;
              const zoneBottom = viewportTop + viewportH * TTS_VISIBLE_BOTTOM_FRACTION;
              
              if (markerTop < zoneTop || markerTop > zoneBottom) {
                const targetScroll = Math.max(0, markerTop - viewportH * TTS_TARGET_SCROLL_FRACTION);
                programmaticScrollRef.current = true;
                ta.scrollTop = targetScroll;
                setTimeout(() => { programmaticScrollRef.current = false; }, 0);
              }
            } catch (scrollErr) { /* non-fatal */ }
          }
        }
        
        // ====== Word-level OPTIONAL emphasis — failures must NOT break sentence highlight ======
        const wordsForChunk = wordTimings && wordTimings[segIdx];
        const wordOverlay = wordHighlightRef.current;
        if (wordOverlayDisabled || !wordsForChunk || wordsForChunk.length === 0 || !wordOverlay || !mirrorRef.current) {
          if (wordOverlay) wordOverlay.style.display = 'none';
          return;
        }
        
        try {
          let wIdx = -1;
          for (let i = 0; i < wordsForChunk.length; i++) {
            if (wordsForChunk[i].start <= adjustedTime) wIdx = i;
            else break;
          }
          if (wIdx < 0) { wordOverlay.style.display = 'none'; return; }
          
          const activeWord = wordsForChunk[wIdx];
          const wStart = activeWord.fullStartChar;
          const wEnd = activeWord.fullEndChar;
          const wordInSentence = wStart >= 0 && wStart >= active.fullStartChar - 5 && wEnd <= active.fullEndChar + 5;
          
          const wordKey = segIdx + ':' + wIdx;
          if (wordKey === lastWordKey) return;  // no change → leave overlay in place
          lastWordKey = wordKey;
          
          if (!wordInSentence) { wordOverlay.style.display = 'none'; return; }
          
          // STRICT VERIFICATION: the text at the mapped position MUST match the WordBoundary text.
          // If not, the mapping is wrong → hide overlay (sentence highlight remains visible).
          const extracted = content.substring(wStart, wEnd).replace(/[^a-zA-Z0-9']/g, '').toLowerCase();
          const expected = (activeWord.text || '').replace(/[^a-zA-Z0-9']/g, '').toLowerCase();
          if (!extracted || !expected || extracted !== expected) {
            wordOverlay.style.display = 'none';
            wordVerifyFailCount++;
            if (wordVerifyFailCount >= 8) {
              // Too many mismatches → disable word overlay for this session.
              wordOverlayDisabled = true;
              console.warn('[TTS] Word overlay auto-disabled after 8 verification failures. Sentence highlight remains active.');
            }
            return;
          }
          
          // Verified — render and measure
          const mirror = mirrorRef.current;
          const now = performance.now();
          if (now - lastMirrorSyncMs > 500) {
            syncMirrorToTextarea(ta, mirror);
            lastMirrorSyncMs = now;
          }
          mirror.textContent = content.substring(0, wStart);
          const wordSpan = document.createElement('span');
          wordSpan.textContent = content.substring(wStart, wEnd);
          mirror.appendChild(wordSpan);
          
          const rects = wordSpan.getClientRects();
          if (rects.length === 0) { wordOverlay.style.display = 'none'; return; }
          
          const mirrorRect = mirror.getBoundingClientRect();
          const firstRect = rects[0];
          const topInMirror = firstRect.top - mirrorRect.top;
          const leftInMirror = firstRect.left - mirrorRect.left;
          const topInTa = topInMirror - ta.scrollTop;
          
          if (topInTa < -firstRect.height || topInTa > ta.clientHeight) {
            wordOverlay.style.display = 'none';
            return;
          }
          
          wordOverlay.style.display = 'block';
          wordOverlay.style.left = leftInMirror + 'px';
          wordOverlay.style.top = topInTa + 'px';
          wordOverlay.style.width = firstRect.width + 'px';
          wordOverlay.style.height = firstRect.height + 'px';
        } catch (wordErr) {
          // Word overlay path is non-critical — hide and continue. Sentence remains.
          if (wordOverlay) wordOverlay.style.display = 'none';
        }
      } catch (tickErr) {
        // Catastrophic failure — log but never stop the loop.
        console.warn('[TTS] tick error (continuing):', tickErr?.message);
      } finally {
        // ALWAYS reschedule — the loop must never stop while audio is playing.
        if (isAudioPlayingRef.current) {
          audioScrollRAF.current = requestAnimationFrame(tick);
        }
      }
    };
    
    audioScrollRAF.current = requestAnimationFrame(tick);
    
    return () => {
      if (audioScrollRAF.current) cancelAnimationFrame(audioScrollRAF.current);
      if (wordHighlightRef.current) wordHighlightRef.current.style.display = 'none';
    };
  }, [isAudioPlaying, project]);
  
  // Quick Play using browser's built-in speech synthesis (instant, free)
  const handleQuickPlay = (targetChapter = null) => {
    // Guard: if called from onClick, targetChapter is the event — ignore it
    if (targetChapter && typeof targetChapter !== 'number') targetChapter = null;
    
    if (isAudioPlaying && !targetChapter) {
      // User clicked while playing — use proper pause, not cancel
      handlePauseAudio();
      return;
    }
    
    const fullContent = project.content?.trim();
    if (!fullContent) {
      toast.error("No content to read!");
      return;
    }
    
    // Use passed chapter or current state
    const chapterToPlay = targetChapter || audioChapter;
    quickPlayChapterRef.current = chapterToPlay;
    
    // Extract content for the current chapter — match by ACTUAL chapter number
    const qpSegments = getChapterSegments(fullContent);
    let content = fullContent;
    
    if (qpSegments.length > 0) {
      const seg = qpSegments.find(s => s.number === chapterToPlay) || qpSegments[chapterToPlay - 1];
      if (seg) {
        content = fullContent.substring(seg.start, seg.end).trim();
        addToLog('audio', `Quick Playing Chapter ${chapterToPlay}`);
      }
    }
    
    // Check if speech synthesis is supported
    if (!('speechSynthesis' in window)) {
      toast.error("Your browser doesn't support text-to-speech. Try the AI Voice option instead.");
      return;
    }
    
    // Cancel any existing speech
    window.speechSynthesis.cancel();
    
    // On mobile, we need to split into smaller chunks (mobile has ~200-300 char limit sometimes)
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const MAX_CHUNK = isMobile ? 200 : 5000;
    
    // Split content into chunks
    const chunks = [];
    let remaining = content;
    while (remaining.length > 0) {
      if (remaining.length <= MAX_CHUNK) {
        chunks.push(remaining);
        break;
      }
      // Find sentence break
      let breakPoint = remaining.substring(0, MAX_CHUNK).lastIndexOf('. ');
      if (breakPoint < 50) breakPoint = remaining.substring(0, MAX_CHUNK).lastIndexOf(' ');
      if (breakPoint < 50) breakPoint = MAX_CHUNK;
      chunks.push(remaining.substring(0, breakPoint + 1));
      remaining = remaining.substring(breakPoint + 1);
    }
    
    // Store chunks and reset position for pause/resume
    quickPlayChunksRef.current = chunks;
    quickPlayChunkRef.current = 0;
    
    const speakFromChunk = (startIndex) => {
      let currentChunk = startIndex;
      
      const speakNext = () => {
        if (currentChunk >= chunks.length) {
          // Chapter done — auto-advance to the next chapter in document order
          const segs = getChapterSegments(project.content || '');
          const curSegIdx = segs.findIndex(s => s.number === chapterToPlay);
          const nextSeg = curSegIdx !== -1 ? segs[curSegIdx + 1] : null;
          if (nextSeg) {
            const next = nextSeg.number;
            setAudioChapter(next);
            toast.info(`Chapter ${chapterToPlay} done. Playing Chapter ${next}...`, { duration: 2000 });
            addToLog('audio', `Auto-advancing to Chapter ${next}`);
            pendingAutoAdvanceRef.current = { from: chapterToPlay, to: next };
            const advSession = audioSessionRef.current;
            setTimeout(() => {
              if (audioSessionRef.current !== advSession) return;
              handleQuickPlay(next);
            }, 500);
          } else {
            setIsAudioPlaying(false);
            toast.success("Finished reading all chapters!");
            addToLog('audio', 'All chapters complete');
          }
          return;
        }
        
        // Update ref so pause knows where we are
        quickPlayChunkRef.current = currentChunk;
        
        const utterance = new SpeechSynthesisUtterance(chunks[currentChunk]);
        utterance.rate = audioSpeed;
        utterance.pitch = 1;
        utterance.volume = 1;
        
        // Try to find a good voice
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          const englishVoice = voices.find(v => 
            v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Microsoft'))
          ) || voices.find(v => v.lang.startsWith('en')) || voices[0];
          if (englishVoice) utterance.voice = englishVoice;
        }
        
        utterance.onstart = () => { pendingAutoAdvanceRef.current = null; };
        utterance.onend = () => {
          currentChunk++;
          quickPlayChunkRef.current = currentChunk;
          speakNext();
        };
        
        utterance.onerror = (e) => {
          console.error("Speech error:", e);
          if (e.error !== 'interrupted' && e.error !== 'canceled') {
            currentChunk++;
            quickPlayChunkRef.current = currentChunk;
            speakNext(); // Try next chunk
          }
        };
        
        window.speechSynthesis.speak(utterance);
      };
      
      speakNext();
    };
    
    // Start playing
    setIsAudioPlaying(true);
    setIsAudioPaused(false);
    setCanResumeAudio(false);
    toast.success("Playing with browser voice!");
    
    // Ensure voices are loaded before starting
    const startSpeaking = () => {
      speakFromChunk(0);
    };
    
    if (window.speechSynthesis.getVoices().length > 0) {
      startSpeaking();
    } else {
      // Wait for voices to load - use a one-time handler
      const onVoicesReady = () => {
        window.speechSynthesis.onvoiceschanged = null; // Clear handler to prevent re-triggering
        startSpeaking();
      };
      window.speechSynthesis.onvoiceschanged = onVoicesReady;
      // Fallback timeout in case onvoiceschanged doesn't fire
      setTimeout(() => {
        if (quickPlayChunkRef.current === 0 && !window.speechSynthesis.speaking) {
          window.speechSynthesis.onvoiceschanged = null;
          startSpeaking();
        }
      }, 500);
    }
  };
  
  // Resume Quick Play from where we left off (after pause)
  const resumeQuickPlayFromChunk = () => {
    const chunks = quickPlayChunksRef.current;
    const startChunk = quickPlayChunkRef.current;
    const chapterToPlay = quickPlayChapterRef.current;
    
    if (!chunks.length || startChunk >= chunks.length) {
      // Nothing to resume — restart chapter
      handleQuickPlay(chapterToPlay);
      return;
    }
    
    // Cancel any stale speech
    window.speechSynthesis.cancel();
    
    setIsAudioPlaying(true);
    setIsAudioPaused(false);
    setCanResumeAudio(false);
    addToLog('audio', `Resuming Quick Play from chunk ${startChunk + 1}/${chunks.length}`);
    
    let currentChunk = startChunk;
    
    const speakNext = () => {
      if (currentChunk >= chunks.length) {
        const segs = getChapterSegments(project.content || '');
        const curSegIdx = segs.findIndex(s => s.number === chapterToPlay);
        const nextSeg = curSegIdx !== -1 ? segs[curSegIdx + 1] : null;
        if (nextSeg) {
          const next = nextSeg.number;
          setAudioChapter(next);
          toast.info(`Chapter ${chapterToPlay} done. Playing Chapter ${next}...`, { duration: 2000 });
          pendingAutoAdvanceRef.current = { from: chapterToPlay, to: next };
          const advSession = audioSessionRef.current;
          setTimeout(() => {
            if (audioSessionRef.current !== advSession) return;
            handleQuickPlay(next);
          }, 500);
        } else {
          setIsAudioPlaying(false);
          toast.success("Finished reading all chapters!");
        }
        return;
      }
      
      quickPlayChunkRef.current = currentChunk;
      
      const utterance = new SpeechSynthesisUtterance(chunks[currentChunk]);
      utterance.rate = audioSpeed;
      utterance.pitch = 1;
      utterance.volume = 1;
      
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        const englishVoice = voices.find(v => 
          v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Microsoft'))
        ) || voices.find(v => v.lang.startsWith('en')) || voices[0];
        if (englishVoice) utterance.voice = englishVoice;
      }
      
      utterance.onstart = () => { pendingAutoAdvanceRef.current = null; };
      utterance.onend = () => {
        currentChunk++;
        quickPlayChunkRef.current = currentChunk;
        speakNext();
      };
      
      utterance.onerror = (e) => {
        if (e.error !== 'interrupted' && e.error !== 'canceled') {
          currentChunk++;
          quickPlayChunkRef.current = currentChunk;
          speakNext();
        }
      };
      
      window.speechSynthesis.speak(utterance);
    };
    
    speakNext();
  };
  
  // Premium AI voice play — large chunks with pre-buffering
  // Uses 2000-char chunks (each ~90s of audio, ~20s to generate)
  // Generates ahead while playing so playback never catches up
  const handlePlayContent = async (targetChapter = null) => {
    // Guard: if called from onClick, targetChapter is the event — ignore it
    if (targetChapter && typeof targetChapter !== 'number') targetChapter = null;
    
    if (isAudioPlaying && !targetChapter) {
      // User clicked while playing — pause
      handlePauseAudio();
      return;
    }
    
    // Don't compete with an active book generation for server resources —
    // simultaneous TTS + generation overloaded the server (timeouts / HTTP 520s)
    if (fullBookJobId) {
      toast.info("Audio narration is paused while your book is generating. Try again once generation finishes.", { duration: 4000 });
      addToLog('audio', 'Blocked: book generation in progress');
      return;
    }
    
    // Check subscription for AI Voice access
    const canUseAIVoice = subscription?.limits?.can_ai_voice || 
                          subscription?.subscription === 'author' || 
                          subscription?.subscription === 'family';
    if (!canUseAIVoice) {
      toast.error("AI Voice is a paid feature. Upgrade to Shweebum Plus ($10/mo) to unlock!");
      return;
    }
    
    const voiceLimit = subscription?.limits?.ai_voice_limit;
    const voiceUsed = subscription?.usage?.ai_voice_this_month || 0;
    if (voiceLimit > 0 && voiceUsed >= voiceLimit) {
      toast.error(`AI Voice limit reached (${voiceLimit}/month). Upgrade to Shweebum Premium for unlimited!`);
      return;
    }
    
    const fullContent = project.content?.trim();
    if (!fullContent) {
      toast.error("No content to read. Write something first!");
      return;
    }
    
    const chapterToPlay = targetChapter || audioChapter;
    
    // Extract chapter content — match by ACTUAL chapter number, not ordinal position
    // (drafts can have missing/out-of-order chapter numbers)
    const segments = getChapterSegments(fullContent);
    let content = fullContent;
    
    if (segments.length > 0) {
      const seg = segments.find(s => s.number === chapterToPlay) || segments[chapterToPlay - 1];
      if (seg) {
        content = fullContent.substring(seg.start, seg.end).trim();
      }
    }
    
    // Sanitize text for TTS
    const sanitize = (t) => t
      .replace(/\u2014/g, ' - ').replace(/\u2013/g, ' - ')
      .replace(/[\u201C\u201D\u201E\u201F""]/g, '"')
      .replace(/[\u2018\u2019\u201A\u201B'']/g, "'")
      .replace(/\u2026/g, '...').replace(/…/g, '...')
      .replace(/[\u00A0\u2003\u2002\u2009\u200A]/g, ' ')
      .replace(/[\u200B\u200C\u200D\u2060\uFEFF]/g, '')
      .replace(/\*{1,3}/g, '').replace(/_{2,}/g, '').replace(/`{1,3}/g, '')
      .replace(/^#{1,6}\s*/gm, '').replace(/---+/g, '. ')
      .replace(/\r\n/g, '\n').replace(/\n{2,}/g, '. ').replace(/\n/g, ' ')
      .replace(/[ \t]+/g, ' ').replace(/\.\s*\./g, '.').trim();
    
    const cleaned = sanitize(content);
    if (!cleaned || cleaned.length < 10) {
      toast.error("No readable content found");
      return;
    }
    
    // Split into smaller 1000-char chunks for faster first-byte audio
    // Smaller chunks = faster generation per chunk = audio starts sooner
    const CHUNK_SIZE = 1000;
    const chunks = [];
    const chunkPositions = []; // Map each chunk to approximate position in original content
    let pos = 0;
    while (pos < cleaned.length) {
      let end = Math.min(pos + CHUNK_SIZE, cleaned.length);
      if (end < cleaned.length) {
        const search = cleaned.substring(pos, end);
        let bp = search.lastIndexOf('. ');
        if (bp > CHUNK_SIZE * 0.3) { end = pos + bp + 2; }
        else { bp = search.lastIndexOf(' '); if (bp > CHUNK_SIZE * 0.3) end = pos + bp + 1; }
      }
      const chunk = cleaned.substring(pos, end).trim();
      if (chunk.length >= 10) {
        // Map cleaned position to approximate original content position using ratio
        const ratioStart = pos / cleaned.length;
        const ratioEnd = end / cleaned.length;
        chunkPositions.push({
          cleanedStart: pos,
          cleanedEnd: end,
          originalStart: Math.floor(ratioStart * content.length),
          originalEnd: Math.floor(ratioEnd * content.length),
        });
        chunks.push(chunk);
      }
      pos = end;
    }
    
    if (chunks.length === 0) {
      toast.error("No readable content");
      return;
    }
    
    addToLog('audio', `Chapter ${chapterToPlay}`, `${cleaned.length} chars, ${chunks.length} segments (~${Math.round(chunks.length * 1.5)} min)`);
    
    // Store chunk positions for text-follows-audio
    audioChunkPositionsRef.current = chunkPositions;
    // Store where the chapter starts in the full project content
    const chapterPos = findChapterPosition(project, chapterToPlay);
    audioChapterContentRef.current = { start: chapterPos >= 0 ? chapterPos : 0, length: content.length };
    // TTS Phase 1: Reset sentence-level timing storage (one entry per chunk)
    audioSentenceTimingRef.current = new Array(chunks.length).fill(null);
    // TTS Phase 1.5: Reset word-level timing storage
    audioWordTimingRef.current = new Array(chunks.length).fill(null);
    audioChapterBoundsRef.current = {
      startPos: chapterPos >= 0 ? chapterPos : 0,
      endPos: (chapterPos >= 0 ? chapterPos : 0) + content.length,
    };
    // Capture original (un-sanitized) chapter content for sentence lookup
    const chapterOriginalContent = content;
    
    // Reset state - cleanup old URLs first
    audioQueueRef.current.forEach(url => { try { if (url) URL.revokeObjectURL(url); } catch(e) {} });
    audioQueueRef.current = [];
    currentAudioIndexRef.current = 0;
    isGeneratingAudioRef.current = true;
    isPlayingRef.current = false; // Reset mutex
    
    if (audioAbortControllerRef.current) audioAbortControllerRef.current.abort();
    audioAbortControllerRef.current = new AbortController();
    const sessionId = ++audioSessionRef.current;
    
    setIsAudioLoading(true);
    setIsAudioPlaying(true);
    setAudioProgress({ currentTime: 0, duration: 0, segment: 0, totalSegments: chunks.length });
    toast.loading(`Generating audio (${chunks.length} segments)...`, { id: "audio-loading", duration: 120000 });
    
    // Create fresh audio element to avoid stale event handlers
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.src = '';
    }
    audioRef.current = new Audio();
    const audio = audioRef.current;
    audio.playbackRate = audioSpeed;
    
    // Track progress for the seek bar
    audio.ontimeupdate = () => {
      setAudioProgress(prev => ({
        ...prev,
        currentTime: audio.currentTime,
        duration: audio.duration || 0,
      }));
    };
    
    // Generate a single chunk via TTS API (sentence-timed)
    const generateChunk = async (index) => {
      if (audioSessionRef.current !== sessionId) return null;
      const text = chunks[index];
      
      for (let attempt = 0; attempt < 3; attempt++) {
        if (audioSessionRef.current !== sessionId) return null;
        try {
          addToLog('audio', `Generating segment ${index + 1}/${chunks.length}...`, `${text.length} chars`);
          
          // Use XMLHttpRequest instead of fetch to avoid "Request object could not be cloned" 
          // error on some mobile browsers where Service Workers or extensions interfere
          const result = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', `${API}/tts/generate-timed`, true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.responseType = 'arraybuffer';
            xhr.timeout = 60000;
            
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                const buf = xhr.response;
                if (!buf || buf.byteLength < 500) {
                  reject(new Error(`Response too small: ${buf?.byteLength || 0}b`));
                  return;
                }
                try {
                  // Parse length-prefixed binary format:
                  //   [4 bytes BE uint32 = metadata length]
                  //   [N bytes UTF-8 JSON metadata]
                  //   [M bytes MP3 audio]
                  const view = new DataView(buf);
                  const metaLen = view.getUint32(0, false); // big-endian
                  if (metaLen <= 0 || metaLen > buf.byteLength - 4) {
                    reject(new Error(`Invalid metadata length: ${metaLen}`));
                    return;
                  }
                  const metaBytes = new Uint8Array(buf, 4, metaLen);
                  const metaJson = new TextDecoder('utf-8').decode(metaBytes);
                  const meta = JSON.parse(metaJson);
                  const audioBytes = buf.slice(4 + metaLen);
                  const blob = new Blob([audioBytes], { type: 'audio/mpeg' });
                  resolve({
                    blob,
                    sentences: meta.sentences || [],
                    words: meta.words || [],
                  });
                } catch (parseErr) {
                  reject(new Error(`Parse error: ${parseErr.message}`));
                }
              } else {
                reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
              }
            };
            
            xhr.onerror = () => reject(new Error('Network error - check your connection'));
            xhr.ontimeout = () => reject(new Error('Timeout (60s)'));
            xhr.onabort = () => reject(new Error('Aborted'));
            
            // Store XHR for cancellation
            if (!audioAbortControllerRef.current) {
              audioAbortControllerRef.current = { abort: () => xhr.abort() };
            }
            
            // ALWAYS send speed: 1.0 — playbackRate handles speed client-side
            xhr.send(JSON.stringify({ text, voice: audioVoice }));
          });
          
          if (audioSessionRef.current !== sessionId) return null;
          
          const audioUrl = URL.createObjectURL(result.blob);
          
          // Map each sentence's text to its absolute position in the full project content.
          // Backend gives us sentence text + time offsets; we locate the text inside the
          // original chapter content so highlights align with what's actually on screen.
          const chunkInfo = chunkPositions[index] || { originalStart: 0, originalEnd: chapterOriginalContent.length };
          const chapterAbsStart = audioChapterContentRef.current.start;
          const searchStart = Math.max(0, chunkInfo.originalStart - 50);
          const searchEnd = Math.min(chapterOriginalContent.length, chunkInfo.originalEnd + 200);
          const haystack = chapterOriginalContent.substring(searchStart, searchEnd);
          
          // Normalize both sentence text AND haystack identically before matching,
          // so smart quotes / em dashes / ellipses / whitespace don't cause drift.
          const normalizeForMatch = (s) => s
            .toLowerCase()
            .replace(/[\u2014\u2013]/g, ' - ')
            .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
            .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
            .replace(/\u2026/g, '...')
            .replace(/\s+/g, ' ')
            .trim();
          
          // Build normalized haystack and an index map: normalized[i] -> original[origIdx]
          // We walk the original char-by-char, normalize each char, and track mapping.
          let normHay = '';
          const normToOrig = []; // normToOrig[i] = original index in haystack of normHay[i]
          for (let k = 0; k < haystack.length; k++) {
            const ch = haystack[k];
            // Apply same normalization per-char (best effort — most rules are 1:1)
            let norm;
            if (ch === '\u2014' || ch === '\u2013') norm = ' - ';
            else if ('\u201C\u201D\u201E\u201F'.indexOf(ch) >= 0) norm = '"';
            else if ('\u2018\u2019\u201A\u201B'.indexOf(ch) >= 0) norm = "'";
            else if (ch === '\u2026') norm = '...';
            else if (/\s/.test(ch)) norm = ' ';
            else norm = ch.toLowerCase();
            for (let m = 0; m < norm.length; m++) {
              // Collapse consecutive spaces in normHay
              if (norm[m] === ' ' && normHay.endsWith(' ')) continue;
              normHay += norm[m];
              normToOrig.push(k);
            }
          }
          
          let cursor = 0; // running pointer in normHay
          const mappedSentences = [];
          (result.sentences || []).forEach((s) => {
            const sentText = (s.text || '').trim();
            if (!sentText) return;
            const normSent = normalizeForMatch(sentText);
            const probe = normSent.substring(0, Math.min(60, normSent.length));
            let foundNorm = -1;
            if (probe.length >= 4) {
              foundNorm = normHay.indexOf(probe, cursor);
              if (foundNorm === -1) foundNorm = normHay.indexOf(probe, 0);
            }
            
            let fullStartChar, fullEndChar;
            if (foundNorm !== -1 && foundNorm < normToOrig.length) {
              const origOffset = normToOrig[foundNorm];
              fullStartChar = chapterAbsStart + searchStart + origOffset;
              // End position: try to map end of normalized sentence to original
              const endNormIdx = Math.min(foundNorm + normSent.length - 1, normHay.length - 1);
              const origEndOffset = normToOrig[endNormIdx] !== undefined ? normToOrig[endNormIdx] + 1 : origOffset + sentText.length;
              fullEndChar = chapterAbsStart + searchStart + origEndOffset;
              cursor = foundNorm + Math.max(1, normSent.length - 20);
            } else {
              // Last-resort fallback: linear interpolation within chunk
              const chunkLen = Math.max(1, chunkInfo.originalEnd - chunkInfo.originalStart);
              const total = Math.max(1, (result.sentences || []).length);
              fullStartChar = chapterAbsStart + chunkInfo.originalStart + Math.floor(chunkLen * (mappedSentences.length / total));
              fullEndChar = chapterAbsStart + chunkInfo.originalStart + Math.floor(chunkLen * ((mappedSentences.length + 1) / total));
            }
            
            mappedSentences.push({
              text: sentText,
              start: s.start || 0,    // seconds
              end: s.end || 0,        // seconds
              fullStartChar,
              fullEndChar,
            });
          });
          audioSentenceTimingRef.current[index] = mappedSentences;
          
          // ===== TTS Phase 1.5: Map WordBoundary events to absolute char positions =====
          // Reuses the same normHay / normToOrig built above for sentences.
          let wordCursor = 0;
          const mappedWords = [];
          (result.words || []).forEach((w) => {
            const wordText = (w.text || '').trim();
            if (!wordText) return;
            const normWord = normalizeForMatch(wordText);
            if (normWord.length < 1) return;
            let foundNorm = -1;
            // For short words (<= 3 chars) we still try to find them but with a word-boundary check
            if (normWord.length <= 3) {
              // Search with surrounding non-word-char check to avoid partial matches
              let pos = wordCursor;
              while (pos < normHay.length) {
                const idx = normHay.indexOf(normWord, pos);
                if (idx === -1) break;
                const before = idx === 0 ? ' ' : normHay[idx - 1];
                const after = idx + normWord.length >= normHay.length ? ' ' : normHay[idx + normWord.length];
                if (!/[a-z0-9]/i.test(before) && !/[a-z0-9]/i.test(after)) {
                  foundNorm = idx;
                  break;
                }
                pos = idx + 1;
              }
              if (foundNorm === -1) foundNorm = normHay.indexOf(normWord, wordCursor);
            } else {
              foundNorm = normHay.indexOf(normWord, wordCursor);
            }
            if (foundNorm === -1) foundNorm = normHay.indexOf(normWord, 0);
            
            let fullStartChar = -1, fullEndChar = -1;
            if (foundNorm !== -1 && foundNorm < normToOrig.length) {
              const origOffset = normToOrig[foundNorm];
              fullStartChar = chapterAbsStart + searchStart + origOffset;
              const endNormIdx = Math.min(foundNorm + normWord.length - 1, normHay.length - 1);
              const origEndOffset = normToOrig[endNormIdx] !== undefined ? normToOrig[endNormIdx] + 1 : origOffset + wordText.length;
              fullEndChar = chapterAbsStart + searchStart + origEndOffset;
              wordCursor = foundNorm + normWord.length;
            }
            // If mapping fails, fullStartChar stays -1 — tick will skip word highlight gracefully
            mappedWords.push({
              text: wordText,
              start: w.start || 0,
              end: w.end || 0,
              fullStartChar,
              fullEndChar,
            });
          });
          audioWordTimingRef.current[index] = mappedWords;
          
          addToLog('audio', `Segment ${index + 1}/${chunks.length} ready`, `${mappedSentences.length} sentences, ${mappedWords.length} words`);
          return audioUrl;
        } catch (err) {
          if (audioSessionRef.current !== sessionId) return null;
          const errMsg = err.message || 'Unknown';
          addToLog('error', `Segment ${index + 1} attempt ${attempt + 1} failed`, errMsg);
          if (attempt < 2) await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        }
      }
      addToLog('error', `Segment ${index + 1} failed after 3 attempts`, 'Skipping...');
      return null;
    };
    
    // Safe play function with mutex to prevent race conditions
    const safePlay = async (url) => {
      if (isPlayingRef.current || audioSessionRef.current !== sessionId) {
        console.warn('[TTS] safePlay BLOCKED — isPlayingRef=', isPlayingRef.current, 'session match=', audioSessionRef.current === sessionId);
        return false;
      }
      isPlayingRef.current = true;
      
      try {
        audio.src = url;
        audio.playbackRate = audioSpeed;
        audio.load();
        await audio.play();
        // This chapter is now audible — no longer a silent pending auto-advance
        pendingAutoAdvanceRef.current = null;
        console.log('[TTS] safePlay OK chunk', currentAudioIndexRef.current + 1, 'src len=', url.length);
        return true;
      } catch (err) {
        // AbortError means play() was interrupted - this is expected during stop/pause
        if (err.name !== 'AbortError') {
          console.warn('[TTS] Play failed:', err.name, err.message);
        }
        isPlayingRef.current = false;
        return false;
      }
    };
    
    // Play next audio from queue
    const playNext = async () => {
      if (audioSessionRef.current !== sessionId) return;
      
      const idx = currentAudioIndexRef.current;
      
      // All chunks done — auto-advance to next chapter (by document order, not number+1)
      if (idx >= chunks.length) {
        isPlayingRef.current = false;
        toast.dismiss("audio-buffering");
        
        const segs = getChapterSegments(project.content || '');
        const curSegIdx = segs.findIndex(s => s.number === chapterToPlay);
        const nextSeg = curSegIdx !== -1 ? segs[curSegIdx + 1] : null;
        if (nextSeg) {
          const next = nextSeg.number;
          setAudioChapter(next);
          setIsAudioPlaying(false);
          setIsAudioLoading(false);
          toast.info(`Chapter ${chapterToPlay} done. Playing Chapter ${next}...`, { duration: 2000 });
          addToLog('audio', `Auto-advancing to Chapter ${next}`);
          // Mark as pending until it actually produces sound; guard the timer
          // against being superseded by a user action (skip/stop/jump)
          pendingAutoAdvanceRef.current = { from: chapterToPlay, to: next };
          const advSession = audioSessionRef.current;
          setTimeout(() => {
            if (audioSessionRef.current !== advSession) return;
            handlePlayContent(next);
          }, 800);
        } else {
          setIsAudioPlaying(false);
          setIsAudioLoading(false);
          toast.success("Finished reading all chapters!", { duration: 3000 });
          addToLog('audio', 'All chapters complete');
        }
        return;
      }
      
      // Chunk ready in queue
      if (idx < audioQueueRef.current.length) {
        const url = audioQueueRef.current[idx];
        if (!url) {
          // Failed chunk — skip
          currentAudioIndexRef.current++;
          isPlayingRef.current = false;
          setTimeout(playNext, 50);
          return;
        }
        
        toast.dismiss("audio-buffering");
        addToLog('audio', `Playing ${idx + 1}/${chunks.length}`);
        
        const played = await safePlay(url);
        if (!played && audioSessionRef.current === sessionId) {
          // Play failed, skip to next
          currentAudioIndexRef.current++;
          isPlayingRef.current = false;
          setTimeout(playNext, 100);
        }
        return;
      }
      
      // Still generating - wait
      if (isGeneratingAudioRef.current) {
        toast.loading("Buffering...", { id: "audio-buffering", duration: 90000 });
        setTimeout(playNext, 800);
        return;
      }
      
      // Generation done but segment missing - skip
      currentAudioIndexRef.current++;
      isPlayingRef.current = false;
      setTimeout(playNext, 50);
    };
    
    // Audio ended - advance to next
    audio.onended = () => {
      if (audioSessionRef.current !== sessionId) return;
      console.log('[TTS] onended chunk', currentAudioIndexRef.current + 1, '/', chunks.length, 'queue.len=', audioQueueRef.current.length, 'generating=', isGeneratingAudioRef.current);
      toast.dismiss("audio-buffering");
      
      const idx = currentAudioIndexRef.current;
      if (audioQueueRef.current[idx]) {
        try { URL.revokeObjectURL(audioQueueRef.current[idx]); } catch(e) {}
        audioQueueRef.current[idx] = null;
      }
      
      currentAudioIndexRef.current++;
      setAudioProgress(prev => ({ ...prev, segment: currentAudioIndexRef.current, currentTime: 0, duration: 0 }));
      isPlayingRef.current = false;
      playNext();
    };
    
    // Audio error - skip to next
    audio.onerror = (e) => {
      if (audioSessionRef.current !== sessionId) return;
      console.warn('[TTS] onerror chunk', currentAudioIndexRef.current + 1, 'code=', audio.error?.code, 'msg=', audio.error?.message);
      currentAudioIndexRef.current++;
      isPlayingRef.current = false;
      setTimeout(playNext, 100);
    };
    
    try {
      // Pre-generate the first two chunks SEQUENTIALLY. Parallel TTS calls while
      // the server is busy (e.g. full-book generation) overloaded it → 60s timeouts
      // and HTTP 520s. Sequential keeps the pre-buffer benefit at half the peak load.
      const firstUrl = await generateChunk(0);
      const secondUrl = (chunks.length > 1 && audioSessionRef.current === sessionId)
        ? await generateChunk(1)
        : null;
      
      if (!firstUrl || audioSessionRef.current !== sessionId) {
        isGeneratingAudioRef.current = false;
        isPlayingRef.current = false;
        toast.dismiss("audio-loading");
        toast.error("Failed to generate audio. Please try again.");
        setIsAudioPlaying(false);
        setIsAudioLoading(false);
        return;
      }
      
      audioQueueRef.current.push(firstUrl);
      if (chunks.length > 1) {
        audioQueueRef.current.push(secondUrl); // may be null; playNext handles it
      }
      setIsAudioLoading(false);
      toast.dismiss("audio-loading");
      toast.success("Now playing!", { duration: 2000 });
      playNext();
      
      // Generate remaining chunks in background (start from chunk 2)
      (async () => {
        for (let i = 2; i < chunks.length; i++) {
          if (audioSessionRef.current !== sessionId) break;
          const url = await generateChunk(i);
          if (audioSessionRef.current !== sessionId) break;
          audioQueueRef.current.push(url);
        }
        if (audioSessionRef.current === sessionId) {
          isGeneratingAudioRef.current = false;
          const ok = audioQueueRef.current.filter(x => x).length;
          addToLog('audio', `Generation done`, `${ok}/${chunks.length} successful`);
        }
      })();
      
    } catch (error) {
      toast.dismiss("audio-loading");
      toast.error("Audio generation failed");
      setIsAudioPlaying(false);
      setIsAudioLoading(false);
      isGeneratingAudioRef.current = false;
      isPlayingRef.current = false;
    }
  };
  
  // TRUE PAUSE: keeps audio URLs alive, just pauses playback
  const handlePauseAudio = () => {
    // Pause AI Voice (HTML Audio element)
    if (audioRef.current && isAudioPlaying && !useQuickPlay) {
      audioRef.current.pause();
      isPlayingRef.current = false; // Release mutex
      setIsAudioPaused(true);
      setIsAudioPlaying(false);
      setCanResumeAudio(true);
      setAudioLastPosition({ 
        chapter: audioChapter, 
        chunkIndex: currentAudioIndexRef.current,
        totalChunks: audioQueueRef.current.length
      });
      addToLog('audio', `Paused AI Voice at segment ${currentAudioIndexRef.current + 1}/${audioQueueRef.current.length}`, 'Click Resume to continue');
      toast.info(`Paused Chapter ${audioChapter}. Click Resume to continue.`, { duration: 3000 });
      return;
    }
    
    // Pause Quick Play (browser speech synthesis)
    if (window.speechSynthesis && (window.speechSynthesis.speaking || window.speechSynthesis.pending)) {
      window.speechSynthesis.pause();
      setIsAudioPaused(true);
      setIsAudioPlaying(false);
      setCanResumeAudio(true);
      const chunkInfo = `chunk ${quickPlayChunkRef.current + 1}/${quickPlayChunksRef.current.length}`;
      addToLog('audio', `Paused Quick Play at ${chunkInfo}`, 'Click Resume to continue');
      toast.info(`Paused Chapter ${audioChapter}. Click Resume to continue.`, { duration: 3000 });
    }
  };

  // FULL STOP: destroys all audio, cancels in-flight requests, resets state
  const handleStopAudio = () => {
    toast.dismiss("audio-loading");
    toast.dismiss("audio-buffering");
    
    // Cancel generation and invalidate session
    isGeneratingAudioRef.current = false;
    isPlayingRef.current = false; // Release mutex
    audioSessionRef.current++;
    if (audioAbortControllerRef.current) {
      audioAbortControllerRef.current.abort();
      audioAbortControllerRef.current = null;
    }
    setIsAudioPaused(false);
    
    // Stop browser speech and reset Quick Play state
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    quickPlayChunkRef.current = 0;
    quickPlayChunksRef.current = [];
    
    // Stop audio element
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.src = '';
    }
    
    // Clean up blob URLs
    audioQueueRef.current.forEach(url => {
      try { if (url) URL.revokeObjectURL(url); } catch (e) {}
    });
    audioQueueRef.current = [];
    currentAudioIndexRef.current = 0;
    
    setIsAudioPlaying(false);
    setIsAudioLoading(false);
    setCanResumeAudio(false);
  };

  // Resume audio - use true resume if audio is just paused, else re-generate from position
  const handleResumeAudio = () => {
    if (!canResumeAudio) {
      toast.error("Nothing to resume");
      return;
    }
    
    // TRUE RESUME for Quick Play: browser speech was paused
    if (isAudioPaused && useQuickPlay) {
      // First try native speechSynthesis.resume()
      if (window.speechSynthesis && window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
        setIsAudioPaused(false);
        setIsAudioPlaying(true);
        setCanResumeAudio(false);
        addToLog('audio', 'Resuming Quick Play from paused position');
        return;
      }
      
      // Fallback: resume from the tracked chunk position
      resumeQuickPlayFromChunk();
      return;
    }
    
    // TRUE RESUME for AI Voice: audio element was paused (URLs still alive)
    if (isAudioPaused && audioRef.current && audioRef.current.src) {
      setIsAudioPaused(false);
      setIsAudioPlaying(true);
      setCanResumeAudio(false);
      isPlayingRef.current = true; // Set mutex before play
      addToLog('audio', `Resuming AI Voice Chapter ${audioChapter}`, 'Continuing from paused position');
      
      audioRef.current.play()
        .then(() => {
          // Successfully resumed
        })
        .catch(err => {
          isPlayingRef.current = false; // Release mutex on error
          // Only log if not a normal interruption
          if (err.name !== 'AbortError') {
            console.error("Resume play error:", err);
            addToLog('error', 'Resume failed', err.message || 'Unknown');
          }
          // Fallback: re-generate from saved chapter
          setIsAudioPaused(false);
          setIsAudioPlaying(false);
          const chapterToResume = audioLastPosition?.chapter || audioChapter;
          setTimeout(() => {
            handlePlayContent(chapterToResume);
          }, 100);
        });
      return;
    }
    
    // Browser speech resume (Quick Play was paused via speechSynthesis.pause())
    if (isAudioPaused && window.speechSynthesis && window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsAudioPaused(false);
      setIsAudioPlaying(true);
      setCanResumeAudio(false);
      addToLog('audio', 'Resuming browser voice playback');
      return;
    }
    
    // FALLBACK: re-generate from saved chapter position
    const chapterToResume = audioLastPosition?.chapter || audioChapter;
    setAudioChapter(chapterToResume);
    setCanResumeAudio(false);
    setIsAudioPaused(false);
    addToLog('audio', `Restarting Chapter ${chapterToResume}`, 'Re-generating audio');
    
    setTimeout(() => {
      if (useQuickPlay) {
        handleQuickPlay(chapterToResume);
      } else {
        handlePlayContent(chapterToResume);
      }
    }, 100);
  };

  // Skip to next chapter (by document order — chapter numbers can have gaps)
  const handleNextChapter = () => {
    // If auto-advance silently queued a chapter the user hasn't HEARD yet,
    // "next" means "play that chapter now" — not skip past it
    const pending = pendingAutoAdvanceRef.current;
    pendingAutoAdvanceRef.current = null;
    handleStopAudio();
    
    let nextChapter;
    if (pending && pending.to === audioChapter) {
      nextChapter = pending.to;
    } else {
      const segs = getChapterSegments(project.content || '');
      nextChapter = audioChapter + 1;
      if (segs.length > 0) {
        const curSegIdx = segs.findIndex(s => s.number === audioChapter);
        const nextSeg = curSegIdx !== -1 ? segs[curSegIdx + 1] : segs.find(s => s.number > audioChapter);
        if (!nextSeg) {
          toast.info("Already at the last chapter");
          return;
        }
        nextChapter = nextSeg.number;
      }
    }
    
    setAudioChapter(nextChapter);
    setSelectedChapterNum(nextChapter);
    scrollToChapter(nextChapter);
    addToLog('audio', `Skipping to Chapter ${nextChapter}`);
    
    const navSession = audioSessionRef.current;
    setTimeout(() => {
      if (audioSessionRef.current !== navSession) return;
      if (useQuickPlay) {
        handleQuickPlay(nextChapter);
      } else {
        handlePlayContent(nextChapter);
      }
    }, 300);
  };

  // Jump directly to any chapter and start reading it
  const handleJumpToChapter = (chapterNum) => {
    if (!chapterNum) return;
    pendingAutoAdvanceRef.current = null;
    handleStopAudio();
    setAudioChapter(chapterNum);
    setSelectedChapterNum(chapterNum);
    scrollToChapter(chapterNum);
    addToLog('audio', `Jumping to Chapter ${chapterNum}`);
    
    const navSession = audioSessionRef.current;
    setTimeout(() => {
      if (audioSessionRef.current !== navSession) return;
      if (useQuickPlay) {
        handleQuickPlay(chapterNum);
      } else {
        handlePlayContent(chapterNum);
      }
    }, 300);
  };

  // Skip to previous chapter
  const handlePrevChapter = () => {
    // If auto-advance silently moved us forward, "previous" = replay the chapter just heard
    const pending = pendingAutoAdvanceRef.current;
    pendingAutoAdvanceRef.current = null;
    handleStopAudio();
    
    let prevChapter;
    if (pending && pending.to === audioChapter) {
      prevChapter = pending.from;
    } else {
      const segs = getChapterSegments(project.content || '');
      prevChapter = audioChapter - 1;
      if (segs.length > 0) {
        const curSegIdx = segs.findIndex(s => s.number === audioChapter);
        const prevSeg = curSegIdx > 0 ? segs[curSegIdx - 1] : (curSegIdx === -1 ? [...segs].reverse().find(s => s.number < audioChapter) : null);
        if (!prevSeg) {
          toast.info("Already at the first chapter");
          return;
        }
        prevChapter = prevSeg.number;
      } else if (audioChapter <= 1) {
        toast.info("Already at the first chapter");
        return;
      }
    }
    
    setAudioChapter(prevChapter);
    setSelectedChapterNum(prevChapter);
    scrollToChapter(prevChapter);
    addToLog('audio', `Going back to Chapter ${prevChapter}`);
    
    setTimeout(() => {
      if (useQuickPlay) {
        handleQuickPlay(prevChapter);
      } else {
        handlePlayContent(prevChapter);
      }
    }, 300);
  };

  // Rewind by one paragraph/segment within current chapter
  const handleRewindParagraph = () => {
    const currentIndex = currentAudioIndexRef.current;
    const queueLength = audioQueueRef.current.length;
    
    // For Quick Play (browser TTS)
    if (useQuickPlay && quickPlayChunksRef.current.length > 0) {
      const currentChunk = quickPlayChunkRef.current;
      if (currentChunk <= 0) {
        toast.info("Already at the beginning");
        return;
      }
      
      // Stop current speech and restart from previous chunk
      window.speechSynthesis.cancel();
      const newChunk = Math.max(0, currentChunk - 1);
      quickPlayChunkRef.current = newChunk;
      
      // Resume from the new position
      const chunks = quickPlayChunksRef.current;
      const resumeQuickPlayFrom = (startIndex) => {
        let idx = startIndex;
        const playNext = () => {
          if (idx >= chunks.length) {
            setIsAudioPlaying(false);
            return;
          }
          quickPlayChunkRef.current = idx;
          const utterance = new SpeechSynthesisUtterance(chunks[idx]);
          utterance.rate = audioSpeed;
          utterance.onend = () => { idx++; playNext(); };
          window.speechSynthesis.speak(utterance);
        };
        playNext();
      };
      
      setIsAudioPlaying(true);
      resumeQuickPlayFrom(newChunk);
      toast.info(`Rewound to segment ${newChunk + 1}/${chunks.length}`, { duration: 2000 });
      return;
    }
    
    // For AI Voice (audio queue)
    if (queueLength > 0) {
      if (currentIndex <= 0) {
        toast.info("Already at the beginning");
        return;
      }
      
      const newIndex = Math.max(0, currentIndex - 1);
      currentAudioIndexRef.current = newIndex;
      
      // Play from the new index
      if (audioRef.current && audioQueueRef.current[newIndex]) {
        audioRef.current.src = audioQueueRef.current[newIndex];
        audioRef.current.playbackRate = audioSpeed;
        audioRef.current.play().catch(e => console.error("Rewind play error:", e));
        setIsAudioPlaying(true);
        scrollToAudioSegment(newIndex);
        toast.info(`Rewound to segment ${newIndex + 1}/${queueLength}`, { duration: 2000 });
      }
    } else {
      toast.info("No audio playing to rewind");
    }
  };

  // Forward by one paragraph/segment within current chapter
  const handleForwardParagraph = () => {
    const currentIndex = currentAudioIndexRef.current;
    const queueLength = audioQueueRef.current.length;
    
    // For Quick Play (browser TTS)
    if (useQuickPlay && quickPlayChunksRef.current.length > 0) {
      const currentChunk = quickPlayChunkRef.current;
      const chunks = quickPlayChunksRef.current;
      
      if (currentChunk >= chunks.length - 1) {
        toast.info("Already at the end");
        return;
      }
      
      // Stop current speech and restart from next chunk
      window.speechSynthesis.cancel();
      const newChunk = Math.min(chunks.length - 1, currentChunk + 1);
      quickPlayChunkRef.current = newChunk;
      
      // Resume from the new position
      const resumeQuickPlayFrom = (startIndex) => {
        let idx = startIndex;
        const playNext = () => {
          if (idx >= chunks.length) {
            setIsAudioPlaying(false);
            return;
          }
          quickPlayChunkRef.current = idx;
          const utterance = new SpeechSynthesisUtterance(chunks[idx]);
          utterance.rate = audioSpeed;
          utterance.onend = () => { idx++; playNext(); };
          window.speechSynthesis.speak(utterance);
        };
        playNext();
      };
      
      setIsAudioPlaying(true);
      resumeQuickPlayFrom(newChunk);
      toast.info(`Skipped to segment ${newChunk + 1}/${chunks.length}`, { duration: 2000 });
      return;
    }
    
    // For AI Voice (audio queue)
    if (queueLength > 0) {
      if (currentIndex >= queueLength - 1) {
        toast.info("Already at the end");
        return;
      }
      
      const newIndex = Math.min(queueLength - 1, currentIndex + 1);
      currentAudioIndexRef.current = newIndex;
      
      // Play from the new index
      if (audioRef.current && audioQueueRef.current[newIndex]) {
        audioRef.current.src = audioQueueRef.current[newIndex];
        audioRef.current.playbackRate = audioSpeed;
        audioRef.current.play().catch(e => console.error("Forward play error:", e));
        setIsAudioPlaying(true);
        scrollToAudioSegment(newIndex);
        toast.info(`Skipped to segment ${newIndex + 1}/${queueLength}`, { duration: 2000 });
      }
    } else {
      toast.info("No audio playing to skip");
    }
  };

  // Writing Enhancement Tools functions
  const handleRewrite = async (type) => {
    if (!selectedText || selectedText.trim().length < 10) {
      toast.error("Please select at least 10 characters of text to rewrite");
      return;
    }
    
    setRewriteLoading(true);
    setRewriteResult(null);
    
    const endpoint = type === 'show_dont_tell' ? '/rewrite/show-dont-tell' 
                   : type === 'sensory' ? '/rewrite/sensory-expansion'
                   : type === 'humanize' ? '/rewrite/humanize'
                   : '/rewrite/polish';
    
    try {
      const res = await axios.post(`${API}${endpoint}`, {
        text: selectedText,
        project_id: id
      });
      setRewriteResult(res.data);
      toast.success("Text rewritten successfully!");
    } catch (error) {
      toast.error("Failed to rewrite text");
      console.error(error);
    } finally {
      setRewriteLoading(false);
    }
  };
  
  const applyRewrite = () => {
    if (!rewriteResult?.rewritten || !selectedText) return;
    
    const newContent = project.content.replace(selectedText, rewriteResult.rewritten);
    setProject(prev => ({ ...prev, content: newContent }));
    
    // Save to backend
    axios.put(`${API}/project/${id}`, { content: newContent })
      .then(() => {
        toast.success("Rewrite applied to your story!");
        setRewriteResult(null);
        setSelectedText('');
        setShowWritingTools(false);
      })
      .catch(() => toast.error("Failed to save changes"));
  };

  // Story Doctor functions
  const handleScanForIssues = async () => {
    // Story Doctor analysis is available on all tiers (including free)
    
    if (!project.content) {
      toast.error("No content to scan");
      return;
    }
    setScanning(true);
    setIssues([]);
    addToLog('scan', 'Scanning story for issues...');
    try {
      const res = await axios.post(`${API}/story-doctor/scan`, {
        project_id: id,
        text: project.content
      });
      setIssues(res.data.issues);
      if (res.data.issues.length === 0) {
        addToLog('scan', 'No issues found - story looks great!');
        toast.success("No issues found! Your story looks great!");
      } else {
        const issuesSummary = res.data.issues.map(i => `• ${i.type}: ${i.description}`).join('\n');
        addToLog('scan', `Found ${res.data.issues.length} issue(s)`, issuesSummary);
        toast.info(`Found ${res.data.issues.length} issue(s) to review`);
      }
      if (res.data.error) {
        addToLog('error', 'Scan had issues', res.data.error);
      }
    } catch (error) {
      addToLog('error', 'Scan failed', error.message);
      toast.error("Failed to scan for issues");
    } finally {
      setScanning(false);
    }
  };

  // Fixed issues history to show what changed
  const [fixedHistory, setFixedHistory] = useState([]);

  const handleFixIssue = async (issue, index) => {
    setFixingIssue(index);
    addToLog('fix', `Fixing: ${issue.type} - ${issue.description}`);
    try {
      const res = await axios.post(`${API}/story-doctor/fix`, {
        project_id: id,
        issue_type: issue.type,
        issue_description: issue.description,
        problematic_text: issue.location,
        suggested_fix: issue.suggestion,
        full_content: project.content
      });
      
      if (res.data.error) {
        addToLog('error', `Could not fix: ${issue.description}`, res.data.message || 'AI unavailable - try manual fix');
        toast.error("Could not auto-fix - see log for details");
      } else {
        setProject(prev => ({
          ...prev,
          content: res.data.fixed_content,
          word_count: res.data.word_count
        }));
        // Add to fixed history with change summary
        setFixedHistory(prev => [...prev, {
          issue: issue.description,
          type: issue.type,
          original: res.data.original_text,
          summary: res.data.change_summary,
          timestamp: new Date().toLocaleTimeString()
        }]);
        addToLog('fix', `FIXED: ${issue.type}`, res.data.change_summary);
        // Remove the fixed issue from the list
        setIssues(prev => prev.filter((_, i) => i !== index));
        toast.success("Issue fixed! See log below.");
      }
    } catch (error) {
      addToLog('error', `Fix failed: ${issue.description}`, error.message);
      toast.error("Failed to fix issue");
    } finally {
      setFixingIssue(null);
    }
  };

  const handleFixAllIssues = async () => {
    for (let i = 0; i < issues.length; i++) {
      await handleFixIssue(issues[0], 0);
    }
  };

  // Fix a recommendation from character analysis
  const handleFixRecommendation = async (recommendation) => {
    setFixingIssue('recommendation');
    addToLog('fix', `Applying recommendation: ${recommendation.substring(0, 50)}...`);
    try {
      const res = await axios.post(`${API}/story-doctor/fix`, {
        project_id: id,
        issue_type: 'recommendation',
        issue_description: recommendation,
        problematic_text: '',
        suggested_fix: recommendation,
        full_content: project.content
      });
      
      if (res.data.error) {
        addToLog('error', `Could not apply: ${recommendation.substring(0, 30)}...`, res.data.message || 'AI unavailable');
        toast.error("Could not apply recommendation");
      } else {
        setProject(prev => ({
          ...prev,
          content: res.data.fixed_content,
          word_count: res.data.word_count
        }));
        addToLog('fix', `Applied: ${recommendation.substring(0, 50)}...`, res.data.change_summary);
        toast.success("Recommendation applied!");
      }
    } catch (error) {
      addToLog('error', `Fix failed: ${recommendation.substring(0, 30)}...`, error.message);
      toast.error("Failed to apply recommendation");
    } finally {
      setFixingIssue(null);
    }
  };

  const handleDeepFix = async () => {
    if (!project.content) {
      toast.error("No content to fix");
      return;
    }
    setDeepFixing(true);
    try {
      const res = await axios.post(`${API}/story-doctor/deep-fix`, {
        project_id: id,
        text: project.content
      });
      
      // Check if story is too long
      if (res.data.error === "story_too_long") {
        toast.info(`Your story has ${res.data.word_count.toLocaleString()} words. Use "Scan for Issues" to fix section by section.`);
        setDeepFixing(false);
        return;
      }
      
      setProject(prev => ({
        ...prev,
        content: res.data.fixed_content,
        word_count: res.data.word_count
      }));
      setIssues([]);
      toast.success("Deep revision complete! Your story has been thoroughly improved.");
    } catch (error) {
      toast.error("Deep fix failed. Your story may be too long - try 'Scan for Issues' instead.");
    } finally {
      setDeepFixing(false);
    }
  };

  // Calculate word count for UI
  const wordCount = project?.content?.split(/\s+/).filter(Boolean).length || 0;
  const isLongStory = wordCount > 8000;

  // Smart Story Doctor functions
  const handleSmartAnalysis = async () => {
    if (!project.content) {
      toast.error("No content to analyze");
      return;
    }
    setAnalyzingMode('smart');
    setSmartAnalysis(null);
    setDoctorTab('overview');
    setAnalysisJobId(null);
    addToLog('analyze', 'Starting comprehensive story analysis...');
    
    try {
      // Create analysis job
      const jobResponse = await axios.post(`${API}/analysis-jobs`, {
        project_id: id,
        analysis_type: 'comprehensive'
      });
      
      const jobId = jobResponse.data.job_id;
      setAnalysisJobId(jobId);
      addToLog('analyze', `Analysis job started: ${jobId.slice(0, 8)}...`);
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 60; // 60 * 3 seconds = 3 minutes max
      
      while (attempts < maxAttempts) {
        attempts++;
        await new Promise(r => setTimeout(r, 3000));
        
        try {
          const statusRes = await axios.get(`${API}/analysis-jobs/${jobId}/status`);
          const { status, current_chapter, total_chapters, progress_percent, result, error_message } = statusRes.data;
          
          if (status.startsWith('ANALYZING_CHAPTER')) {
            addToLog('analyze', `Analyzing chapter ${current_chapter}/${total_chapters} (${progress_percent}%)`);
          }
          
          if (status === 'COMPLETE' && result) {
            setSmartAnalysis(result);
            addToLog('analyze', `Analysis complete: ${result.overall_score}/10 overall score, ${result.all_issues?.length || 0} issues found`);
            toast.success(`Analysis complete! Score: ${result.overall_score}/10, ${result.all_issues?.length || 0} issues found`);
            return;
          }
          
          if (status === 'FAILED') {
            throw new Error(error_message || 'Analysis failed');
          }
        } catch (pollError) {
          if (pollError.code === 'ECONNABORTED') continue;
          throw pollError;
        }
      }
      
      throw new Error('Analysis timed out');
    } catch (error) {
      addToLog('error', 'Smart analysis failed', error.message);
      toast.error("Analysis failed - please try again");
    } finally {
      setAnalyzingMode('');
    }
  };

  const handleCharacterAnalysis = async () => {
    if (!project.content) {
      toast.error("No content to analyze");
      return;
    }
    setAnalyzingMode('characters');
    addToLog('analyze', 'Analyzing character consistency...');
    
    try {
      const res = await axios.post(
        `${API}/story-doctor/character-consistency?project_id=${id}`,
        {},
        { timeout: 120000 } // 2 minute timeout
      );
      if (res.data.error) {
        throw new Error(res.data.error);
      }
      setSmartAnalysis(prev => ({
        ...prev,
        characterAnalysis: res.data
      }));
      setDoctorTab('characters');
      addToLog('analyze', `Character analysis complete: ${res.data.characters?.length || 0} characters analyzed`);
      toast.success("Character analysis complete!");
    } catch (error) {
      const errorMsg = error.code === 'ECONNABORTED' || error.message?.includes('timeout') 
        ? 'Analysis timed out - try again on a faster connection'
        : (error.response?.data?.error || error.message);
      addToLog('error', 'Character analysis failed', errorMsg);
      toast.error(errorMsg);
    } finally {
      setAnalyzingMode('');
    }
  };

  const handlePreviewFix = async (issue) => {
    setPreviewFix({ loading: true, issue });
    try {
      const res = await axios.post(`${API}/story-doctor/preview-fix?project_id=${id}&issue_location=${encodeURIComponent(issue.location)}&issue_suggestion=${encodeURIComponent(issue.suggestion)}&issue_type=${issue.type}`);
      setPreviewFix({ ...res.data, issue, loading: false });
    } catch (error) {
      toast.error("Could not generate preview");
      setPreviewFix(null);
    }
  };

  const handleApplyPreviewedFix = async () => {
    if (!previewFix?.before || !previewFix?.after) return;
    setApplyingFixIdx(-1); // Use -1 to indicate preview fix in progress
    try {
      const res = await axios.post(`${API}/story-doctor/apply-fix?project_id=${id}&before_text=${encodeURIComponent(previewFix.before)}&after_text=${encodeURIComponent(previewFix.after)}`);
      if (res.data.success) {
        setProject(prev => ({ ...prev, content: prev.content.replace(previewFix.before, previewFix.after), word_count: res.data.word_count }));
        toast.success("Fix applied successfully!");
        // Remove the fixed issue from smart analysis
        if (smartAnalysis) {
          setSmartAnalysis(prev => ({
            ...prev,
            all_issues: prev.all_issues.filter(i => i.location !== previewFix.issue.location)
          }));
        }
        setPreviewFix(null);
      } else {
        toast.error(res.data.error || "Could not apply fix");
      }
    } catch (error) {
      toast.error("Failed to apply fix");
    } finally {
      setApplyingFixIdx(null);
    }
  };

  // Apply all fixes from comprehensive analysis
  const handleApplyAllAnalysisFixes = async () => {
    if (!analysisJobId || !smartAnalysis?.all_issues?.length) {
      toast.error("No analysis results to apply fixes from");
      return;
    }
    
    setApplyingAllFixes(true);
    addToLog('fix', 'Applying all fixes from analysis...');
    
    try {
      const res = await axios.post(`${API}/analysis-jobs/${analysisJobId}/apply-fixes`, {
        fix_indices: [] // Empty means apply all
      });
      
      if (res.data.fixes_applied > 0) {
        // Refresh project to get updated content
        const projectRes = await axios.get(`${API}/project/${id}`);
        setProject(projectRes.data);
        
        // Update smart analysis to remove fixed issues
        const fixedIndices = res.data.fixes_details?.map(f => f.issue_idx) || [];
        setSmartAnalysis(prev => ({
          ...prev,
          all_issues: prev.all_issues.filter((_, idx) => !fixedIndices.includes(idx))
        }));
        
        addToLog('fix', `Applied ${res.data.fixes_applied} fixes, ${res.data.fixes_failed || 0} failed`);
        toast.success(`Applied ${res.data.fixes_applied} fixes! ${res.data.fixes_failed || 0} could not be applied.`);
        
        if (res.data.fixes_failed > 0) {
          addToLog('fix', `Failed fixes: ${res.data.failed_details?.map(f => f.reason).join(', ')}`);
        }
      } else {
        toast.info("No fixes could be applied - text may have changed");
        addToLog('fix', 'No fixes applied', res.data.failed_details?.map(f => f.reason).join(', '));
      }
    } catch (error) {
      addToLog('error', 'Failed to apply fixes', error.message);
      toast.error("Failed to apply fixes");
    } finally {
      setApplyingAllFixes(false);
    }
  };

  // Fix a single issue from smart analysis
  const handleFixSingleIssue = async (issueIdx) => {
    if (!analysisJobId) {
      toast.error("No analysis job found - run analysis first");
      return;
    }
    
    setApplyingFixIdx(issueIdx); // Track which specific issue is being fixed
    addToLog('fix', `Fixing issue #${issueIdx + 1}...`);
    
    try {
      const res = await axios.post(`${API}/analysis-jobs/${analysisJobId}/fix-single?issue_idx=${issueIdx}`);
      
      if (res.data.success) {
        // Refresh project
        const projectRes = await axios.get(`${API}/project/${id}`);
        setProject(projectRes.data);
        
        // Remove fixed issue from list
        setSmartAnalysis(prev => ({
          ...prev,
          all_issues: prev.all_issues.filter((_, idx) => idx !== issueIdx)
        }));
        
        // Remove from selected issues if it was selected
        setSelectedIssues(prev => {
          const newSet = new Set(prev);
          newSet.delete(issueIdx);
          return newSet;
        });
        
        addToLog('fix', `Fixed ${res.data.issue_type} issue in Ch ${res.data.chapter}`);
        toast.success(`Fixed! Before: "${res.data.before?.slice(0, 50)}..." → After: "${res.data.after?.slice(0, 50)}..."`);
      } else {
        addToLog('error', 'Fix failed', res.data.error);
        toast.error(res.data.error || "Could not apply fix");
      }
    } catch (error) {
      addToLog('error', 'Fix failed', error.message);
      toast.error("Failed to apply fix");
    } finally {
      setApplyingFixIdx(null); // Clear the fixing state
    }
  };

  // Fix only selected issues (multi-select)
  const handleFixSelectedIssues = async () => {
    if (selectedIssues.size === 0) {
      toast.error("No issues selected");
      return;
    }
    
    setApplyingAllFixes(true);
    const indicesToFix = Array.from(selectedIssues).sort((a, b) => b - a); // Fix from highest index first to avoid shifting
    let fixedCount = 0;
    
    for (const idx of indicesToFix) {
      try {
        const res = await axios.post(`${API}/analysis-jobs/${analysisJobId}/fix-single?issue_idx=${idx}`);
        if (res.data.success) {
          fixedCount++;
        }
      } catch (e) {
        console.error(`Failed to fix issue ${idx}:`, e);
      }
    }
    
    // Refresh project and update issues list
    const projectRes = await axios.get(`${API}/project/${id}`);
    setProject(projectRes.data);
    
    // Remove all fixed issues
    setSmartAnalysis(prev => ({
      ...prev,
      all_issues: prev.all_issues.filter((_, idx) => !selectedIssues.has(idx))
    }));
    
    setSelectedIssues(new Set());
    setApplyingAllFixes(false);
    
    toast.success(`Fixed ${fixedCount} of ${indicesToFix.length} selected issues!`);
  };

  // Helper to get score color
  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100';
    if (score >= 6) return 'text-cyan-400 bg-slate-800/60';
    if (score >= 4) return 'text-cyan-400 bg-cyan-900/20';
    return 'text-red-600 bg-red-100';
  };

  // ===== STORY BIBLE FUNCTIONS =====
  const loadStoryBible = async () => {
    setLoadingBible(true);
    try {
      const res = await axios.get(`${API}/story-bible/${id}`);
      setStoryBible(res.data);
    } catch (error) {
      console.error('Failed to load story bible:', error);
      // Initialize default story bible
      setStoryBible({
        protagonist: project.protagonist || null,
        pov_character: project.protagonist?.name || null,
        active_characters: project.characters || [],
        exited_characters: [],
        open_plot_threads: [],
        resolved_plot_threads: [],
        foreshadowing_seeds: [],
        planned_reveals: [],
        genre_tone_lock: `${project.genre} - ${project.tone || 'balanced'}`,
        world_rules: [],
        timeline: [],
        exclusions: []
      });
    } finally {
      setLoadingBible(false);
    }
  };

  const updateStoryBible = async (updates) => {
    try {
      const res = await axios.post(`${API}/story-bible/update`, {
        project_id: id,
        updates
      });
      setStoryBible(res.data.story_bible);
      toast.success("Story Bible updated!");
    } catch (error) {
      toast.error("Failed to update Story Bible");
    }
  };

  // ===== CHAPTER PLANNING FUNCTIONS =====
  const generateChapterPlan = async (chapterNumber, userGuidance = '', exclusions = []) => {
    setPlanningChapter(chapterNumber);
    setChapterPlan(null);
    addToLog('plan', `Planning Chapter ${chapterNumber}...`);
    
    try {
      // Use streaming endpoint to prevent mobile browser timeout
      const response = await fetch(`${API}/chapter/plan-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: id,
          chapter_number: chapterNumber,
          user_guidance: userGuidance,
          exclusions: exclusions
        })
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'status') {
                addToLog('plan', data.message);
              } else if (data.type === 'plan') {
                setChapterPlan(data.data);
                setShowChapterPlanning(true);
                addToLog('plan', `Chapter ${chapterNumber} plan ready for review`);
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (parseErr) {
              // Ignore JSON parse errors for partial data
              if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
            }
          }
        }
      }
    } catch (error) {
      const errorMsg = error?.message || 'Planning failed';
      addToLog('error', 'Chapter planning failed', errorMsg);
      toast.error(`Planning failed: ${errorMsg}`);
    } finally {
      setPlanningChapter(null);
    }
  };

  const approveAndGenerateChapter = async (modifications = '') => {
    if (!chapterPlan) return;
    
    const chapterNum = chapterPlan.chapter_number;
    const projectId = id;
    const startWordCount = project?.word_count || 0;
    
    setGeneratingChapter(chapterNum);
    setShowChapterPlanning(false);
    setChapterProgress({ part: 0, total: 3, status: 'generating', message: 'Starting job-based generation...' });
    addToLog('generate', `Starting Chapter ${chapterNum} generation (V3 beat-locked engine)...`);
    
    try {
      // Step 1: Create the chapter job using V3 beat-locked engine - this returns immediately with job_id
      addToLog('generate', 'Creating V3 generation job (beat-locked architecture)...');
      const jobResponse = await axios.post(`${API}/chapter-jobs-v3`, {
        project_id: projectId,
        chapter_number: chapterNum,
        approved_plan: chapterPlan,
        modifications
      });
      
      const jobId = jobResponse.data.job_id;
      addToLog('generate', `Job created: ${jobId.slice(0, 8)}... Server is processing.`);
      setChapterProgress({ part: 0, total: 3, status: 'generating', message: 'Job started...' });
      
      // Step 2: Poll the job status endpoint until complete or failed
      let attempts = 0;
      const maxAttempts = 120; // 120 * 3 seconds = 6 minutes max
      let lastStatus = '';
      let consecutiveFailures = 0;
      let lastWordCount = 0;
      
      while (attempts < maxAttempts) {
        attempts++;
        await new Promise(r => setTimeout(r, 3000)); // Poll every 3 seconds
        
        try {
          // Add timeout to prevent hanging on slow production servers
          const statusRes = await axios.get(`${API}/chapter-jobs/${jobId}/status`, { timeout: 10000 });
          const { status, current_part, word_count, error_message } = statusRes.data;
          
          // Reset consecutive failures on success
          consecutiveFailures = 0;
          
          // Only log when status changes to avoid spam
          if (status !== lastStatus) {
            addToLog('generate', `Status: ${status} (Part ${current_part}/3, ${word_count} words)`);
            lastStatus = status;
          }
          
          // Update progress UI
          setChapterProgress({ 
            part: current_part, 
            total: 3, 
            status: 'generating', 
            message: `${status.replace(/_/g, ' ')} (${word_count} words)` 
          });
          
          // Check terminal states
          if (status === 'COMPLETE') {
            // Job finished successfully - refresh project to get final content
            const finalRes = await axios.get(`${API}/project/${projectId}`);
            setProject(finalRes.data);
            
            const wordsAdded = (finalRes.data.word_count || 0) - startWordCount;
            addToLog('sync', `Chapter complete! ${wordsAdded} words generated.`);
            toast.success(`Chapter ${chapterNum} generated with ${wordsAdded} words!`);
            setChapterProgress({ part: 3, total: 3, status: 'done', message: 'Complete!' });
            setChapterPlan(null);
            localStorage.removeItem(`backup_ch_${projectId}_${chapterNum}`);
            
            // Track writing streak
            try {
              const today = new Date().toDateString();
              const lastWrite = localStorage.getItem('shweebum_last_write_date');
              let streak = parseInt(localStorage.getItem('shweebum_write_streak') || '0');
              
              if (lastWrite !== today) {
                const yesterday = new Date(Date.now() - 86400000).toDateString();
                if (lastWrite === yesterday) {
                  streak += 1;
                  if (streak === 3) toast.success("🔥 3 day writing streak! You're on fire!");
                  if (streak === 7) toast.success("🔥🔥 7 day streak! You're unstoppable!");
                  if (streak === 30) toast.success("🔥🔥🔥 30 DAYS! You're a writing legend!");
                } else {
                  streak = 1;
                }
                localStorage.setItem('shweebum_last_write_date', today);
                localStorage.setItem('shweebum_write_streak', String(streak));
              }
              
              // Milestone celebrations
              const totalWords = finalRes.data.word_count || 0;
              if (totalWords >= 10000 && totalWords - wordsAdded < 10000) {
                toast.success("🎉 Milestone: 10,000 words! Your story is taking shape!");
              } else if (totalWords >= 25000 && totalWords - wordsAdded < 25000) {
                toast.success("🎉 Milestone: 25,000 words! You've written a novella!");
              } else if (totalWords >= 50000 && totalWords - wordsAdded < 50000) {
                toast.success("🎉 Milestone: 50,000 words! That's a full novel!");
              }
            } catch (e) { /* streak tracking error - non-critical */ }
            
            // Fetch updated chapter summaries for butterfly effect
            try {
              const summariesRes = await axios.get(`${API}/chapter-summaries/${projectId}`);
              if (summariesRes.data?.summaries) {
                setChapterSummaries(summariesRes.data.summaries);
                addToLog('summary', 'Chapter summary saved for story continuity');
              }
            } catch (e) {}
            
            return; // Success!
          }
          
          if (status === 'FAILED') {
            addToLog('error', `Generation failed: ${error_message || 'Unknown error'}`);
            toast.error(`Generation failed: ${error_message || 'Unknown error'}`);
            setChapterProgress({ part: current_part, total: 3, status: 'error', message: error_message || 'Failed' });
            return;
          }
          
          // Check if we have partial content saved (for PART_X_COMPLETE statuses)
          if (status.includes('PART_') && status.includes('COMPLETE')) {
            // Refresh project to show partial progress
            try {
              const partialRes = await axios.get(`${API}/project/${projectId}`);
              if (partialRes.data.word_count > (project?.word_count || 0)) {
                setProject(partialRes.data);
              }
            } catch (e) {}
          }
          
        } catch (pollErr) {
          consecutiveFailures++;
          // Only log every 5th failure to reduce noise
          if (consecutiveFailures % 5 === 1) {
            addToLog('sync', `Status check failed (attempt ${attempts}), server may be busy...`);
          }
          
          // After 10 consecutive failures, try to check project directly
          if (consecutiveFailures >= 10 && consecutiveFailures % 10 === 0) {
            try {
              addToLog('sync', 'Checking project content directly...');
              const directRes = await axios.get(`${API}/project/${projectId}`, { timeout: 60000 });
              const currentWords = (directRes.data.word_count || 0) - startWordCount;
              if (currentWords >= 1500) {
                // Content is there! Mark as success
                setProject(directRes.data);
                addToLog('recover', `Chapter content found! ${currentWords} words generated.`);
                toast.success(`Chapter ${chapterNum} generated with ${currentWords} words!`);
                setChapterProgress({ part: 3, total: 3, status: 'done', message: 'Complete!' });
                setChapterPlan(null);
                localStorage.removeItem(`backup_ch_${projectId}_${chapterNum}`);
                return; // Success via direct check!
              } else if (currentWords > lastWordCount) {
                // Progress is being made, update and continue
                setProject(directRes.data);
                lastWordCount = currentWords;
                const estimatedPart = currentWords >= 1200 ? 3 : currentWords >= 600 ? 2 : 1;
                addToLog('sync', `Progress detected: ${currentWords} words (Part ${estimatedPart}/3)`);
                setChapterProgress({ 
                  part: estimatedPart, 
                  total: 3, 
                  status: 'generating', 
                  message: `${currentWords} words generated` 
                });
              }
            } catch (directErr) {
              // Direct check also failed, continue polling
            }
          }
        }
      }
      
      // Timeout reached - check final state
      addToLog('error', 'Generation timed out after 6 minutes');
      
      // Try to get whatever content was saved
      const finalRes = await axios.get(`${API}/project/${projectId}`);
      setProject(finalRes.data);
      const wordsRecovered = (finalRes.data.word_count || 0) - startWordCount;
      
      if (wordsRecovered >= 1500) {
        addToLog('recover', `Recovered complete chapter (${wordsRecovered} words)`);
        toast.success(`Chapter recovered with ${wordsRecovered} words!`);
        setChapterProgress({ part: 3, total: 3, status: 'done', message: '' });
        setChapterPlan(null);
      } else if (wordsRecovered >= 600) {
        const partsComplete = wordsRecovered >= 1200 ? 2 : 1;
        addToLog('recover', `Partial content recovered: ${wordsRecovered} words (${partsComplete}/3 parts)`);
        toast.warning(`Only ${partsComplete}/3 parts generated. Try again.`);
        setChapterProgress({ part: partsComplete, total: 3, status: 'error', message: 'Timeout' });
      } else {
        toast.error('Generation timed out with insufficient content');
        setChapterProgress({ part: 0, total: 3, status: 'error', message: 'Timeout' });
      }
      
    } catch (error) {
      const errorMsg = String(error?.response?.data?.detail || error?.message || 'Generation failed');
      addToLog('error', 'Generation error', errorMsg);
      toast.error(errorMsg);
      
      // Try to recover any partial content
      try {
        const res = await axios.get(`${API}/project/${projectId}`);
        const recovered = (res.data?.word_count || 0) - startWordCount;
        setProject(res.data);
        
        if (recovered >= 1500) {
          addToLog('recover', `Recovered complete chapter (${recovered} words)`);
          toast.success(`Chapter recovered with ${recovered} words!`);
          setChapterProgress({ part: 3, total: 3, status: 'done', message: '' });
          setChapterPlan(null);
          return;
        } else if (recovered >= 600) {
          const partsRecovered = recovered >= 1200 ? 2 : 1;
          addToLog('recover', `Partial content saved (${recovered} words, ${partsRecovered}/3 parts)`);
          toast.warning(`Only ${partsRecovered}/3 parts generated. Try generating again.`);
        }
      } catch (e) {}
      
      setChapterProgress({ part: 0, total: 3, status: 'error', message: errorMsg });
    } finally {
      setGeneratingChapter(null);
      setTimeout(() => setChapterProgress({ part: 0, total: 3, status: 'idle', message: '' }), 3000);
    }
  };

  // ===== ASK BEFORE ADDING FUNCTIONS =====
  const handleProposalResponse = async (approved, details = null) => {
    if (!pendingProposal) return;
    
    try {
      await axios.post(`${API}/story/approve-element?project_id=${id}&element_type=${pendingProposal.proposal_type}&element_name=${encodeURIComponent(pendingProposal.element_name)}&approved=${approved}`, details);
      
      if (approved) {
        toast.success(`Added: ${pendingProposal.element_name}`);
        // Reload story bible
        loadStoryBible();
      } else {
        toast.info(`Rejected and excluded: ${pendingProposal.element_name}`);
      }
    } catch (error) {
      toast.error("Failed to process response");
    } finally {
      setPendingProposal(null);
    }
  };

  // ===== FORESHADOWING FUNCTIONS =====
  const [showForeshadowingModal, setShowForeshadowingModal] = useState(false);
  const [planningForeshadow, setPlanningForeshadow] = useState(false);
  const [newReveal, setNewReveal] = useState({ description: '', target_chapter: '', importance: 'major', hint_style: 'subtle' });

  // ===== TENSION TRACKER STATE =====
  const [showTensionModal, setShowTensionModal] = useState(false);
  const [tensionCurve, setTensionCurve] = useState(null);
  const [loadingTension, setLoadingTension] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('classic_hero');

  // ===== CHAPTER SUMMARIES STATE =====

  const tensionTemplates = {
    classic_hero: { name: "Classic Hero's Journey", description: "Traditional rising action to climax" },
    thriller: { name: "Thriller/Suspense", description: "High tension throughout with peaks" },
    mystery: { name: "Mystery/Detective", description: "Building intrigue with revelation climax" },
    tragedy: { name: "Tragedy", description: "Rising hope leading to devastating fall" },
    romance: { name: "Romance", description: "Emotional ups and downs to happy ending" }
  };

  const tensionLevelColors = {
    0: "#d1fae5", 1: "#22c55e", 2: "#84cc16", 3: "#eab308", 4: "#f97316",
    5: "#ef4444", 6: "#dc2626", 7: "#b91c1c", 8: "#22c55e", 9: "#059669", 10: "#7c3aed"
  };

  const tensionLevelNames = {
    0: "None", 1: "Calm", 2: "Intrigue", 3: "Unease", 4: "Danger",
    5: "Crisis", 6: "Despair", 7: "Climax", 8: "Resolution", 9: "Aftermath", 10: "Transcendence"
  };

  const loadTensionCurve = async () => {
    setLoadingTension(true);
    try {
      const res = await axios.get(`${API}/tension/get-curve/${id}`);
      setTensionCurve(res.data.tension_curve);
    } catch (error) {
      console.error('Failed to load tension curve:', error);
    } finally {
      setLoadingTension(false);
    }
  };

  const createTensionCurve = async (template) => {
    setLoadingTension(true);
    try {
      const res = await axios.post(`${API}/tension/create-curve`, {
        project_id: id,
        template: template
      });
      if (res.data.success) {
        setTensionCurve(res.data.tension_curve);
        toast.success(`Applied "${tensionTemplates[template]?.name}" tension curve!`);
        // Reload story bible to show tension
        loadStoryBible();
      }
    } catch (error) {
      toast.error("Failed to create tension curve");
    } finally {
      setLoadingTension(false);
    }
  };

  const planForeshadowing = async () => {
    if (!newReveal.description || !newReveal.target_chapter) {
      toast.error("Please fill in the reveal description and target chapter");
      return;
    }
    
    setPlanningForeshadow(true);
    addToLog('foreshadow', `Planning foreshadowing for reveal in Chapter ${newReveal.target_chapter}...`);
    
    try {
      const res = await axios.post(`${API}/foreshadowing/plan-reveal?project_id=${id}`, {
        description: newReveal.description,
        target_chapter: parseInt(newReveal.target_chapter),
        importance: newReveal.importance,
        hint_style: newReveal.hint_style
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        addToLog('foreshadow', `Created ${res.data.hint_chapters.length} foreshadowing hints`);
        // Reload story bible to show new planned reveals
        loadStoryBible();
        setNewReveal({ description: '', target_chapter: '', importance: 'major', hint_style: 'subtle' });
        setShowForeshadowingModal(false);
      }
    } catch (error) {
      addToLog('error', 'Foreshadowing planning failed', error.message);
      toast.error("Failed to plan foreshadowing");
    } finally {
      setPlanningForeshadow(false);
    }
  };

  const severityColors = {
    high: "bg-red-100 text-red-800 border-red-200",
    medium: "bg-slate-800/60 text-cyan-200 border-cyan-500/25",
    low: "bg-blue-100 text-blue-800 border-blue-200"
  };

  const issueTypeIcons = {
    name: "👤",
    grammar: "📝",
    plot: "🕳️",
    continuity: "🔗",
    story_beat: "💫"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <ShweebumCat size="lg" speaking />
          <p className="mt-4 text-muted-foreground">Loading your book...</p>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className={`min-h-screen overflow-x-hidden transition-colors duration-300 ${focusMode && kindleMode ? 'kindle-page-mode bg-[#F4ECD8]' : 'bg-background'}`}>
      {/* Header */}
      <header className={`border-b sticky top-0 z-50 transition-colors duration-300 ${focusMode && kindleMode ? 'bg-[#F4ECD8]/95 border-amber-200 backdrop-blur-md' : 'bg-card border-border'}`}>
        <div className="px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            {/* Left: Logo + Title + Stats */}
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <Link to="/dashboard" className="flex-shrink-0" data-testid="back-to-dashboard">
                <ShweebumCat size="sm" />
              </Link>
              <div className="min-w-0 flex-1">
                <h1 className="font-literary text-sm sm:text-lg text-foreground truncate max-w-[140px] sm:max-w-none" data-testid="project-title">{project.title}</h1>
                <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-muted-foreground">
                  <span className="whitespace-nowrap">{project.word_count || 0} w</span>
                  <button
                    onClick={togglePrivacy}
                    className={`flex items-center gap-0.5 px-1 py-0.5 rounded-full font-medium transition-all cursor-pointer ${
                      privacyEnabled
                        ? 'bg-emerald-900/50 text-emerald-400'
                        : 'bg-slate-800/50 text-slate-400'
                    }`}
                    data-testid="privacy-toggle"
                  >
                    {privacyEnabled ? <ShieldCheck className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> : <Shield className="w-2.5 h-2.5 sm:w-3 sm:h-3" />}
                  </button>
                  {project.content && (() => {
                    const chapterMatches = project.content.match(/CHAPTER\s+(\d+):/gi);
                    if (chapterMatches && chapterMatches.length > 0) {
                      const chapterNums = chapterMatches.map(m => parseInt(m.match(/\d+/)[0]));
                      const currentChapter = Math.max(...chapterNums);
                      const totalChapters = project.chapter_outline?.length || currentChapter;
                      return (
                        <span className="flex items-center gap-0.5 px-1 py-0.5 bg-slate-800/60 text-cyan-200 rounded-full font-medium whitespace-nowrap" data-testid="chapter-counter">
                          <BookOpen className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          {currentChapter}/{totalChapters}
                        </span>
                      );
                    }
                    return null;
                  })()}
                  {/* Fidelity Badge (P0 #3) — only shown if a fidelity_report exists on this project */}
                  {project.story_bible?.fidelity_report?.overall_fidelity_score > 0 && (() => {
                    const score = project.story_bible.fidelity_report.overall_fidelity_score;
                    const hasIssues = project.story_bible.fidelity_report.issues_found;
                    const color = score >= 95 ? 'bg-emerald-900/50 text-emerald-300'
                      : score >= 80 ? 'bg-amber-900/50 text-amber-300'
                      : 'bg-rose-900/50 text-rose-300';
                    return (
                      <button
                        type="button"
                        onClick={() => setShowFidelityReport(true)}
                        className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap transition-all hover:scale-105 ${color}`}
                        data-testid="fidelity-badge"
                        title={hasIssues ? "Click to see fidelity report" : "All canon honored"}
                      >
                        {hasIssues ? '⚠' : '✓'} {score}%
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
            
            {/* Right: Action buttons - Dropdown on mobile, inline on desktop */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              {/* Mobile: Menu dropdown */}
              <div className="sm:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" data-testid="mobile-menu-btn">
                      <Menu className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem onClick={() => setShowImport(true)} data-testid="mobile-import-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Import
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowExport(true)} data-testid="mobile-export-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => {
                        localStorage.removeItem('shweebum_user');
                        localStorage.removeItem('shweebum_token');
                        sessionStorage.clear();
                        window.location.replace('/');
                      }}
                      className="text-red-400"
                      data-testid="mobile-logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {/* Desktop: Inline buttons */}
              <Button variant="outline" size="sm" onClick={() => setShowImport(true)} className="hidden sm:flex h-8 px-3 text-xs" data-testid="import-btn">
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                Import
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowExport(true)} className="hidden sm:flex h-8 px-3 text-xs" data-testid="export-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Export
              </Button>
              
              {/* Save button - always visible */}
              <Button size="sm" onClick={handleSave} disabled={isSaving} className="h-8 px-2 sm:px-3 text-xs bg-cyan-700 hover:bg-cyan-800" data-testid="save-btn">
                {isSaving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <><Save className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Save</span></>
                )}
              </Button>
              
              {lastSaveTime && (
                <span className="text-[10px] text-slate-500 hidden lg:inline whitespace-nowrap">
                  Saved {lastSaveTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              
              {/* Desktop logout */}
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem('shweebum_user');
                  localStorage.removeItem('shweebum_token');
                  sessionStorage.clear();
                  window.location.replace('/');
                }}
                className="hidden sm:flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
                data-testid="logout-writing-btn"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Writing Toolbar - Quick access to most-used tools */}
      <WritingToolbar
        onQuickFix={() => setShowNewQuickFix(true)}
        onSmartRefine={() => setShowQuickFix(true)}
        onStoryDoctor={() => setShowFindFix(true)}
        onStoryBible={() => { loadStoryBible(); setShowStoryBible(true); }}
        onChapterEditor={() => {
          const chapterCount = project?.chapter_outline?.length || (project?.content?.match(/---\s*CHAPTER\s+\d+/gi) || []).length;
          if (chapterCount > 0) {
            openChapterEditor(1);
          } else {
            toast.error("No chapters found");
          }
        }}
        hasChapters={(project?.content?.length || 0) > 50}
        onStoryNavigator={() => { setShowStoryNavigator(true); if(project?.content) handleAnalyzeStoryPosition(); }}
        onStoryArchitect={storyArchitectEnabled ? () => setShowStoryArchitect(true) : undefined}
        onAudioPlay={() => handlePlayContent(audioChapter)}
        onAudioPause={handlePauseAudio}
        isAudioPlaying={isAudioPlaying}
        isAudioLoading={isAudioLoading}
        canResumeAudio={canResumeAudio}
        onResumeAudio={handleResumeAudio}
        currentAudioChapter={selectedChapterNum || audioChapter}
        totalChapters={project?.chapter_outline?.length || (project?.content?.match(/---\s*CHAPTER\s+\d+/gi) || []).length}
        onPrevChapter={() => {
          const total = project?.chapter_outline?.length || (project?.content?.match(/---\s*CHAPTER\s+\d+/gi) || []).length;
          if (total > 0) {
            const newCh = Math.max(1, (selectedChapterNum || 1) - 1);
            setSelectedChapterNum(newCh);
            setAudioChapter(newCh);
            scrollToChapter(newCh);
          }
        }}
        onNextChapter={() => {
          const total = project?.chapter_outline?.length || (project?.content?.match(/---\s*CHAPTER\s+\d+/gi) || []).length;
          if (total > 0) {
            const newCh = Math.min(total, (selectedChapterNum || 1) + 1);
            setSelectedChapterNum(newCh);
            setAudioChapter(newCh);
            scrollToChapter(newCh);
          }
        }}
        onRewindParagraph={handleRewindParagraph}
        onForwardParagraph={handleForwardParagraph}
        onWritingTools={() => {
          const selection = window.getSelection()?.toString() || '';
          setSelectedText(selection);
          setRewriteResult(null);
          setShowWritingTools(true);
        }}
        onAnalysisDashboard={() => setShowAnalysisDashboard(true)}
        onStoryDirection={() => handleGetSuggestions("direction")}
        onAnalyze={handleAnalyze}
        analyzing={analyzing}
        onAIDetection={() => setShowAIDetection(true)}
        onSynopsisLadder={() => setShowSynopsisLadder(true)}
        onCanonLock={() => setShowCanonLock(true)}
        onCharacterPortraits={() => setShowCharacterPortraits(true)}
        onContactSupport={() => setShowContactSupport(true)}
        onFocusMode={() => setFocusMode(f => !f)}
        focusModeActive={focusMode}
        onFocusModeKindle={() => setKindleMode(k => !k)}
        kindleModeActive={kindleMode}
        onFinalPolish={() => {
          const isOwner = user?.email?.toLowerCase() === 'therealbeastlygamer@gmail.com';
          const hasAccess = isOwner || ['plus', 'premium', 'admin', 'author', 'storyteller', 'family'].includes(user?.subscription);
          if (hasAccess) {
            setShowFinalEditor(true);
          } else {
            setUpgradeFeature('Final Polish (Pro)');
            setShowUpgradeModal(true);
          }
        }}
        disabled={generatingChapter !== null || planningChapter !== null}
      />

      <main className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
        {/* PROMINENT: Generate Entire Book Banner - Only show when there's an outline */}
        {project?.chapter_outline?.length > 0 && !generatingFullBook && (
          <div className="mb-4 p-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-xl border-2 border-purple-500/50 shadow-lg shadow-purple-500/10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-600 rounded-full">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Generate Your Entire Book</h3>
                  <p className="text-purple-300 text-sm">
                    {project.chapter_outline.length} chapters ready • ~{project.chapter_outline.length * 1}-{project.chapter_outline.length * 2} min
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                {/* Auto-Refine Intensity Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-purple-300 whitespace-nowrap">Polish:</span>
                  <select
                    value={project?.auto_refine_intensity || 'none'}
                    onChange={async (e) => {
                      try {
                        await axios.patch(`${API}/project/${id}`, { auto_refine_intensity: e.target.value });
                        setProject(prev => ({ ...prev, auto_refine_intensity: e.target.value }));
                        toast.success(`Auto-polish set to: ${e.target.value}`);
                      } catch (err) {
                        toast.error("Failed to update setting");
                      }
                    }}
                    className="bg-slate-800 border border-purple-500/50 text-white text-sm rounded-lg px-2 py-2 focus:ring-purple-500 focus:border-purple-500 min-w-[100px]"
                    data-testid="auto-refine-select"
                  >
                    <option value="none">Off</option>
                    <option value="light">Light</option>
                    <option value="standard">Standard</option>
                    <option value="pro">Pro</option>
                  </select>
                </div>
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-6 py-3 text-base font-semibold shadow-lg w-full sm:w-auto"
                  onClick={() => handleGenerateFullBook(1)}
                  disabled={generatingChapter !== null || planningChapter !== null}
                  data-testid="generate-full-book-hero-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate All Chapters
                </Button>
              </div>
            </div>
            {project?.auto_refine_intensity && project.auto_refine_intensity !== 'none' && (
              <p className="text-xs text-purple-400 mt-2 text-center sm:text-right">
                Chapters will be automatically refined after generation
              </p>
            )}
          </div>
        )}
        
        {/* Full Book Generation Progress Banner */}
        {generatingFullBook && (
          <div className="mb-4 p-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-xl border-2 border-purple-500/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {cancellingFullBook === 'idle' ? (
                  <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
                ) : (
                  <Square className="w-6 h-6 text-red-400" />
                )}
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {cancellingFullBook === 'idle' && 'Generating Your Book...'}
                    {cancellingFullBook === 'requested' && 'Cancellation Requested...'}
                    {cancellingFullBook === 'confirmed' && 'Stopping After Current Chapter...'}
                  </h3>
                  <p className={`text-sm ${cancellingFullBook !== 'idle' ? 'text-red-300' : 'text-purple-300'}`}>
                    {cancellingFullBook === 'idle' && `Chapter ${fullBookProgress.currentChapter} of ${fullBookProgress.totalChapters}`}
                    {cancellingFullBook === 'requested' && 'Waiting for current chapter to finish writing...'}
                    {cancellingFullBook === 'confirmed' && `${fullBookProgress.chaptersCompleted} chapters saved — stopping soon`}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                className={cancellingFullBook !== 'idle' 
                  ? "border-red-600 bg-red-900/40 text-red-300 cursor-not-allowed opacity-70"
                  : "border-red-500/50 text-red-400 hover:bg-red-900/30"
                }
                onClick={handleCancelFullBook}
                disabled={cancellingFullBook !== 'idle'}
                data-testid="cancel-full-book-hero-btn"
              >
                {cancellingFullBook === 'idle' && <><Square className="w-4 h-4 mr-2" />Cancel</>}
                {cancellingFullBook === 'requested' && <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cancelling...</>}
                {cancellingFullBook === 'confirmed' && <><Check className="w-4 h-4 mr-2" />Stopping...</>}
              </Button>
            </div>
            <div className="w-full bg-purple-950 rounded-full h-3 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  cancellingFullBook !== 'idle'
                    ? 'bg-gradient-to-r from-red-500 to-orange-500'
                    : 'bg-gradient-to-r from-purple-500 to-indigo-500'
                }`}
                style={{ width: `${(fullBookProgress.chaptersCompleted / fullBookProgress.totalChapters) * 100}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-sm text-purple-300">
              <span>{fullBookProgress.chaptersCompleted} chapters completed</span>
              {cancellingFullBook === 'idle' && fullBookProgress.estimatedTimeRemaining > 0 && (
                <span>~{Math.ceil(fullBookProgress.estimatedTimeRemaining / 60)} min remaining</span>
              )}
              {cancellingFullBook !== 'idle' && (
                <span className="text-red-400">Cancellation in progress</span>
              )}
            </div>
          </div>
        )}

        {/* Resume Book Generation Banner - Shows when generation was interrupted */}
        {!generatingFullBook && resumeInfo && resumeInfo.remainingChapters > 0 && (
          <div className="mb-4 p-4 bg-gradient-to-r from-slate-800/50 to-slate-700/50 rounded-xl border-2 border-cyan-500/30" data-testid="resume-book-banner">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-cyan-600/30 rounded-lg">
                <RefreshCw className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">Book Generation Paused</h3>
                <p className="text-cyan-200/80 text-sm">
                  Stopped at Chapter {resumeInfo.lastChapter} of {resumeInfo.totalChapters} 
                  {resumeInfo.isLastChapterIncomplete && (
                    <span className="text-cyan-400 ml-1">
                      (Chapter {resumeInfo.lastChapter} may be incomplete - only {resumeInfo.lastChapterWordCount} words)
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            {/* Progress bar showing completion */}
            <div className="w-full bg-slate-900/60 rounded-full h-2 mb-4">
              <div 
                className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full transition-all"
                style={{ width: `${(resumeInfo.lastChapter / resumeInfo.totalChapters) * 100}%` }}
              />
            </div>
            
            <div className="flex flex-wrap gap-3">
              {/* Continue from next chapter */}
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleGenerateFullBook(resumeInfo.lastChapter + 1)}
                disabled={generatingChapter !== null || planningChapter !== null}
                data-testid="resume-continue-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Continue from Chapter {resumeInfo.lastChapter + 1}
                <span className="ml-2 text-green-200">({resumeInfo.remainingChapters} left)</span>
              </Button>
              
              {/* Erase last chapter and regenerate */}
              {resumeInfo.isLastChapterIncomplete && (
                <Button
                  variant="outline"
                  className="border-cyan-500/30 text-cyan-300 hover:bg-slate-800/40"
                  onClick={() => handleEraseLastChapterAndRegenerate(true)}
                  disabled={generatingChapter !== null || planningChapter !== null}
                  data-testid="resume-erase-regen-btn"
                >
                  <RotateCw className="w-4 h-4 mr-2" />
                  Erase Ch {resumeInfo.lastChapter} & Regenerate
                </Button>
              )}
              
              {/* Just erase last chapter (for any chapter) */}
              <Button
                variant="outline"
                className="border-red-500/30 text-red-300/80 hover:bg-red-900/20"
                onClick={() => handleEraseLastChapterAndRegenerate(false)}
                disabled={generatingChapter !== null || planningChapter !== null}
                data-testid="resume-erase-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Erase Chapter {resumeInfo.lastChapter}
              </Button>
            </div>
          </div>
        )}

        {/* Mobile: Sidebar first, then writing area */}

        {/* Focus Mode: Kindle toggle pill */}
        {focusMode && (
          <div className="flex items-center justify-center mb-4 gap-3">
            <span className={`text-sm ${kindleMode ? 'text-amber-900' : 'text-slate-400'}`}>Focus Mode is on.</span>
            <button
              onClick={() => setKindleMode(k => !k)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                kindleMode 
                  ? 'bg-amber-100 border-amber-300 text-amber-800' 
                  : 'bg-slate-800 border-slate-600 text-slate-300 hover:border-amber-400'
              }`}
              data-testid="kindle-mode-toggle"
            >
              {kindleMode ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
              {kindleMode ? 'Kindle Mode On' : 'Switch to Kindle Mode'}
            </button>
            <button onClick={() => { setFocusMode(false); setKindleMode(false); }}
              className={`text-xs underline transition-colors ${kindleMode ? 'text-amber-800 hover:text-amber-950' : 'text-slate-500 hover:text-slate-300'}`}>
              Exit
            </button>
          </div>
        )}

        {/* Main layout — collapses to 1 col in Focus Mode */}
        <div className={`grid grid-cols-1 gap-4 lg:gap-6 transition-all duration-300 ${focusMode ? 'lg:grid-cols-1' : 'lg:grid-cols-3'}`}>
          {/* Mobile Chapter Outline Toggle */}
          {project?.chapter_outline?.length > 0 && (
            <div className="lg:hidden">
              <Card className="bg-slate-800/40 border-cyan-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-lg text-cyan-300 flex items-center gap-2">
                      <BookOpen className="w-5 h-5" />
                      Chapters ({project.chapter_outline.length})
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowCharacterEditor(true)}
                        className="h-9 text-xs bg-purple-600/20 border-purple-500/50"
                        data-testid="edit-characters-mobile-btn"
                      >
                        <Users className="w-3 h-3 mr-1" />
                        Characters
                      </Button>
                      <Button 
                        size="lg" 
                        variant="outline" 
                        className="border-cyan-500/30 h-10 px-4"
                        onClick={() => setShowChapterOutline(!showChapterOutline)}
                      >
                        {showChapterOutline ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                  </div>
                  {showChapterOutline && (
                    <div className="mt-4 space-y-3">
                      {/* Instructions for Mobile */}
                      {generatingChapter === null && (
                        <div className="p-2 bg-blue-900/30 rounded-lg border border-blue-500/50">
                          <p className="text-xs text-blue-300">
                            <strong>Tap "Generate"</strong> and wait for all 3 parts to complete before starting another chapter.
                          </p>
                        </div>
                      )}
                      
                      {/* Progress Bar for Mobile */}
                      {generatingChapter !== null && (
                        <div className="p-3 bg-slate-800/40 rounded-lg border-2 border-cyan-500/30">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-cyan-300">✨ Writing Ch {generatingChapter}</span>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-card">
                              {chapterProgress.status === 'done' ? '✅' : 
                               chapterProgress.status === 'syncing' ? '🔄' :
                               `${chapterProgress.part}/${chapterProgress.total}`}
                            </span>
                          </div>
                          <div className="w-full bg-card rounded-full h-3 overflow-hidden shadow-inner">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${
                                chapterProgress.status === 'error' ? 'bg-red-500' :
                                chapterProgress.status === 'done' ? 'bg-green-500' :
                                chapterProgress.status === 'syncing' ? 'bg-purple-500 animate-pulse' :
                                chapterProgress.part === 1 ? 'bg-blue-500' :
                                chapterProgress.part === 2 ? 'bg-cyan-500' :
                                'bg-green-500'
                              }`}
                              style={{ width: chapterProgress.status === 'syncing' ? '100%' : `${(chapterProgress.part / (chapterProgress.total || 3)) * 100}%` }}
                            />
                          </div>
                          <p className="text-xs text-cyan-300 mt-2 text-center font-medium">
                            {chapterProgress.status === 'done' ? '🎉 Complete! Generate another.' :
                             chapterProgress.status === 'error' ? '⚠️ Error - try again' :
                             chapterProgress.status === 'syncing' ? `🔄 ${chapterProgress.message || 'Syncing with server...'}` :
                             chapterProgress.part === 1 ? '📝 Opening scene...' :
                             chapterProgress.part >= (chapterProgress.total || 3) ? '🎬 Climax & ending...' :
                             chapterProgress.part >= 2 ? `📖 Part ${chapterProgress.part}/${chapterProgress.total}...` :
                             '⏳ Starting...'}
                          </p>
                          {chapterProgress.status !== 'done' && chapterProgress.status !== 'error' && (
                            <p className="text-xs text-cyan-400 mt-1 text-center animate-pulse">
                              {chapterProgress.status === 'syncing' ? 'Server is generating remaining parts...' : 'Please wait...'}
                            </p>
                          )}
                          {chapterProgress.status !== 'done' && chapterProgress.status !== 'error' && (
                            <button
                              onClick={handleCancelChapterGeneration}
                              data-testid="cancel-chapter-generation-btn"
                              className="mt-2 w-full text-xs py-1.5 rounded bg-red-900/50 hover:bg-red-800/70 text-red-300 hover:text-red-200 border border-red-500/30 hover:border-red-500/50 transition-colors"
                            >
                              Cancel Generation
                            </button>
                          )}
                        </div>
                      )}
                      {project.chapter_outline.map((chapter, idx) => {
                        const chapterWritten = project.content?.includes(`CHAPTER ${chapter.number}:`);
                        const totalChapters = project.chapter_outline.length;
                        const storyProgress = chapter.number / totalChapters;
                        const isFinalChapter = chapter.number === totalChapters;
                        const isWrapUpZone = storyProgress >= 0.75;
                        
                        return (
                          <div key={idx} className={`p-3 rounded-lg ${
                            chapterWritten ? 'bg-green-900/30 border border-green-500/50' : 
                            isFinalChapter ? 'bg-purple-900/30 border border-purple-500/50' :
                            isWrapUpZone ? 'bg-slate-800/40 border border-cyan-500/30' : 
                            'bg-card border'
                          }`}>
                            <div className="flex items-center justify-between gap-1">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {editingChapterTitle === `m-${idx}` ? (
                                  <input
                                    autoFocus
                                    className="text-base font-medium text-foreground bg-card border border-cyan-500/50 rounded px-1.5 py-0.5 w-full"
                                    value={chapter.title || ''}
                                    data-testid={`chapter-title-input-mobile-${idx}`}
                                    onChange={(e) => {
                                      const newOutline = [...project.chapter_outline];
                                      newOutline[idx] = { ...chapter, title: e.target.value, ...(chapterWritten ? { edited_after_write: true } : {}) };
                                      setProject(prev => ({ ...prev, chapter_outline: newOutline }));
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    onBlur={async () => {
                                      setEditingChapterTitle(null);
                                      try {
                                        await axios.put(`${API}/project/${id}`, { chapter_outline: project.chapter_outline });
                                        toast.success("Chapter title saved!", { duration: 1500 });
                                      } catch (err) {
                                        toast.error("Failed to save changes");
                                      }
                                    }}
                                  />
                                ) : (
                                  <span className="text-base font-medium truncate">Ch {chapter.number}: {chapter.title}</span>
                                )}
                                {!chapterWritten && isFinalChapter && (
                                  <span className="text-xs px-2 py-1 bg-purple-600 text-purple-100 rounded font-medium">FINALE</span>
                                )}
                                {!chapterWritten && isWrapUpZone && !isFinalChapter && (
                                  <span className="text-xs px-2 py-1 bg-cyan-900/30 text-cyan-300 rounded font-medium">Wrap-up</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  className="h-8 w-8 flex items-center justify-center rounded text-slate-400 hover:text-cyan-300 hover:bg-cyan-900/30 transition-colors"
                                  onClick={() => setEditingChapterTitle(`m-${idx}`)}
                                  title="Rewrite chapter title"
                                  data-testid={`edit-chapter-title-btn-mobile-${idx}`}
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                  className="h-8 w-8 flex items-center justify-center rounded text-slate-400 hover:text-red-400 hover:bg-red-900/30 transition-colors"
                                  onClick={() => handleDeleteChapterOutline(idx)}
                                  title="Delete this chapter"
                                  data-testid={`delete-chapter-btn-mobile-${idx}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              {chapterWritten ? (
                                chapter.edited_after_write ? (
                                  <Button
                                    size="lg"
                                    className="h-10 px-4 text-sm bg-amber-600 hover:bg-amber-500 text-white"
                                    onClick={() => handleRewriteChapter(chapter.number)}
                                    disabled={generatingChapter !== null || planningChapter !== null}
                                    title="Outline changed — delete this chapter's text and rewrite it in place"
                                    data-testid={`rewrite-chapter-btn-mobile-${idx}`}
                                  >
                                    {generatingChapter === chapter.number ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" /> Rewrite</>}
                                  </Button>
                                ) : (
                                  <Badge className="bg-green-600/30 text-green-300 border border-green-500/40 text-sm px-3 py-1">Done</Badge>
                                )
                              ) : (
                                <div className="flex gap-2">
                                  <Button
                                    size="lg"
                                    variant="outline"
                                    className="h-10 px-3 text-sm border-blue-300 text-blue-600"
                                    onClick={() => generateChapterPlan(chapter.number)}
                                    disabled={generatingChapter !== null || planningChapter !== null}
                                    title="Plan first, then generate"
                                  >
                                    {planningChapter === chapter.number ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Map className="w-4 h-4 mr-1" /> Plan</>}
                                  </Button>
                                  <Button
                                    size="lg"
                                    variant={isFreePlan ? "outline" : "gold"}
                                    className={`h-10 px-4 text-sm ${isFreePlan ? 'border-slate-600 text-slate-400' : ''}`}
                                    onClick={() => {
                                      if (isFreePlan) {
                                        toast.error("Chapter generation requires a paid plan. Upgrade to Shweebum Plus ($10/mo)!");
                                        return;
                                      }
                                      handleGenerateChapter(chapter.number);
                                    }}
                                    disabled={generatingChapter !== null || planningChapter !== null}
                                    title={isFreePlan ? "Upgrade to generate chapters" : "Generate directly"}
                                  >
                                    {generatingChapter === chapter.number ? <RefreshCw className="w-4 h-4 animate-spin" /> : 
                                      isFreePlan ? <><Lock className="w-3 h-3 mr-1" /> Go</> : "Go"
                                    }
                                  </Button>
                                </div>
                              )}
                              </div>
                            </div>
                            {/* Editable chapter summary - Mobile */}
                            {(
                              <div className="relative mt-2">
                                <textarea
                                  className="text-sm text-muted-foreground w-full bg-card border border-border hover:border-border focus:border-blue-400 focus:bg-card rounded p-2 pr-10 resize-y transition-colors"
                                  rows={6}
                                  value={chapter.summary || ''}
                                  placeholder="Write your detailed chapter plan here! Include specific scenes, dialogue, character actions. The more detail you add, the more closely the AI will follow your vision."
                                  onChange={(e) => {
                                    const newOutline = [...project.chapter_outline];
                                    newOutline[idx] = { ...chapter, summary: e.target.value, ...(chapterWritten ? { edited_after_write: true } : {}) };
                                    setProject(prev => ({ ...prev, chapter_outline: newOutline }));
                                  }}
                                  onBlur={async () => {
                                    try {
                                      await axios.put(`${API}/project/${id}`, {
                                        chapter_outline: project.chapter_outline
                                      });
                                      toast.success("Chapter plan saved!", { duration: 1500 });
                                    } catch (err) {
                                      toast.error("Failed to save changes");
                                    }
                                  }}
                                />
                                <SpeechToTextButton
                                  onTranscript={(text, append) => {
                                    const newOutline = [...project.chapter_outline];
                                    const currentSummary = chapter.summary || '';
                                    newOutline[idx] = { 
                                      ...chapter, 
                                      summary: append && currentSummary ? currentSummary + ' ' + text : text 
                                    };
                                    setProject(prev => ({ ...prev, chapter_outline: newOutline }));
                                  }}
                                  className="absolute right-2 top-2 h-7 w-7"
                                  size="icon"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Chapter Length picker (mobile panel) */}
                      {chapterLengthPicker}
                      {/* Add Chapter Outline (mobile panel) */}
                      <button
                        onClick={() => setShowAddChapter(true)}
                        data-testid="add-chapter-outline-btn-mobile"
                        className="w-full mt-2 py-2 rounded-lg border border-dashed border-cyan-500/50 text-cyan-300 hover:bg-cyan-900/30 hover:border-cyan-400 text-sm font-medium transition-colors"
                      >
                        + Add Chapter
                      </button>
                      {/* Erase Content Buttons on Mobile */}
                      
                      {/* Generate Full Book Button - Mobile */}
                      {!generatingFullBook ? (
                        <Button
                          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-sm"
                          onClick={() => handleGenerateFullBook(1)}
                          disabled={generatingChapter !== null || planningChapter !== null || (user?.subscription === 'free' || !user?.subscription)}
                        >
                          {(user?.subscription === 'free' || !user?.subscription) ? (
                            <><Lock className="w-4 h-4 mr-2" />Generate Entire Book</>
                          ) : (
                            <><Sparkles className="w-4 h-4 mr-2" />Generate Entire Book</>
                          )}
                        </Button>
                      ) : (
                        <div className="space-y-2 p-2 bg-purple-900/30 rounded-lg border border-purple-500/50">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-purple-300">
                              {cancellingFullBook === 'idle' && `Generating... Ch ${fullBookProgress.currentChapter}/${fullBookProgress.totalChapters}`}
                              {cancellingFullBook === 'requested' && 'Cancelling...'}
                              {cancellingFullBook === 'confirmed' && 'Stopping...'}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-5 text-xs ${cancellingFullBook !== 'idle' ? 'text-red-300 opacity-70 cursor-not-allowed' : 'text-red-400'}`}
                              onClick={handleCancelFullBook}
                              disabled={cancellingFullBook !== 'idle'}
                              data-testid="cancel-full-book-mobile-btn"
                            >
                              {cancellingFullBook === 'idle' ? 'Cancel' : cancellingFullBook === 'requested' ? 'Cancelling...' : 'Stopping...'}
                            </Button>
                          </div>
                          <div className="w-full bg-purple-950 rounded-full h-1.5">
                            <div 
                              className={`h-full rounded-full transition-all ${cancellingFullBook !== 'idle' ? 'bg-red-500' : 'bg-purple-500'}`}
                              style={{ width: `${(fullBookProgress.chaptersCompleted / fullBookProgress.totalChapters) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                      
                      {project?.content?.trim().length > 0 && (
                        <div className="space-y-1">
                          {project.content.includes('--- CHAPTER') && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full text-red-400 border-red-500/40 hover:bg-red-900/30 text-xs"
                              onClick={async () => {
                                if (!confirm('Erase the last chapter?')) return;
                                const content = project.content;
                                const lastChapterIndex = content.lastIndexOf('\n\n--- CHAPTER');
                                if (lastChapterIndex > 0) {
                                  const newContent = content.substring(0, lastChapterIndex);
                                  setProject(prev => ({ ...prev, content: newContent, word_count: newContent.split(' ').length }));
                                  try {
                                    await axios.put(`${API}/project/${id}`, { content: newContent, word_count: newContent.split(' ').length });
                                    toast.success('Last chapter erased');
                                  } catch (e) { toast.error('Failed to save'); }
                                } else {
                                  if (confirm('This is the only chapter. Clear ALL content?')) {
                                    setProject(prev => ({ ...prev, content: '', word_count: 0 }));
                                    try {
                                      await axios.put(`${API}/project/${id}`, { content: '', word_count: 0 });
                                      toast.success('All content cleared!');
                                    } catch (e) { toast.error('Failed to save'); }
                                  }
                                }
                              }}
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Erase Last Chapter
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-red-500 text-xs"
                            onClick={async () => {
                              if (!confirm('Clear ALL content and start fresh?')) return;
                              setProject(prev => ({ ...prev, content: '', word_count: 0 }));
                              try {
                                await axios.put(`${API}/project/${id}`, { content: '', word_count: 0 });
                                toast.success('All content cleared!');
                              } catch (e) { toast.error('Failed to save'); }
                            }}
                          >
                            Start Over
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Main Writing Area */}
          <div className={`order-2 lg:order-1 transition-all duration-300 ${focusMode ? 'lg:col-span-1 max-w-3xl mx-auto w-full' : 'lg:col-span-2'}`}>
            <Card className={`transition-all duration-300 ${kindleMode ? 'kindle-formatting bg-[#F4ECD8] border-amber-200' : 'bg-card book-page'}`}>
              {/* Formatting Toolbar */}
              <div className="formatting-toolbar">
                <select 
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  title="Font Family"
                >
                  <option value="Georgia, serif">Georgia</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                  <option value="'Palatino Linotype', serif">Palatino</option>
                  <option value="'Book Antiqua', serif">Book Antiqua</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Helvetica Neue', sans-serif">Helvetica</option>
                  <option value="Verdana, sans-serif">Verdana</option>
                  <option value="'Courier New', monospace">Courier New</option>
                </select>
                
                <select
                  value={fontSize}
                  onChange={(e) => setFontSize(e.target.value)}
                  title="Font Size"
                >
                  <option value="14px">14px</option>
                  <option value="16px">16px</option>
                  <option value="18px">18px</option>
                  <option value="20px">20px</option>
                  <option value="22px">22px</option>
                  <option value="24px">24px</option>
                </select>
                
                <div className="toolbar-divider" />
                
                <button
                  onClick={() => setTextAlign('left')}
                  className={textAlign === 'left' ? 'active' : ''}
                  title="Align Left"
                >
                  ←
                </button>
                <button
                  onClick={() => setTextAlign('center')}
                  className={textAlign === 'center' ? 'active' : ''}
                  title="Align Center"
                >
                  ↔
                </button>
                <button
                  onClick={() => setTextAlign('right')}
                  className={textAlign === 'right' ? 'active' : ''}
                  title="Align Right"
                >
                  →
                </button>
                <button
                  onClick={() => setTextAlign('justify')}
                  className={textAlign === 'justify' ? 'active' : ''}
                  title="Justify"
                >
                  ≡
                </button>
                
                <div className="toolbar-divider" />
                
                <select
                  value={lineHeight}
                  onChange={(e) => setLineHeight(e.target.value)}
                  title="Line Spacing"
                >
                  <option value="1.5">Single</option>
                  <option value="1.8">1.5 spacing</option>
                  <option value="2.0">Double</option>
                  <option value="2.5">2.5 spacing</option>
                </select>
                
                <div className="toolbar-divider" />
                
                <button
                  onClick={() => {
                    // Fix formatting - normalize whitespace
                    const fixed = project.content
                      .replace(/\n+/g, ' ')  // Replace newlines with spaces
                      .replace(/\s+/g, ' ')  // Normalize multiple spaces
                      .replace(/\.\s*/g, '.\n\n')  // Add paragraph breaks after periods
                      .replace(/\?\s*/g, '?\n\n')  // Add paragraph breaks after questions
                      .replace(/!\s*/g, '!\n\n')   // Add paragraph breaks after exclamations
                      .replace(/"\s*\n\n/g, '"\n\n')  // Keep quotes intact
                      .trim();
                    setProject({ ...project, content: fixed });
                    toast.success("Text formatting fixed!");
                  }}
                  title="Fix Formatting (joins words into proper paragraphs)"
                  className="bg-slate-800/60 border-cyan-500/30 text-cyan-200"
                >
                  Fix Format
                </button>
                
                <div className="toolbar-divider" />
                
                {/* Audio Reading Controls */}
                <button
                  onClick={() => setShowAudioPanel(!showAudioPanel)}
                  title="Audio Reader - Listen to your story"
                  className={`flex items-center gap-1 px-2 py-1 rounded ${showAudioPanel ? 'bg-purple-900/50 border border-purple-500/50 text-purple-300' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="audio-panel-toggle"
                >
                  <Headphones className="w-4 h-4" />
                  <span className="hidden sm:inline">Listen</span>
                </button>
                
                <span className="text-xs text-muted-foreground ml-auto">
                  {project.content?.split(/\s+/).filter(Boolean).length || 0} words
                </span>
              </div>
              
              {/* Audio Controls Panel */}
              {showAudioPanel && (
                <>
                <div className="p-3 bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border-b border-purple-500/30 flex flex-wrap items-center gap-3">
                  {/* Quick Play Toggle */}
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-purple-300 flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={useQuickPlay}
                        onChange={(e) => setUseQuickPlay(e.target.checked)}
                        disabled={isAudioPlaying}
                        className="rounded"
                      />
                      Quick Play
                    </label>
                    <span className="text-xs text-purple-400" title="Uses browser voice - instant but less natural">
                      (instant)
                    </span>
                  </div>
                  
                  {!useQuickPlay && (
                    <>
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-purple-300">Voice:</label>
                        <select
                          value={audioVoice}
                          onChange={(e) => setAudioVoice(e.target.value)}
                          className="text-xs rounded border-purple-500/50 bg-purple-900/50 text-purple-200 px-2 py-1"
                          disabled={isAudioPlaying || isAudioLoading}
                        >
                          <option value="aria">Aria (Expressive)</option>
                          <option value="jenny">Jenny (Warm)</option>
                          <option value="emma">Emma (Clear)</option>
                          <option value="ana">Ana (Young)</option>
                          <option value="sonia">Sonia (British)</option>
                          <option value="guy">Guy (Warm Male)</option>
                          <option value="andrew">Andrew (Calm)</option>
                          <option value="ryan">Ryan (British Male)</option>
                          <option value="brian">Brian (Deep)</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-purple-300">Speed:</label>
                    <select
                      value={audioSpeed}
                      onChange={(e) => {
                        const newSpeed = parseFloat(e.target.value);
                        setAudioSpeed(newSpeed);
                        if (audioRef.current) audioRef.current.playbackRate = newSpeed;
                      }}
                      className="text-xs rounded border-purple-500/50 bg-purple-900/50 text-purple-200 px-2 py-1"
                    >
                      <option value={0.7}>0.7x Slow</option>
                      <option value={0.85}>0.85x Relaxed</option>
                      <option value={1.0}>1x Normal</option>
                      <option value={1.25}>1.25x Brisk</option>
                      <option value={1.5}>1.5x Fast</option>
                      <option value={2.0}>2x Double</option>
                      <option value={3.0}>3x Triple</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-auto">
                    {/* -15s Seek Back */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (audioRef.current && audioRef.current.duration) {
                          audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15);
                        }
                      }}
                      disabled={!isAudioPlaying && !isAudioPaused}
                      className="border-purple-500/50 text-purple-300 hover:bg-purple-900/50 text-xs px-2"
                      title="Back 15 seconds"
                      data-testid="audio-seek-back"
                    >
                      -15s
                    </Button>
                    
                    {/* +15s Seek Forward */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (audioRef.current && audioRef.current.duration) {
                          audioRef.current.currentTime = Math.min(audioRef.current.duration, audioRef.current.currentTime + 15);
                        }
                      }}
                      disabled={!isAudioPlaying && !isAudioPaused}
                      className="border-purple-500/50 text-purple-300 hover:bg-purple-900/50 text-xs px-2"
                      title="Forward 15 seconds"
                      data-testid="audio-seek-forward"
                    >
                      +15s
                    </Button>
                    
                    <div className="w-px h-6 bg-purple-500/30 mx-0.5" />
                    
                    {/* Previous Chapter */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handlePrevChapter}
                      disabled={isAudioLoading || audioChapter <= 1}
                      className="border-purple-500/50 text-purple-300 hover:bg-purple-900/50"
                      title="Previous Chapter"
                      data-testid="audio-prev-chapter"
                    >
                      <SkipBack className="w-4 h-4" />
                    </Button>
                    
                    {/* Chapter picker — jump straight to any chapter */}
                    <select
                      value={audioChapter}
                      onChange={(e) => handleJumpToChapter(parseInt(e.target.value))}
                      disabled={isAudioLoading}
                      className="text-xs font-medium text-purple-300 bg-slate-800 border border-purple-500/50 rounded px-1.5 py-1 max-w-[170px] cursor-pointer hover:border-purple-400 focus:outline-none"
                      title="Jump to any chapter and start reading from there"
                      data-testid="audio-chapter-select"
                    >
                      {(() => {
                        const segs = getChapterSegments(project.content || '');
                        const opts = segs.map((s, i) => (
                          <option key={`${s.number}-${i}`} value={s.number}>
                            Ch {s.number}{s.title ? `: ${s.title.length > 26 ? s.title.slice(0, 26) + '…' : s.title}` : ''}
                          </option>
                        ));
                        if (!segs.some(s => s.number === audioChapter)) {
                          opts.unshift(<option key="current" value={audioChapter}>Ch {audioChapter}</option>);
                        }
                        return opts;
                      })()}
                    </select>
                    
                    {/* Next Chapter */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleNextChapter}
                      disabled={isAudioLoading}
                      className="border-purple-500/50 text-purple-300 hover:bg-purple-900/50"
                      title="Next Chapter"
                      data-testid="audio-next-chapter"
                    >
                      <SkipForward className="w-4 h-4" />
                    </Button>
                    
                    {/* Divider */}
                    <div className="w-px h-6 bg-purple-500/30 mx-1" />
                    
                    {/* Rewind Paragraph */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRewindParagraph}
                      disabled={isAudioLoading}
                      className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-900/50"
                      title="Rewind one paragraph"
                      data-testid="audio-rewind-paragraph"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    
                    {/* Forward Paragraph */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleForwardParagraph}
                      disabled={isAudioLoading}
                      className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-900/50"
                      title="Skip forward one paragraph"
                      data-testid="audio-forward-paragraph"
                    >
                      <RotateCw className="w-4 h-4" />
                    </Button>
                    
                    {/* Resume Button - shows when playback was paused */}
                    {canResumeAudio && !isAudioPlaying && (
                      <Button
                        size="sm"
                        onClick={handleResumeAudio}
                        className="bg-green-600 hover:bg-green-700 animate-pulse"
                        title={`Resume from where you left off`}
                        data-testid="audio-resume-btn"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Resume
                      </Button>
                    )}
                    
                    {/* Pause Button - only when playing */}
                    {isAudioPlaying && !isAudioLoading && (
                      <Button
                        size="sm"
                        onClick={handlePauseAudio}
                        className="bg-cyan-600 hover:bg-cyan-700"
                        title="Pause playback (keeps position)"
                        data-testid="audio-pause-btn"
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Pause
                      </Button>
                    )}
                    
                    {/* Main Play/Stop Button */}
                    <Button
                      size="sm"
                      onClick={isAudioPlaying ? handleStopAudio : (useQuickPlay ? handleQuickPlay : handlePlayContent)}
                      disabled={isAudioLoading || !project.content?.trim()}
                      className={`${isAudioPlaying ? 'bg-red-500 hover:bg-red-600' : useQuickPlay ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                      title={isAudioPlaying ? "Stop completely" : "Start from beginning of chapter"}
                      data-testid="audio-play-btn"
                    >
                      {isAudioLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Loading...
                        </>
                      ) : isAudioPlaying ? (
                        <>
                          <Square className="w-4 h-4 mr-1" />
                          Stop
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-1" />
                          {useQuickPlay ? 'Quick Play' : 'AI Voice'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Audio Progress Bar — seekable */}
                {(isAudioPlaying || isAudioPaused) && audioProgress.duration > 0 && (
                  <div className="px-3 py-2 bg-slate-800/60 border-b border-purple-500/20">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-purple-400 tabular-nums w-10 text-right">
                        {Math.floor(audioProgress.currentTime / 60)}:{String(Math.floor(audioProgress.currentTime % 60)).padStart(2, '0')}
                      </span>
                      <input
                        type="range"
                        min={0}
                        max={audioProgress.duration || 100}
                        value={audioProgress.currentTime || 0}
                        onChange={(e) => {
                          const time = parseFloat(e.target.value);
                          if (audioRef.current) audioRef.current.currentTime = time;
                        }}
                        className="flex-1 h-1.5 accent-purple-500 cursor-pointer"
                        style={{ background: `linear-gradient(to right, rgb(168 85 247) ${(audioProgress.currentTime / (audioProgress.duration || 1)) * 100}%, rgb(51 65 85) ${(audioProgress.currentTime / (audioProgress.duration || 1)) * 100}%)` }}
                        data-testid="audio-seek-slider"
                      />
                      <span className="text-xs text-purple-400 tabular-nums w-10">
                        {Math.floor(audioProgress.duration / 60)}:{String(Math.floor(audioProgress.duration % 60)).padStart(2, '0')}
                      </span>
                    </div>
                    {audioProgress.totalSegments > 1 && (
                      <p className="text-center text-xs text-slate-500 mt-0.5">
                        Segment {audioProgress.segment + 1} of {audioProgress.totalSegments}
                      </p>
                    )}
                  </div>
                )}
              </>
              )}
              
              <CardContent className="p-0">
                {/* Edit hint bar */}
                {project.content && (
                  <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800/60 border-b border-slate-700/50">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <PenTool className="w-3 h-3" />
                      You can edit the text directly — just click and type
                    </span>
                    <button 
                      onClick={() => setShowQuickFix(true)}
                      className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
                      data-testid="nova-edit-inline-btn"
                    >
                      <Zap className="w-3 h-3" />
                      AI Rewrite (Nova)
                    </button>
                  </div>
                )}
                <div className="relative">
                <textarea
                  ref={contentRef}
                  value={project.content || ""}
                  onScroll={() => {
                    // TTS Phase 1: detect manual user scroll during audio playback
                    // and pause auto-scroll for 5 seconds. Skip our own programmatic scrolls.
                    if (!isAudioPlayingRef.current) return;
                    if (programmaticScrollRef.current) return;
                    userScrolledRef.current = true;
                    if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current);
                    userScrollTimeoutRef.current = setTimeout(() => {
                      userScrolledRef.current = false;
                    }, 5000);
                  }}
                  onChange={(e) => {
                    const newContent = e.target.value;
                    setProject({ ...project, content: newContent, word_count: newContent.split(/\s+/).filter(Boolean).length });
                    // Wow Factor - typing pause detection
                    if (typingPauseTimerRef.current) clearTimeout(typingPauseTimerRef.current);
                    typingPauseTimerRef.current = setTimeout(() => {
                      if (!wowVisibleRef.current && newContent.length > 200) {
                        const words = newContent.toLowerCase().split(/\s+/);
                        const freq = {};
                        words.forEach(w => { const c = w.replace(/[^a-z]/g,''); if(c.length>3) freq[c]=(freq[c]||0)+1; });
                        const over = Object.entries(freq).filter(([,v])=>v>=6).sort((a,b)=>b[1]-a[1]);
                        const sentences = newContent.split(/[.!?]+/).filter(s=>s.trim().length>5);
                        const avgLen = sentences.length>0 ? Math.round(words.length/sentences.length) : 0;
                        const last = sentences[sentences.length-1]||'';
                        const pool = [];
                        if(over.length>0) pool.push(`"${over[0][0]}" appears ${over[0][1]} times — consider a synonym.`);
                        if(last.split(/\s+/).length>35) pool.push("That last sentence is quite long — consider splitting it.");
                        if(avgLen<10 && sentences.length>5) pool.push("Crisp, short sentences. Great for tension!");
                        if(avgLen>22) pool.push("Long, flowing prose — great for immersive scenes.");
                        pool.push("Keep going — the story is finding its shape.", "Nice flow in this section!", "You're building something real here.");
                        const msg = pool[Math.floor(Math.random()*pool.length)];
                        wowVisibleRef.current = true;
                        setWowMessage(msg);
                        setTimeout(()=>{ setWowMessage(null); wowVisibleRef.current=false; }, 4000);
                      }
                    }, 1800);
                  }}
                  placeholder="Start writing your story here... or let Shweebum help you generate content."
                  className={`writing-area w-full border-none shadow-none resize-none focus:ring-0 focus:outline-none transition-colors duration-300 ${kindleMode ? 'kindle-mode-textarea' : ''} ${isAudioPlaying ? 'audio-reading' : ''}`}
                  style={{
                    fontFamily: fontFamily,
                    fontSize: fontSize,
                    textAlign: textAlign,
                    lineHeight: lineHeight
                  }}
                  data-testid="writing-area"
                />
                {/* TTS Phase 2: hidden mirror div for caret-position measurement.
                    Styles are synced to the textarea at runtime via syncMirrorToTextarea. */}
                <div
                  ref={mirrorRef}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    zIndex: -1,
                  }}
                />
                {/* TTS Phase 1.5: VISIBLE word-highlight overlay positioned via mirror measurements.
                    No CSS transition — instant positioning to avoid "fading away" between distant words. */}
                <div
                  ref={wordHighlightRef}
                  aria-hidden="true"
                  data-testid="tts-word-overlay"
                  style={{
                    position: 'absolute',
                    display: 'none',
                    pointerEvents: 'none',
                    backgroundColor: 'rgba(34, 211, 238, 0.45)',
                    borderRadius: '2px',
                    boxShadow: '0 0 6px rgba(34, 211, 238, 0.55)',
                    zIndex: 2,
                  }}
                />
                </div>
                {/* Wow Factor message */}
                <AnimatePresence>
                  {wowMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="mx-4 mb-3 px-4 py-2 rounded-lg bg-cyan-950/70 border border-cyan-500/25 text-sm text-cyan-300 flex items-center gap-2"
                      data-testid="wow-message-paid"
                    >
                      <Sparkles className="w-4 h-4 shrink-0 text-cyan-400" />
                      {wowMessage}
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>

            {/* Generation Controls */}
            <Card className="mt-4 bg-card">
              <CardHeader className="pb-2 md:pb-4">
                <CardTitle className="text-lg md:text-xl flex items-center gap-2">
                  <Wand2 className="w-5 h-5 md:w-6 md:h-6 text-cyan-300" />
                  Generate Content
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-5">
                {/* Shweebum's Story Direction Suggestions */}
                <Button
                  variant="outline"
                  className="w-full py-4 md:py-3 text-base md:text-sm bg-purple-900/30 border-purple-500/50 hover:bg-purple-900/50 text-foreground"
                  onClick={handleGetStructuredSuggestions}
                  disabled={loadingSuggestions || generatingFromDirection}
                  data-testid="shweebum-suggest-btn"
                >
                  {loadingSuggestions ? (
                    <RefreshCw className="w-5 h-5 md:w-4 md:h-4 mr-2 animate-spin" />
                  ) : (
                    <Lightbulb className="w-5 h-5 md:w-4 md:h-4 mr-2 text-purple-400" />
                  )}
                  {loadingSuggestions ? 'Shweebum is thinking...' : 'Let Shweebum Suggest What\'s Next'}
                </Button>

                {/* Direction Suggestions Cards */}
                {directionSuggestions.length > 0 && !generatingFromDirection && (
                  <div className="space-y-3" data-testid="direction-suggestions">
                    <p className="text-sm font-medium text-foreground">Pick a direction:</p>
                    {directionSuggestions.map((suggestion, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                          selectedDirection === suggestion 
                            ? 'border-purple-500 bg-purple-900/30 shadow-md' 
                            : 'border-border bg-card hover:border-purple-500/50'
                        }`}
                        onClick={() => handleChooseDirection(suggestion)}
                        data-testid={`direction-card-${idx + 1}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 text-white text-sm font-bold flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-foreground text-base">{suggestion.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">{suggestion.description}</p>
                            <p className="text-xs text-purple-400 mt-1 italic">{suggestion.emotional_impact}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Generation Progress from Direction */}
                {generatingFromDirection && (
                  <div className="p-4 bg-purple-900/30 rounded-lg border border-purple-500/50" data-testid="direction-gen-progress">
                    <div className="flex items-center gap-3 mb-3">
                      <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
                      <p className="text-sm font-medium text-purple-300">
                        {chapterProgress.status === 'syncing' ? chapterProgress.message : directionGenProgress}
                      </p>
                    </div>
                    <div className="w-full bg-purple-950 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          chapterProgress.status === 'syncing' ? 'bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse' :
                          'bg-gradient-to-r from-purple-500 to-indigo-500'
                        }`}
                        style={{ width: chapterProgress.status === 'syncing' ? '100%' : `${(chapterProgress.part / (chapterProgress.total || 3)) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-center mt-1 text-purple-400">
                      {chapterProgress.status === 'syncing' ? 'Server is finishing generation...' :
                       chapterProgress.status === 'done' ? '✅ Complete!' :
                       chapterProgress.part === 0 ? 'Preparing...' :
                       chapterProgress.part === 1 ? 'Writing opening...' :
                       chapterProgress.part === 2 ? 'Developing the scene...' :
                       'Writing the conclusion...'}
                    </p>
                  </div>
                )}
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs md:text-xs">
                    <span className="bg-card px-2 text-muted-foreground">or write your own</span>
                  </div>
                </div>

                <div>
                  <Label className="text-base md:text-sm font-medium">What should happen next?</Label>
                  <Textarea
                    value={generateOptions.prompt}
                    onChange={(e) => setGenerateOptions({ ...generateOptions, prompt: e.target.value })}
                    placeholder="E.g., The protagonist discovers a hidden letter that reveals a family secret..."
                    className="mt-2 min-h-[120px] md:min-h-[80px] text-base md:text-sm"
                    data-testid="generate-prompt"
                  />
                  {/* Chapter placement selector */}
                  {project?.chapter_outline?.length > 0 && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <Label className="text-xs text-slate-400 whitespace-nowrap">Add to:</Label>
                      <select
                        value={generateOptions.targetChapter || 'end'}
                        onChange={(e) => setGenerateOptions({ ...generateOptions, targetChapter: e.target.value })}
                        className="rounded bg-slate-800 border border-cyan-500/30 text-slate-200 px-3 py-1.5 text-xs focus:border-cyan-500 focus:outline-none"
                        data-testid="generate-target-chapter"
                      >
                        <option value="end">End of manuscript</option>
                        {(project.chapter_outline || []).map(ch => (
                          <option key={`start-${ch.number}`} value={`chapter-${ch.number}-start`}>
                            Beginning of Ch {ch.number}: {ch.title || `Chapter ${ch.number}`}
                          </option>
                        ))}
                        {(project.chapter_outline || []).map(ch => (
                          <option key={`end-${ch.number}`} value={`chapter-${ch.number}-end`}>
                            End of Ch {ch.number}: {ch.title || `Chapter ${ch.number}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                
                {/* Quick Generation - for small additions */}
                <div className="p-4 md:p-3 bg-background rounded-lg border">
                  <p className="text-sm md:text-xs text-muted-foreground mb-3 md:mb-2 font-medium">Quick Add (small additions)</p>
                  <div className="flex flex-col md:flex-row gap-3 md:gap-2">
                    <div className="flex-1">
                      <Select 
                        value={generateOptions.length_type} 
                        onValueChange={(v) => setGenerateOptions({ ...generateOptions, length_type: v })}
                      >
                        <SelectTrigger data-testid="length-type-select" className="h-12 md:h-9 text-base md:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paragraphs">Paragraphs (~100 words each)</SelectItem>
                          <SelectItem value="words">Words</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        min="1"
                        max={generateOptions.length_type === 'words' ? 500 : 5}
                        value={generateOptions.length_value}
                        onChange={(e) => setGenerateOptions({ ...generateOptions, length_value: parseInt(e.target.value) || 1 })}
                        data-testid="length-value-input"
                        className="h-12 md:h-9 text-base md:text-sm"
                      />
                    </div>
                    <Button 
                      onClick={handleGenerate} 
                      variant="gold"
                      className="h-12 md:h-9 px-6 md:px-4 text-base md:text-sm"
                      disabled={generating}
                      data-testid="generate-btn"
                    >
                      {generating ? (
                        <RefreshCw className="w-5 h-5 md:w-4 md:h-4 animate-spin" />
                      ) : (
                        "Add"
                      )}
                    </Button>
                  </div>
                  <p className="text-sm md:text-xs text-muted-foreground mt-2 md:mt-1">
                    Best for: Quick additions, dialogue, short descriptions
                  </p>
                </div>

                {/* Extended Add - for longer additions using job-based system */}
                <div className="p-4 md:p-3 bg-slate-800/40 rounded-lg border-2 border-cyan-500/30">
                  <p className="text-sm md:text-xs text-cyan-300 mb-3 md:mb-2 font-semibold">Extended Add (longer additions)</p>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <Button
                      variant={extensionType === 'chapter_ending' ? 'default' : 'outline'}
                      className={`h-auto py-2 px-2 flex flex-col items-center text-xs ${
                        extensionType === 'chapter_ending' ? 'bg-cyan-600 hover:bg-cyan-700' : 'hover:bg-slate-800/60 border-cyan-500/30'
                      }`}
                      onClick={() => setExtensionType('chapter_ending')}
                      data-testid="ext-type-ending"
                    >
                      <span className="font-semibold">Ending</span>
                      <span className="text-[10px] opacity-80">~600-800 words</span>
                    </Button>
                    <Button
                      variant={extensionType === 'half_chapter' ? 'default' : 'outline'}
                      className={`h-auto py-2 px-2 flex flex-col items-center text-xs ${
                        extensionType === 'half_chapter' ? 'bg-cyan-600 hover:bg-cyan-700' : 'hover:bg-slate-800/60 border-cyan-500/30'
                      }`}
                      onClick={() => setExtensionType('half_chapter')}
                      data-testid="ext-type-half"
                    >
                      <span className="font-semibold">Half Chapter</span>
                      <span className="text-[10px] opacity-80">~1000-1200 words</span>
                    </Button>
                    <Button
                      variant={extensionType === 'full_extension' ? 'default' : 'outline'}
                      className={`h-auto py-2 px-2 flex flex-col items-center text-xs ${
                        extensionType === 'full_extension' ? 'bg-cyan-600 hover:bg-cyan-700' : 'hover:bg-slate-800/60 border-cyan-500/30'
                      }`}
                      onClick={() => setExtensionType('full_extension')}
                      data-testid="ext-type-full"
                    >
                      <span className="font-semibold">Full Extension</span>
                      <span className="text-[10px] opacity-80">~2000 words</span>
                    </Button>
                  </div>
                  <Button 
                    className="w-full py-3 md:py-2 text-base md:text-sm bg-cyan-600 hover:bg-cyan-700"
                    onClick={() => {
                      // Use the same generateOptions.prompt textarea
                      if (!generateOptions.prompt?.trim()) {
                        toast.error("Describe what should happen in the 'What should happen next?' box above");
                        return;
                      }
                      setExtensionDirectives(generateOptions.prompt);
                      handleGenerateExtension();
                    }}
                    disabled={generatingExtension || !generateOptions.prompt?.trim()}
                    data-testid="generate-extension-btn"
                  >
                    {generatingExtension ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Extending Part {extensionProgress.part}/{extensionProgress.total}...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Add {extensionType === 'chapter_ending' ? 'Ending' : extensionType === 'half_chapter' ? 'Half Chapter' : 'Full Extension'}
                      </>
                    )}
                  </Button>
                  
                  {/* Extension Progress */}
                  {generatingExtension && (
                    <div className="mt-3 p-2 bg-card rounded border border-cyan-500/25">
                      <div className="w-full bg-slate-800/60 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            extensionProgress.status === 'COMPLETE' ? 'bg-green-500' :
                            extensionProgress.status === 'FAILED' ? 'bg-red-500' :
                            'bg-cyan-600'
                          }`}
                          style={{ width: `${Math.max(10, (extensionProgress.part / extensionProgress.total) * 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-center mt-1 text-cyan-300">
                        {extensionProgress.status === 'COMPLETE' ? `✅ Added ${extensionProgress.word_count} words!` :
                         extensionProgress.status === 'FAILED' ? `❌ ${extensionProgress.error_message}` :
                         extensionProgress.status?.includes('GENERATING') ? `Writing Part ${extensionProgress.part}...` :
                         extensionProgress.status?.includes('PART_') ? `Part ${extensionProgress.part} saved` :
                         'Starting...'}
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-cyan-300 mt-2">
                    Best for: Chapter endings, expanding scenes, adding 1000+ words
                  </p>
                </div>

                {/* Full Scene Generation - 3-part streaming */}
                <div className="p-4 md:p-3 bg-gradient-to-r from-purple-950/50 to-indigo-950/50 rounded-lg border border-purple-500/30">
                  <p className="text-sm md:text-xs text-purple-300 mb-3 md:mb-2 font-medium">Full Scene (~1,500 words in 3 parts)</p>
                  <Button 
                    className="w-full py-4 md:py-2 text-base md:text-sm bg-purple-600 hover:bg-purple-700"
                    onClick={async () => {
                      if (!generateOptions.prompt) {
                        toast.error("Describe what should happen in the scene");
                        return;
                      }
                      setGenerating(true);
                      setChapterProgress({ part: 0, total: 3, status: 'generating' });
                      addToLog('generate', 'Generating full scene in 3 parts...');
                      
                      try {
                        const response = await fetch(`${API}/generate-scene-stream?project_id=${id}&user_id=${user?.id || ''}`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ prompt: generateOptions.prompt, genre: project.genre })
                        });
                        
                        if (!response.ok) {
                          let errorMsg = 'Generation failed';
                          try { const t = await response.text(); errorMsg = JSON.parse(t).detail || errorMsg; } catch(e) {}
                          throw new Error(errorMsg);
                        }
                        
                        const reader = response.body.getReader();
                        const decoder = new TextDecoder();
                        let fullContent = '';
                        
                        while (true) {
                          const { done, value } = await reader.read();
                          if (done) break;
                          
                          const chunk = decoder.decode(value);
                          const lines = chunk.split('\n');
                          
                          for (const line of lines) {
                            if (line.startsWith('data: ')) {
                              try {
                                const data = JSON.parse(line.slice(6));
                                if (data.type === 'content') {
                                  fullContent += data.content;
                                  setChapterProgress({ part: data.part, total: 3, status: 'generating' });
                                  setProject(prev => ({
                                    ...prev,
                                    content: prev.content + data.content
                                  }));
                                  addToLog('generate', `Part ${data.part}/3 received...`);
                                } else if (data.type === 'done') {
                                  setProject(prev => ({ ...prev, word_count: data.total_words }));
                                  setChapterProgress({ part: 3, total: 3, status: 'done' });
                                  toast.success(`Scene generated! ${fullContent.split(' ').length} words added`);
                                  setGenerateOptions({ ...generateOptions, prompt: '' });
                                } else if (data.type === 'error') {
                                  throw new Error(data.message);
                                }
                              } catch (e) {}
                            }
                          }
                        }
                      } catch (error) {
                        setChapterProgress({ part: 0, total: 3, status: 'error' });
                        const errorMsg = error?.message || 'Generation failed';
                        if (errorMsg.includes('subscription') || errorMsg.includes('requires Pro') || errorMsg.includes('Upgrade')) {
                          showUpgradePrompt('Full Scene Generation');
                        } else {
                          toast.error(`Failed: ${errorMsg}`);
                        }
                      } finally {
                        setGenerating(false);
                        setTimeout(() => setChapterProgress({ part: 0, total: 3, status: 'idle' }), 3000);
                      }
                    }}
                    className="w-full"
                    variant="gold"
                    disabled={generating || !generateOptions.prompt}
                    data-testid="generate-scene-btn"
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Writing Part {chapterProgress.part}/3...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Full Scene
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-purple-400 mt-1">
                    Best for: Major scenes, action sequences, plot developments
                  </p>
                  
                  {/* Progress indicator for scene generation */}
                  {generating && (chapterProgress.status === 'generating' || chapterProgress.status === 'syncing') && (
                    <div className="mt-3 p-2 bg-card rounded border">
                      <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            chapterProgress.status === 'syncing' ? 'bg-purple-500 animate-pulse' :
                            chapterProgress.part === 1 ? 'bg-blue-500' :
                            chapterProgress.part === 2 ? 'bg-cyan-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: chapterProgress.status === 'syncing' ? '100%' : `${(chapterProgress.part / (chapterProgress.total || 3)) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-center mt-1 text-muted-foreground">
                        {chapterProgress.status === 'syncing' ? `🔄 ${chapterProgress.message || 'Syncing...'}` :
                         chapterProgress.part === 1 ? '📝 Opening...' :
                         chapterProgress.part === 2 ? '📖 Developing...' :
                         '🎬 Climax...'}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Hidden Chapter Outline on mobile since it's shown above */}
          {/* Right Panel — hidden in focus mode, tabbed control center */}
          <div className={`space-y-3 order-3 lg:order-2 transition-all duration-300 ${focusMode ? 'hidden' : ''}`}>
            
            {/* ── Tab Switcher ── */}
            <div className="hidden lg:flex rounded-xl overflow-hidden border border-slate-700/60 bg-slate-900/50">
              {[
                { id: 'chapters', label: 'Chapters', color: 'cyan' },
                { id: 'improve', label: 'Improve', color: 'violet' },
                { id: 'story', label: 'Story', color: 'amber' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setRightPanelTab(tab.id)}
                  className={`flex-1 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all ${
                    rightPanelTab === tab.id
                      ? tab.color === 'cyan' ? 'bg-cyan-500/20 text-cyan-300 border-b-2 border-cyan-400'
                        : tab.color === 'violet' ? 'bg-violet-500/20 text-violet-300 border-b-2 border-violet-400'
                        : 'bg-amber-500/20 text-amber-300 border-b-2 border-amber-400'
                      : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/40'
                  }`}
                  data-testid={`right-panel-tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── CHAPTERS TAB ── */}
            <div className={rightPanelTab !== 'chapters' ? 'hidden lg:hidden' : ''}>
            {/* Chapter Outline (Desktop only - mobile has toggle above) */}
            {project?.chapter_outline?.length > 0 && (
              <Card className="hidden lg:block bg-card border-2 border-cyan-500/30 shadow-md">
                <CardHeader className="py-3 bg-slate-800/40">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="flex items-center gap-2 text-foreground">
                      <BookOpen className="w-5 h-5 text-cyan-400" />
                      {project.target_length === 'quick_story' ? 'Short Story' : 'Chapter Outline'}
                      {project.outline_status === 'generating' && (
                        <RefreshCw className="w-4 h-4 animate-spin text-cyan-400" />
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCharacterEditor(true)}
                        className="h-7 text-xs bg-purple-600/20 border-purple-500/50 hover:bg-purple-600/30 text-purple-300"
                        data-testid="edit-characters-btn"
                      >
                        <Users className="w-3 h-3 mr-1" />
                        Edit Characters
                      </Button>
                      <Badge className="bg-cyan-600 text-white">
                        {project.target_length === 'quick_story' ? 'Short Story' : `${project.chapter_outline.length} chapters`}
                      </Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3">
                  {/* Outline generating status */}
                  {project.outline_status === 'generating' && (
                    <div className="mb-4 p-3 bg-slate-800/40 rounded-lg border border-cyan-500/30">
                      <p className="text-sm text-cyan-300 font-medium flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Generating chapter titles and summaries...
                      </p>
                    </div>
                  )}
                  
                  {/* Instructions when NOT generating */}
                  {generatingChapter === null && project.outline_status !== 'generating' && (
                    <div className="mb-4 p-3 bg-blue-900/30 rounded-lg border border-blue-500/50">
                      <p className="text-sm text-blue-300 font-medium mb-1">How it works:</p>
                      <ul className="text-xs text-blue-400 space-y-1">
                        <li>• Click <strong>"Generate"</strong> on any chapter to write it</li>
                        <li>• Each chapter is written in <strong>parts sized to your outline</strong> (big outlines get more parts)</li>
                        <li>• Wait for the <span className="text-green-400 font-medium">green "Complete!"</span> before starting another</li>
                      </ul>
                    </div>
                  )}
                  
                  {/* Progress Bar & Status when generating */}
                  {generatingChapter !== null && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-slate-800/50 to-slate-700/50 rounded-lg border-2 border-cyan-500/30 shadow-md">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-cyan-200">
                          ✨ Writing Chapter {generatingChapter}...
                        </span>
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-card shadow-sm">
                          {chapterProgress.status === 'error' ? '❌ Error' : 
                           chapterProgress.status === 'done' ? '✅ Complete!' :
                           `Part ${chapterProgress.part}/${chapterProgress.total}`}
                        </span>
                      </div>
                      <div className="w-full bg-card rounded-full h-4 overflow-hidden shadow-inner">
                        <div 
                          className={`h-full rounded-full transition-all duration-700 ease-out ${
                            chapterProgress.status === 'error' ? 'bg-gradient-to-r from-red-400 to-red-600' :
                            chapterProgress.status === 'done' ? 'bg-gradient-to-r from-green-400 to-green-600' :
                            chapterProgress.part === 1 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                            chapterProgress.part === 2 ? 'bg-gradient-to-r from-cyan-400 to-blue-500' :
                            chapterProgress.part === 3 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                            'bg-blue-300'
                          }`}
                          style={{ width: `${(chapterProgress.part / chapterProgress.total) * 100}%` }}
                        />
                      </div>
                      <p className="text-sm text-cyan-100 mt-3 text-center font-medium">
                        {chapterProgress.status === 'error' ? '⚠️ Generation failed - please try again' :
                         chapterProgress.status === 'done' ? '🎉 Chapter complete! You can now generate another.' :
                         chapterProgress.part === 0 ? '⏳ Preparing to write...' :
                         chapterProgress.part === 1 ? '📝 Part 1: Writing the opening scene...' :
                         chapterProgress.part >= (chapterProgress.total || 3) ? `🎬 Part ${chapterProgress.part}: Writing the climax & ending...` :
                         `📖 Part ${chapterProgress.part}/${chapterProgress.total}: Developing the story...`}
                      </p>
                      {chapterProgress.status !== 'done' && chapterProgress.status !== 'error' && (
                        <p className="text-xs text-cyan-300 mt-2 text-center animate-pulse">
                          ⚡ Please wait - do not close or navigate away
                        </p>
                      )}
                    </div>
                  )}
                  <ScrollArea className="h-56 w-full [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:w-full">
                    <div className="space-y-2">
                      {project.chapter_outline.map((chapter, idx) => {
                        const chapterWritten = project.content?.includes(`CHAPTER ${chapter.number}:`);
                        const totalChapters = project.chapter_outline.length;
                        const storyProgress = chapter.number / totalChapters;
                        const isFinalChapter = chapter.number === totalChapters;
                        const isWrapUpZone = storyProgress >= 0.75;
                        
                        return (
                          <div 
                            key={idx} 
                            className={`p-2 rounded-lg border ${
                              chapterWritten ? 'bg-green-900/30 border-green-500/40' : 
                              isFinalChapter ? 'bg-purple-900/30 border-purple-500/40' :
                              isWrapUpZone ? 'bg-gradient-to-r from-slate-800/50 to-slate-700/50 border-cyan-500/30' :
                              'bg-background border-border'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1 gap-1 flex-wrap">
                              <div className="flex items-center gap-1 flex-1 min-w-0">
                                {editingChapterTitle === `d-${idx}` ? (
                                  <input
                                    autoFocus
                                    className="font-medium text-sm text-foreground bg-card border border-cyan-500/50 rounded px-1.5 py-0.5 w-full"
                                    value={chapter.title || ''}
                                    data-testid={`chapter-title-input-${idx}`}
                                    onChange={(e) => {
                                      const newOutline = [...project.chapter_outline];
                                      newOutline[idx] = { ...chapter, title: e.target.value, ...(chapterWritten ? { edited_after_write: true } : {}) };
                                      setProject(prev => ({ ...prev, chapter_outline: newOutline }));
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    onBlur={async () => {
                                      setEditingChapterTitle(null);
                                      try {
                                        await axios.put(`${API}/project/${id}`, { chapter_outline: project.chapter_outline });
                                        toast.success("Chapter title saved!", { duration: 1500 });
                                      } catch (err) {
                                        toast.error("Failed to save changes");
                                      }
                                    }}
                                  />
                                ) : (
                                  <span className="font-medium text-sm text-foreground truncate">
                                    Ch {chapter.number}: {chapter.title}
                                  </span>
                                )}
                                {!chapterWritten && isFinalChapter && (
                                  <span className="text-xs px-1.5 py-0.5 bg-purple-600/40 text-purple-200 rounded font-medium">FINALE</span>
                                )}
                                {!chapterWritten && isWrapUpZone && !isFinalChapter && (
                                  <span className="text-xs px-1.5 py-0.5 bg-cyan-900/30 text-cyan-300 rounded font-medium">Wrap-up</span>
                                )}
                              </div>
                              <div className="flex items-center gap-0.5 shrink-0">
                                <button
                                  className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:text-cyan-300 hover:bg-cyan-900/30 transition-colors"
                                  onClick={() => setEditingChapterTitle(`d-${idx}`)}
                                  title="Rewrite chapter title"
                                  data-testid={`edit-chapter-title-btn-${idx}`}
                                >
                                  <Edit3 className="w-3 h-3" />
                                </button>
                                <button
                                  className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:text-red-400 hover:bg-red-900/30 transition-colors"
                                  onClick={() => handleDeleteChapterOutline(idx)}
                                  title="Delete this chapter"
                                  data-testid={`delete-chapter-btn-${idx}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              {chapterWritten ? (
                                chapter.edited_after_write ? (
                                  <Button
                                    size="sm"
                                    className="h-6 text-xs bg-amber-600 hover:bg-amber-500 text-white"
                                    onClick={() => handleRewriteChapter(chapter.number)}
                                    disabled={generatingChapter !== null || planningChapter !== null}
                                    title="Outline changed — delete this chapter's text and rewrite it in place"
                                    data-testid={`rewrite-chapter-btn-${idx}`}
                                  >
                                    {generatingChapter === chapter.number ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-0.5" /> Rewrite</>}
                                  </Button>
                                ) : (
                                  <Badge className="bg-green-600/30 text-green-300 border border-green-500/40 text-xs">Written</Badge>
                                )
                              ) : (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                                    onClick={() => generateChapterPlan(chapter.number)}
                                    disabled={generatingChapter !== null || planningChapter !== null}
                                    title="Plan first, then generate"
                                  >
                                    {planningChapter === chapter.number ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Plan"}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={isFreePlan ? "outline" : "gold"}
                                    className={`h-6 text-xs ${isFreePlan ? 'border-slate-600 text-slate-400' : ''}`}
                                    onClick={() => {
                                      if (isFreePlan) {
                                        toast.error("Chapter generation requires a paid plan. Upgrade to Shweebum Plus ($10/mo)!");
                                        return;
                                      }
                                      handleGenerateChapter(chapter.number);
                                    }}
                                    disabled={generatingChapter !== null || planningChapter !== null}
                                    title={isFreePlan ? "Upgrade to generate" : "Generate directly"}
                                  >
                                    {generatingChapter === chapter.number ? <RefreshCw className="w-3 h-3 animate-spin" /> : 
                                      isFreePlan ? <><Lock className="w-3 h-3 mr-0.5" /> Go</> : "Go"
                                    }
                                  </Button>
                                </div>
                              )}
                              </div>
                            </div>
                            {/* Editable chapter summary */}
                            <div className="relative">
                              <textarea
                                className="text-xs text-muted-foreground w-full bg-card border border-border hover:border-border focus:border-blue-400 focus:bg-card rounded p-2 pr-10 resize-y transition-colors"
                                rows={6}
                                value={chapter.summary || ''}
                                placeholder="Write your detailed chapter plan here! Include specific scenes, dialogue, character actions. The more detail you add, the more closely the AI will follow your vision when you click 'Go'."
                                onChange={(e) => {
                                  const newOutline = [...project.chapter_outline];
                                  newOutline[idx] = { ...chapter, summary: e.target.value, ...(chapterWritten ? { edited_after_write: true } : {}) };
                                  setProject(prev => ({ ...prev, chapter_outline: newOutline }));
                                }}
                                onBlur={async (e) => {
                                  // Save to backend when focus leaves
                                  try {
                                    await axios.put(`${API}/project/${id}`, {
                                      chapter_outline: project.chapter_outline
                                    });
                                    toast.success("Chapter plan saved!", { duration: 1500 });
                                  } catch (err) {
                                    toast.error("Failed to save changes");
                                  }
                                }}
                                title="Click to edit - your changes will be used when generating this chapter"
                              />
                              <SpeechToTextButton
                                onTranscript={(text, append) => {
                                  const newOutline = [...project.chapter_outline];
                                  const currentSummary = chapter.summary || '';
                                  newOutline[idx] = { 
                                    ...chapter, 
                                    summary: append && currentSummary ? currentSummary + ' ' + text : text 
                                  };
                                  setProject(prev => ({ ...prev, chapter_outline: newOutline }));
                                }}
                                className="absolute right-2 top-2 h-7 w-7"
                                size="icon"
                              />
                            </div>
                          </div>
                        );
                      })}
                      {/* Add Chapter Outline (desktop panel) */}
                      <button
                        onClick={() => setShowAddChapter(true)}
                        data-testid="add-chapter-outline-btn"
                        className="w-full mt-2 py-2 rounded-lg border border-dashed border-cyan-500/50 text-cyan-300 hover:bg-cyan-900/30 hover:border-cyan-400 text-sm font-medium transition-colors"
                      >
                        + Add Chapter
                      </button>
                    </div>
                  </ScrollArea>
                  <div className="mt-3 pt-3 border-t border-border space-y-2">
                    {chapterLengthPicker}
                    <p className="text-xs text-muted-foreground text-center">
                      {chapterLengthMode === "auto"
                        ? "Auto: each chapter fits its outline — no padding"
                        : chapterLengthMode === "medium"
                          ? "Each chapter generates ~900 words in 3 parts"
                          : "Each chapter generates ~2,000 words in 3 parts"}
                    </p>
                    
                    {/* Generate Full Book Button & Progress */}
                    {!generatingFullBook ? (
                      <Button
                        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                        onClick={() => handleGenerateFullBook(1)}
                        disabled={generatingChapter !== null || planningChapter !== null}
                        data-testid="generate-full-book-btn"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />Generate My Entire Book
                      </Button>
                    ) : (
                      <div className="space-y-2 p-3 bg-purple-900/30 rounded-lg border border-purple-500/50">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-purple-300">
                            {cancellingFullBook === 'idle' && 'Generating Book...'}
                            {cancellingFullBook === 'requested' && 'Cancellation Requested...'}
                            {cancellingFullBook === 'confirmed' && 'Stopping Generation...'}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`h-6 text-xs ${
                              cancellingFullBook !== 'idle'
                                ? 'border-red-600 bg-red-900/30 text-red-300 cursor-not-allowed opacity-70'
                                : 'border-red-500/50 text-red-400 hover:bg-red-900/30'
                            }`}
                            onClick={handleCancelFullBook}
                            disabled={cancellingFullBook !== 'idle'}
                            data-testid="cancel-full-book-sidebar-btn"
                          >
                            {cancellingFullBook === 'idle' && 'Cancel'}
                            {cancellingFullBook === 'requested' && 'Cancelling...'}
                            {cancellingFullBook === 'confirmed' && 'Stopping...'}
                          </Button>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-purple-950 rounded-full h-2">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              cancellingFullBook !== 'idle'
                                ? 'bg-gradient-to-r from-red-500 to-orange-500'
                                : 'bg-gradient-to-r from-purple-500 to-indigo-500'
                            }`}
                            style={{ width: `${(fullBookProgress.chaptersCompleted / fullBookProgress.totalChapters) * 100}%` }}
                          />
                        </div>
                        
                        {/* Status */}
                        <div className="text-xs text-purple-300 space-y-1">
                          <div className="flex justify-between">
                            <span>Chapter {fullBookProgress.currentChapter} of {fullBookProgress.totalChapters}</span>
                            <span>{fullBookProgress.chaptersCompleted} completed</span>
                          </div>
                          <div className={`italic ${cancellingFullBook !== 'idle' ? 'text-red-400' : 'text-purple-400'}`}>
                            {cancellingFullBook !== 'idle' && 'Finishing current chapter before stopping...'}
                            {cancellingFullBook === 'idle' && fullBookProgress.chapterStatus === 'PLANNING' && 'Planning chapter...'}
                            {cancellingFullBook === 'idle' && fullBookProgress.chapterStatus === 'GENERATING' && 'Writing chapter...'}
                            {cancellingFullBook === 'idle' && fullBookProgress.chapterStatus === 'COMPLETED' && 'Chapter done!'}
                            {cancellingFullBook === 'idle' && fullBookProgress.chapterStatus === 'QUEUED' && 'Starting...'}
                          </div>
                          {cancellingFullBook === 'idle' && fullBookProgress.estimatedTimeRemaining > 0 && (
                            <div className="text-muted-foreground">
                              ~{Math.ceil(fullBookProgress.estimatedTimeRemaining / 60)} min remaining
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Resume button if there are some chapters but not all */}
                    {!generatingFullBook && project?.content?.includes('--- CHAPTER') && (() => {
                      const writtenChapters = (project.content.match(/--- CHAPTER \d+/g) || []).length;
                      const totalChapters = project.chapter_outline?.length || 0;
                      if (writtenChapters > 0 && writtenChapters < totalChapters) {
                        return (
                          <Button
                            variant="outline"
                            className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                            onClick={() => handleGenerateFullBook(writtenChapters + 1)}
                            disabled={generatingChapter !== null || planningChapter !== null || (user?.subscription === 'free' || !user?.subscription)}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Continue from Chapter {writtenChapters + 1} ({totalChapters - writtenChapters} remaining)
                          </Button>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Erase Content Buttons - Show when there's any content */}
                    {project?.content?.trim().length > 0 && (
                      <div className="space-y-2">
                        {project.content.includes('--- CHAPTER') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full text-red-400 border-red-500/40 hover:bg-red-900/30 hover:text-red-300"
                            onClick={async () => {
                              if (!confirm('Erase the last chapter? This cannot be undone.')) return;
                              // Find and remove the last chapter
                              const content = project.content;
                              const lastChapterIndex = content.lastIndexOf('\n\n--- CHAPTER');
                              if (lastChapterIndex > 0) {
                                const newContent = content.substring(0, lastChapterIndex);
                                setProject(prev => ({
                                  ...prev,
                                  content: newContent,
                                  word_count: newContent.split(' ').length
                                }));
                                // Save to backend
                                try {
                                  await axios.put(`${API}/project/${id}`, {
                                    content: newContent,
                                    word_count: newContent.split(' ').length
                                  });
                                  toast.success('Last chapter erased');
                                  addToLog('edit', 'Erased last chapter');
                                } catch (e) {
                                  toast.error('Failed to save - please try again');
                                }
                              } else {
                                // This is the first/only chapter - offer to clear all
                                if (confirm('This is the only chapter. Clear ALL content to start fresh?')) {
                                  const newContent = '';
                                  setProject(prev => ({
                                    ...prev,
                                    content: newContent,
                                    word_count: 0
                                  }));
                                  try {
                                    await axios.put(`${API}/project/${id}`, {
                                      content: newContent,
                                      word_count: 0
                                    });
                                    toast.success('All content cleared - start fresh!');
                                    addToLog('edit', 'Cleared all content');
                                  } catch (e) {
                                    toast.error('Failed to save - please try again');
                                  }
                                }
                              }
                            }}
                          >
                            <Trash2 className="w-3 h-3 mr-2" />
                            Erase Last Chapter
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-red-400 hover:text-red-300 hover:bg-red-900/30 text-xs"
                          onClick={async () => {
                            if (!confirm('⚠️ Clear ALL content and start completely fresh? This cannot be undone!')) return;
                            const newContent = '';
                            setProject(prev => ({
                              ...prev,
                              content: newContent,
                              word_count: 0
                            }));
                            try {
                              await axios.put(`${API}/project/${id}`, {
                                content: newContent,
                                word_count: 0
                              });
                              toast.success('All content cleared - start fresh!');
                              addToLog('edit', 'Cleared all content');
                            } catch (e) {
                              toast.error('Failed to save - please try again');
                            }
                          }}
                        >
                          Clear Everything & Start Over
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            </div> {/* end CHAPTERS TAB */}

            {/* ── IMPROVE TAB ── */}
            <div className={rightPanelTab !== 'improve' ? 'hidden' : 'space-y-3'}>
              {/* Zone Header */}
              <div className="flex items-center gap-2 px-1">
                <div className="w-1.5 h-5 rounded-full bg-gradient-to-b from-violet-400 to-purple-600" />
                <span className="text-xs font-semibold text-violet-300 uppercase tracking-widest">Improve Zone</span>
                <div className="flex-1 h-px bg-violet-500/20" />
                <ShweebumCat size="sm" speaking={analyzing} />
              </div>

              {/* Section 1: Story Memory */}
              <Card className="bg-[#0D1525]/80 border-purple-500/25 shadow-sm">
                <CardContent className="p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-widest mb-2">Story Memory</p>
                  <div className="relative">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-400 via-pink-300 to-indigo-400 rounded-lg opacity-30 animate-pulse" />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start relative bg-purple-900/30 border-purple-500/50 hover:bg-purple-900/50 text-foreground"
                      onClick={() => { loadStoryBible(); setShowStoryBible(true); }}
                      data-testid="story-bible-btn"
                    >
                      <BookMarked className="w-4 h-4 mr-2 text-purple-400" />
                      Story Bible
                      <Sparkles className="w-3 h-3 ml-auto text-purple-400 animate-pulse" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-purple-400/70 px-0.5">Keeps characters, plot threads & tone consistent.</p>
                </CardContent>
              </Card>

              {/* Section 2: Fix & Polish */}
              <Card className="bg-[#0D1525]/80 border-cyan-500/20 shadow-sm">
                <CardContent className="p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-cyan-400 uppercase tracking-widest mb-2">Fix & Polish</p>

                  {/* Issue Scanner */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start bg-rose-900/20 border-rose-500/40 hover:bg-rose-900/40 text-foreground transition-all"
                    onClick={() => setShowFindFix(true)}
                    data-testid="find-fix-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-rose-400" />
                    Issue Scanner
                    <span className="ml-auto text-[9px] text-rose-400/60 font-medium">LOCAL</span>
                  </Button>

                  {/* Quick Fix (Basic) */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start bg-violet-900/20 border-violet-500/40 hover:bg-violet-900/40 text-foreground transition-all"
                    onClick={() => setShowNewQuickFix(true)}
                    data-testid="quick-fix-btn"
                  >
                    <Zap className="w-4 h-4 mr-2 text-violet-400" />
                    Quick Fix (Basic)
                    <span className="ml-auto text-[9px] text-violet-400/60 font-medium">LOCAL</span>
                  </Button>

                  {/* Final Polish (Pro) */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start bg-gradient-to-r from-violet-900/30 to-purple-900/30 border-violet-500/40 hover:from-violet-900/50 hover:to-purple-900/50 text-foreground transition-all"
                    onClick={() => {
                      const isOwner = user?.email?.toLowerCase() === 'therealbeastlygamer@gmail.com';
                      const hasAccess = isOwner || ['plus', 'premium', 'admin', 'author', 'storyteller', 'family'].includes(user?.subscription);
                      if (hasAccess) {
                        setShowFinalEditor(true);
                      } else {
                        setUpgradeFeature('Final Polish (Pro)');
                        setShowUpgradeModal(true);
                      }
                    }}
                    data-testid="final-editor-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-violet-400" />
                    Final Polish (Pro)
                    {user?.subscription && !['plus', 'premium', 'admin', 'author', 'storyteller', 'family'].includes(user.subscription) && user?.email?.toLowerCase() !== 'therealbeastlygamer@gmail.com' ? (
                      <Badge className="ml-auto bg-amber-500/20 text-amber-300 text-[10px]">PLUS</Badge>
                    ) : (
                      <span className="ml-auto text-[9px] text-violet-400/60 font-medium">PRO</span>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Section 3: AI Rewrite */}
              <Card className="bg-[#0D1525]/80 border-cyan-500/30 shadow-sm">
                <CardContent className="p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-cyan-400 uppercase tracking-widest mb-2">AI Rewrite</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start bg-gradient-to-r from-cyan-900/30 to-violet-900/30 border-cyan-500/40 hover:from-cyan-900/50 hover:to-violet-900/50 text-foreground transition-all"
                    onClick={() => setShowQuickFix(true)}
                    data-testid="nova-edit-btn"
                  >
                    <Zap className="w-4 h-4 mr-2 text-cyan-400" />
                    AI Rewrite (Nova)
                    <span className="ml-auto text-[9px] text-cyan-400/70 font-semibold">AI</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start bg-cyan-900/20 border-cyan-500/30 hover:bg-cyan-900/40 text-foreground transition-all"
                    onClick={() => {
                      const chapterCount = (project.content?.match(/---\s*CHAPTER\s+\d+/gi) || []).length;
                      if (chapterCount > 0) {
                        openChapterEditor(1);
                      } else {
                        toast.error("No chapters found");
                      }
                    }}
                    data-testid="chapter-editor-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2 text-cyan-400" />
                    Edit Chapter
                  </Button>
                </CardContent>
              </Card>

              {/* Section 4: Analyze & Plan */}
              <Card className="bg-[#0D1525]/80 border-slate-700/50 shadow-sm">
                <CardContent className="p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Analyze & Plan</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start hover:bg-slate-800/50 text-foreground transition-all"
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    data-testid="analyze-btn"
                  >
                    <BookOpen className="w-4 h-4 mr-2 text-blue-400" />
                    {analyzing ? "Analyzing..." : "Analyze Story"}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start bg-emerald-900/20 border-emerald-500/30 hover:bg-emerald-900/40 text-foreground transition-all"
                    onClick={() => { setShowStoryNavigator(true); if(project?.content) handleAnalyzeStoryPosition(); }}
                    data-testid="story-navigator-btn"
                  >
                    <Map className="w-4 h-4 mr-2 text-emerald-400" />
                    Where Am I? (Navigator)
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start bg-emerald-900/20 border-emerald-500/30 hover:bg-emerald-900/40 text-foreground transition-all"
                    onClick={() => setShowAnalysisDashboard(true)}
                    data-testid="analysis-dashboard-btn"
                  >
                    <BarChart3 className="w-4 h-4 mr-2 text-emerald-400" />
                    Manuscript Analysis
                  </Button>
                </CardContent>
              </Card>

              {/* Section 5: Story Tools (collapsible feel) */}
              <Card className="bg-[#0D1525]/80 border-slate-700/40 shadow-sm">
                <CardContent className="p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Story Tools</p>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start"
                      onClick={() => handleGetSuggestions("plot")}
                      disabled={analyzing}
                      data-testid="suggest-plot-btn"
                    >
                      <Lightbulb className="w-4 h-4 mr-2 text-amber-400" />
                      Suggest Plot Points
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start"
                      onClick={() => handleGetSuggestions("character")}
                      disabled={analyzing}
                      data-testid="suggest-character-btn"
                    >
                      <Users className="w-4 h-4 mr-2 text-pink-400" />
                      Suggest Characters
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start"
                      onClick={() => handleGetSuggestions("names")}
                      disabled={analyzing}
                      data-testid="suggest-names-btn"
                    >
                      <PenTool className="w-4 h-4 mr-2 text-blue-400" />
                      Suggest Names
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start"
                      onClick={() => handleGetSuggestions("direction")}
                      disabled={analyzing}
                      data-testid="suggest-direction-btn"
                    >
                      <Target className="w-4 h-4 mr-2 text-indigo-400" />
                      Story Directions
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start bg-rose-900/20 border-rose-500/30 hover:bg-rose-900/40 text-foreground"
                      onClick={() => setShowBackCover(true)}
                      data-testid="back-cover-btn"
                    >
                      <BookOpen className="w-4 h-4 mr-2 text-rose-400" />
                      Back Cover Blurb
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start bg-gradient-to-r from-violet-900/20 to-fuchsia-900/20 border-violet-500/30 hover:from-violet-900/40 hover:to-fuchsia-900/40 text-foreground"
                      onClick={() => setShowCoverDesign(true)}
                      data-testid="cover-design-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2 text-fuchsia-400" />
                      AI Cover Design
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start bg-slate-800/40 border-cyan-500/20 hover:bg-slate-800/60 text-foreground"
                      onClick={() => {
                        const selection = window.getSelection()?.toString() || '';
                        setSelectedText(selection);
                        setRewriteResult(null);
                        setShowWritingTools(true);
                      }}
                      data-testid="writing-tools-btn"
                    >
                      <Wand2 className="w-4 h-4 mr-2 text-cyan-400" />
                      Writing Polish Tools
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div> {/* end IMPROVE TAB */}

            {/* ── STORY TAB ── */}
            <div className={rightPanelTab !== 'story' ? 'hidden' : 'space-y-3'}>
              <div className="flex items-center gap-2 px-1">
                <div className="w-1.5 h-5 rounded-full bg-gradient-to-b from-amber-400 to-orange-500" />
                <span className="text-xs font-semibold text-amber-300 uppercase tracking-widest">Story Tools</span>
                <div className="flex-1 h-px bg-amber-500/20" />
              </div>

              {/* Story Memory */}
              <Card className="bg-[#0D1525]/80 border-purple-500/25">
                <CardContent className="p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-widest mb-2">Story Memory</p>
                  <div className="relative">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-400 via-pink-300 to-indigo-400 rounded-lg opacity-25 animate-pulse" />
                    <Button variant="outline" size="sm" className="w-full justify-start relative bg-purple-900/30 border-purple-500/50 hover:bg-purple-900/50 text-foreground"
                      onClick={() => { loadStoryBible(); setShowStoryBible(true); }} data-testid="story-story-bible-btn">
                      <BookMarked className="w-4 h-4 mr-2 text-purple-400" />Story Bible
                      <Sparkles className="w-3 h-3 ml-auto text-purple-400 animate-pulse" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Analyze */}
              <Card className="bg-[#0D1525]/80 border-slate-700/50">
                <CardContent className="p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Analyze</p>
                  <Button variant="outline" size="sm" className="w-full justify-start hover:bg-slate-800/50 text-foreground" onClick={handleAnalyze} disabled={analyzing} data-testid="story-analyze-btn">
                    <BookOpen className="w-4 h-4 mr-2 text-blue-400" />{analyzing ? 'Analyzing…' : 'Analyze Story'}
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start bg-emerald-900/20 border-emerald-500/30 hover:bg-emerald-900/40 text-foreground"
                    onClick={() => { setShowStoryNavigator(true); if(project?.content) handleAnalyzeStoryPosition(); }} data-testid="story-navigator-btn2">
                    <Map className="w-4 h-4 mr-2 text-emerald-400" />Where Am I? (Navigator)
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start bg-emerald-900/20 border-emerald-500/30 hover:bg-emerald-900/40 text-foreground"
                    onClick={() => setShowAnalysisDashboard(true)} data-testid="story-analysis-dashboard-btn">
                    <BarChart3 className="w-4 h-4 mr-2 text-emerald-400" />Manuscript Analysis
                  </Button>
                </CardContent>
              </Card>

              {/* Suggestions */}
              <Card className="bg-[#0D1525]/80 border-amber-500/20">
                <CardContent className="p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-widest mb-2">Generate Ideas</p>
                  <Button variant="outline" size="sm" className="w-full justify-start hover:bg-slate-800/50 text-foreground"
                    onClick={() => handleGetSuggestions("plot")} disabled={analyzing} data-testid="story-plot-btn">
                    <Lightbulb className="w-4 h-4 mr-2 text-amber-400" />Suggest Plot Points
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start hover:bg-slate-800/50 text-foreground"
                    onClick={() => handleGetSuggestions("character")} disabled={analyzing} data-testid="story-char-btn">
                    <Users className="w-4 h-4 mr-2 text-pink-400" />Suggest Characters
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start hover:bg-slate-800/50 text-foreground"
                    onClick={() => handleGetSuggestions("names")} disabled={analyzing} data-testid="story-names-btn">
                    <PenTool className="w-4 h-4 mr-2 text-blue-400" />Suggest Names
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start hover:bg-slate-800/50 text-foreground"
                    onClick={() => handleGetSuggestions("direction")} disabled={analyzing} data-testid="story-dir-btn">
                    <Target className="w-4 h-4 mr-2 text-indigo-400" />Story Directions
                  </Button>
                </CardContent>
              </Card>

              {/* Covers & Blurb */}
              <Card className="bg-[#0D1525]/80 border-rose-500/20">
                <CardContent className="p-3 space-y-2">
                  <p className="text-[10px] font-semibold text-rose-400 uppercase tracking-widest mb-2">Book Packaging</p>
                  <Button variant="outline" size="sm" className="w-full justify-start bg-rose-900/20 border-rose-500/30 hover:bg-rose-900/40 text-foreground"
                    onClick={() => setShowBackCover(true)} data-testid="story-back-cover-btn">
                    <BookOpen className="w-4 h-4 mr-2 text-rose-400" />Back Cover Blurb
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start bg-violet-900/20 border-violet-500/30 hover:bg-violet-900/40 text-foreground"
                    onClick={() => setShowCoverDesign(true)} data-testid="story-cover-design-btn">
                    <Sparkles className="w-4 h-4 mr-2 text-fuchsia-400" />AI Cover Design
                  </Button>
                </CardContent>
              </Card>
            </div> {/* end STORY TAB */}

            {/* Suggestions Panel (shared, shows in all tabs) */}
            {suggestions && (
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle className="text-lg">Shweebum Says...</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <div className="prose prose-sm text-foreground whitespace-pre-wrap">
                      {suggestions}
                    </div>
                  </ScrollArea>
                  <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setSuggestions(null)}>Clear</Button>
                </CardContent>
              </Card>
            )}

            {/* Stats */}
            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Book Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Words</span>
                  <span className="font-medium">{project.word_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pages (est.)</span>
                  <span className="font-medium">{Math.ceil((project.word_count || 0) / 250)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Genre</span>
                  <Badge variant="secondary" className="capitalize">{project.genre}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant="outline" className="capitalize">{project.status}</Badge>
                </div>
                
                {/* Clear Content Button - Always show when there's content */}
                {project?.content?.trim().length > 0 && (
                  <div className="pt-3 border-t border-slate-700">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                      onClick={async () => {
                        if (!confirm('⚠️ Clear ALL content and start fresh? This cannot be undone!')) return;
                        setProject(prev => ({ ...prev, content: '', word_count: 0 }));
                        try {
                          await axios.put(`${API}/project/${id}`, { content: '', word_count: 0 });
                          toast.success('All content cleared - start fresh!');
                          addToLog('edit', 'Cleared all content');
                        } catch (e) {
                          toast.error('Failed to save - please try again');
                        }
                      }}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear All Content
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Activity Log */}
        <div className="mt-6">
          <Card className="bg-background border-border">
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Activity Log
                </CardTitle>
                <div className="flex gap-2">
                  {hasErrors && (
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => setShowReportDialog(true)}
                      className="text-xs"
                      data-testid="report-issue-btn"
                    >
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Report Issue
                    </Button>
                  )}
                  {!hasErrors && activityLog.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowReportDialog(true)}
                      className="text-xs text-muted-foreground"
                      data-testid="send-report-btn"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Send Report
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      if (activityLog.length === 0) {
                        toast.info("Nothing to copy");
                        return;
                      }
                      const logText = activityLog.map(entry => 
                        `[${entry.timestamp}] ${entry.type.toUpperCase()}: ${entry.message}${entry.details ? '\n  ' + entry.details : ''}`
                      ).join('\n\n');
                      navigator.clipboard.writeText(logText).then(() => {
                        toast.success("Activity log copied!");
                      }).catch(() => {
                        toast.error("Failed to copy");
                      });
                    }}
                    className="text-xs text-muted-foreground"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setActivityLog([])}
                    className="text-xs text-muted-foreground"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="py-2 px-4 max-h-48 overflow-y-auto">
              {activityLog.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  Activity will appear here during AI operations: audio playback, story analysis, chapter generation, and more. Use "Send Report" to email issues to us.
                </p>
              ) : (
                <div className="space-y-2">
                  {activityLog.map((entry) => (
                    <div 
                      key={entry.id} 
                      className={`text-xs p-2 rounded ${
                        entry.type === 'error' ? 'bg-red-50 border border-red-200' :
                        entry.type === 'fix' ? 'bg-green-50 border border-green-200' :
                        entry.type === 'scan' ? 'bg-blue-50 border border-blue-200' :
                        entry.type === 'audio' ? 'bg-purple-50 border border-purple-200' :
                        entry.type === 'progress' ? 'bg-slate-800/50 border border-cyan-500/25' :
                        entry.type === 'success' ? 'bg-green-50 border border-green-200' :
                        entry.type === 'story' ? 'bg-indigo-50 border border-indigo-200' :
                        'bg-card border border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-medium uppercase ${
                          entry.type === 'error' ? 'text-red-700' :
                          entry.type === 'fix' ? 'text-green-700' :
                          entry.type === 'scan' ? 'text-blue-700' :
                          entry.type === 'audio' ? 'text-purple-700' :
                          entry.type === 'progress' ? 'text-cyan-300' :
                          entry.type === 'success' ? 'text-green-700' :
                          entry.type === 'story' ? 'text-indigo-700' :
                          'text-foreground'
                        }`}>
                          {entry.type}
                        </span>
                        <span className="text-muted-foreground">{entry.timestamp}</span>
                      </div>
                      <p className="text-foreground">{entry.message}</p>
                      {entry.details && (
                        <pre className="mt-1 text-muted-foreground whitespace-pre-wrap text-xs bg-card/50 p-1 rounded">
                          {entry.details}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Import Dialog */}
      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        projectId={id}
        hasContent={Boolean(project?.content?.trim())}
        onImported={async () => {
          try {
            const res = await axios.get(`${API}/project/${id}`);
            if (res.data) setProject(res.data);
          } catch (e) { /* silent */ }
        }}
      />

      {/* Export Dialog */}
      {/* Professional Export Modal */}
      <ExportModal
        open={showExport}
        onOpenChange={setShowExport}
        project={project}
        user={user}
      />

      {/* Report Issue Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="w-[95vw] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              Report an Issue
            </DialogTitle>
            <DialogDescription>
              Send this error report to help us fix the problem.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 max-h-48 overflow-y-auto">
              <p className="text-xs font-medium text-red-700 mb-2">Error Details:</p>
              {activityLog.filter(e => e.type === 'error').slice(0, 5).map((entry, i) => (
                <div key={i} className="text-xs text-red-600 mb-1">
                  <span className="font-medium">{entry.message}</span>
                  {entry.details && <span className="text-red-500"> - {entry.details}</span>}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Your activity log and browser info will be sent to help diagnose the issue.
              {user?.email && <span> We'll respond to <strong>{user.email}</strong> if needed.</span>}
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowReportDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                className="flex-1"
                onClick={sendIssueReport}
                disabled={reportSending}
                data-testid="send-report-btn"
              >
                {reportSending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Smart Story Doctor Dialog */}
      <StoryDoctorModal
        open={showStoryDoctor}
        onOpenChange={setShowStoryDoctor}
        smartAnalysis={smartAnalysis}
        setSmartAnalysis={setSmartAnalysis}
        previewFix={previewFix}
        setPreviewFix={setPreviewFix}
        doctorTab={doctorTab}
        setDoctorTab={setDoctorTab}
        issues={issues}
        setIssues={setIssues}
        selectedIssues={selectedIssues}
        setSelectedIssues={setSelectedIssues}
        analyzingMode={analyzingMode}
        scanning={scanning}
        deepFixing={deepFixing}
        isLongStory={isLongStory}
        applyingFixIdx={applyingFixIdx}
        applyingAllFixes={applyingAllFixes}
        project={project}
        projectId={id}
        API={API}
        getScoreColor={getScoreColor}
        handleSmartAnalysis={handleSmartAnalysis}
        handleCharacterAnalysis={handleCharacterAnalysis}
        handleScanForIssues={handleScanForIssues}
        handleFixIssue={handleFixIssue}
        handleFixAllIssues={handleFixAllIssues}
        handleDeepFix={handleDeepFix}
        handleFixSingleIssue={handleFixSingleIssue}
        handleFixSelectedIssues={handleFixSelectedIssues}
        handleApplyPreviewedFix={handleApplyPreviewedFix}
        handleApplyAllAnalysisFixes={handleApplyAllAnalysisFixes}
        handleFixRecommendation={handleFixRecommendation}
        fixingIssue={fixingIssue}
      />

      {/* Smart Refine Modal */}
      <SmartRefineModal
        open={showSmartRefine}
        onOpenChange={setShowSmartRefine}
        project={project}
        projectId={id}
        onProjectUpdate={(updatedProject) => {
          if (updatedProject) {
            setProject(prev => ({ ...prev, ...updatedProject }));
          }
        }}
      />

      {/* Final Editor Modal */}
      <LocalEditorModal
        open={showFinalEditor}
        onOpenChange={setShowFinalEditor}
        project={project}
        user={user}
        onProjectUpdate={async () => {
          try {
            const res = await axios.get(`${API}/project/${id}`);
            setProject(res.data);
          } catch (e) { console.error(e); }
        }}
        onOpenExport={setShowExport}
      />

      {/* Local Find & Fix Modal */}
      <FindFixModal
        open={showFindFix}
        onOpenChange={setShowFindFix}
        project={project}
        user={user}
        onProjectUpdate={async () => {
          try {
            const res = await axios.get(`${API}/project/${id}`);
            setProject(res.data);
          } catch (e) { console.error(e); }
        }}
      />

      {/* Local Quick Fix Modal */}
      <QuickFixModal
        open={showNewQuickFix}
        onOpenChange={setShowNewQuickFix}
        project={project}
        user={user}
        onProjectUpdate={async () => {
          try {
            const res = await axios.get(`${API}/project/${id}`);
            setProject(res.data);
          } catch (e) { console.error(e); }
        }}
      />

      {/* Contact Support Modal */}
      <ContactSupportModal
        open={showContactSupport}
        onOpenChange={setShowContactSupport}
        user={user}
        project={project}
        API={API}
      />

      {/* AI Detection & Humanizer Modal */}
      <AIDetectionModal
        open={showAIDetection}
        onClose={() => setShowAIDetection(false)}
        projectId={id}
        chapters={(project?.chapter_outline || []).map((ch, idx) => ({
          chapter_number: ch.chapter_number || idx + 1,
          title: ch.title,
          content: extractChapterContent(ch.chapter_number || idx + 1)
        }))}
        currentChapter={selectedChapterNum || 1}
        onContentUpdate={(chapterNum, newContent) => {
          // Update the chapter content in project
          updateChapterContent(chapterNum, newContent);
        }}
      />

      {/* Synopsis Ladder Modal */}
      <SynopsisLadderModal
        open={showSynopsisLadder}
        onClose={() => setShowSynopsisLadder(false)}
        projectId={id}
        projectTitle={project?.title}
        chapters={(project?.chapter_outline || []).map((ch, idx) => ({
          chapter_number: ch.chapter_number || idx + 1,
          title: ch.title,
          content: extractChapterContent(ch.chapter_number || idx + 1)
        }))}
        currentChapter={selectedChapterNum || 1}
      />

      {/* Canon Lock Modal */}
      <CanonLockModal
        open={showCanonLock}
        onClose={() => setShowCanonLock(false)}
        projectId={id}
        chapters={(project?.chapter_outline || []).map((ch, idx) => ({
          chapter_number: ch.chapter_number || idx + 1,
          title: ch.title,
          content: extractChapterContent(ch.chapter_number || idx + 1),
          summary: ch.summary || project?.chapter_summaries?.[ch.chapter_number || idx + 1]
        }))}
        currentChapter={selectedChapterNum || 1}
        storyBible={{
          characters: project?.characters || [],
          world_rules: project?.world_rules || [],
          timeline: project?.story_bible?.timeline || [],
          plot_points: project?.story_bible?.plot_points || []
        }}
      />

      {/* Character Portrait Modal */}
      <CharacterPortraitModal
        open={showCharacterPortraits}
        onClose={() => setShowCharacterPortraits(false)}
        projectId={id}
        characters={project?.characters || []}
      />

      {/* Nova Edit Dialog - AI-Powered Chapter Rewrite */}
      <Dialog open={showQuickFix} onOpenChange={(open) => { if (!open) { setQuickFixResult(null); setQuickFixProgress({ status: '', progress: 0, message: '' }); } setShowQuickFix(open); }}>
        <DialogContent className={`w-[95vw] bg-slate-900 border-cyan-500/20 mx-auto ${quickFixResult ? 'max-w-4xl' : 'max-w-lg'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-100">
              <Zap className="w-5 h-5 text-cyan-400" />
              {quickFixResult ? 'Review Changes' : quickFixLoading ? 'Nova is rewriting...' : 'AI Rewrite (Nova)'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {quickFixResult 
                ? `Chapter ${quickFixResult.chapterNumber} — Review changes below`
                : quickFixLoading 
                  ? quickFixProgress.message
                  : 'Select a chapter and tell Nova what to change'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Loading State with Progress */}
          {quickFixLoading && !quickFixResult && (
            <div className="py-6 space-y-4">
              <div className="flex items-center justify-center gap-3">
                <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
                <div className="text-left">
                  <p className="text-sm font-medium text-slate-200">{quickFixProgress.message}</p>
                  <p className="text-xs text-slate-500">Chapter {quickFixChapter}</p>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-violet-500 to-cyan-500 transition-all duration-500 ease-out"
                  style={{ width: `${quickFixProgress.progress}%` }}
                />
              </div>
              
              <div className="flex justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <span className={quickFixProgress.status === 'locating' || quickFixProgress.progress > 10 ? 'text-cyan-400' : ''}>
                    {quickFixProgress.progress > 10 ? '✓' : '○'} Locate Scene
                  </span>
                </span>
                <span className={quickFixProgress.status === 'rewriting' || quickFixProgress.progress > 50 ? 'text-cyan-400' : ''}>
                  {quickFixProgress.progress > 50 ? '✓' : '○'} Rewrite
                </span>
                <span className={quickFixProgress.progress === 100 ? 'text-cyan-400' : ''}>
                  {quickFixProgress.progress === 100 ? '✓' : '○'} Complete
                </span>
              </div>
            </div>
          )}
          
          {!quickFixResult && !quickFixLoading ? (
            <>
              <div className="space-y-4 py-2">
                {/* Chapter selector */}
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Which chapter?</label>
                  <select
                    value={quickFixChapter}
                    onChange={(e) => setQuickFixChapter(parseInt(e.target.value))}
                    className="w-full rounded-md bg-slate-800 border border-slate-700 text-slate-200 px-3 py-2 text-sm"
                    data-testid="quick-fix-chapter-select"
                  >
                    {(project.chapter_outline || []).map((ch) => (
                      <option key={ch.number} value={ch.number}>
                        Chapter {ch.number}: {ch.title || `Chapter ${ch.number}`}
                      </option>
                    ))}
                    {(!project.chapter_outline || project.chapter_outline.length === 0) && (
                      Array.from({ length: Math.max(1, (project.content?.match(/---\s*CHAPTER\s+\d+/gi) || []).length) }, (_, i) => (
                        <option key={i + 1} value={i + 1}>Chapter {i + 1}</option>
                      ))
                    )}
                  </select>
                </div>
                
                {/* Instruction */}
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">What needs to change?</label>
                  <textarea
                    value={quickFixInstruction}
                    onChange={(e) => setQuickFixInstruction(e.target.value)}
                    placeholder="E.g., 'They linger too long on picking up papers. This scene needs to be faster and more natural.'"
                    className="w-full rounded-md bg-slate-800 border border-slate-700 text-slate-200 px-3 py-2 text-sm min-h-[100px] resize-none placeholder:text-slate-500"
                    data-testid="quick-fix-instruction"
                  />
                </div>
                
                {/* Examples */}
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <p className="text-xs font-medium text-slate-400 mb-2">Examples:</p>
                  <div className="space-y-1">
                    {[
                      "The paper-dropping scene goes on too long. Make it 1-2 paragraphs max.",
                      "Remove the part where he stands there staring at her — it's unrealistic.",
                      "The dialogue between them feels forced. Make it more natural.",
                      "This chapter ends too abruptly. Add a smoother transition."
                    ].map((example, i) => (
                      <button
                        key={i}
                        onClick={() => setQuickFixInstruction(example)}
                        className="block w-full text-left text-xs text-slate-500 hover:text-cyan-400 transition-colors py-0.5 cursor-pointer"
                      >
                        "{example}"
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowQuickFix(false)}
                  className="border-slate-600 text-slate-300 w-full sm:w-auto"
                  disabled={quickFixLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleQuickFix}
                  disabled={quickFixLoading || !quickFixInstruction.trim()}
                  className="bg-violet-600 hover:bg-violet-500 text-white w-full sm:w-auto"
                  data-testid="quick-fix-apply-btn"
                >
                  {quickFixLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Fixing...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Find &amp; Fix
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : quickFixResult ? (
            <>
              {/* Review Step — show original vs fixed side by side */}
              <div className="space-y-4 py-2">
                {/* Instruction reminder */}
                <div className="bg-violet-900/20 border border-violet-500/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-violet-300 mb-1">Your instruction:</p>
                  <p className="text-sm text-slate-300 italic">"{quickFixInstruction}"</p>
                </div>
                
                {/* Located range info */}
                {quickFixResult.locatedRange && (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="bg-cyan-900/30 text-cyan-300 px-2 py-0.5 rounded">
                      Paragraphs {quickFixResult.locatedRange.startParagraph + 1}–{quickFixResult.locatedRange.endParagraph + 1}
                    </span>
                    <span>of {quickFixResult.locatedRange.totalParagraphs} in Chapter {quickFixResult.chapterNumber}</span>
                    {quickFixResult.locatedRange.confidence && (
                      <span className="text-slate-500">
                        ({Math.round(quickFixResult.locatedRange.confidence * 100)}% confidence)
                      </span>
                    )}
                  </div>
                )}
                
                {/* Side-by-side comparison */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Original */}
                  <div>
                    <p className="text-sm font-medium text-red-400 mb-2 flex items-center gap-1">
                      <X className="w-4 h-4" />
                      Original Section:
                    </p>
                    <div 
                      className="bg-red-950/20 border border-red-900/30 rounded-lg p-4 max-h-[350px] overflow-y-auto text-sm text-slate-300 leading-relaxed whitespace-pre-wrap"
                      data-testid="quick-fix-original"
                    >
                      {quickFixResult.originalText || 'Original text not available'}
                    </div>
                  </div>
                  
                  {/* Fixed */}
                  <div>
                    <p className="text-sm font-medium text-emerald-400 mb-2 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Rewritten Section:
                    </p>
                    <div 
                      className="bg-emerald-950/20 border border-emerald-900/30 rounded-lg p-4 max-h-[350px] overflow-y-auto text-sm text-slate-200 leading-relaxed whitespace-pre-wrap"
                      data-testid="quick-fix-result"
                    >
                      {quickFixResult.fixedText}
                    </div>
                  </div>
                </div>
                
                {/* Word count change */}
                {quickFixResult.originalText && quickFixResult.fixedText && (
                  <div className="text-xs text-slate-500 text-center">
                    {(() => {
                      const origWords = (quickFixResult.originalText || '').split(/\s+/).filter(w => w).length;
                      const fixedWords = (quickFixResult.fixedText || '').split(/\s+/).filter(w => w).length;
                      const diff = fixedWords - origWords;
                      const diffStr = diff > 0 ? `+${diff}` : diff.toString();
                      return `${origWords} → ${fixedWords} words (${diffStr})`;
                    })()}
                  </div>
                )}
              </div>
              
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={handleRejectQuickFix}
                  className="border-red-600/50 text-red-400 hover:bg-red-900/30 w-full sm:w-auto"
                  data-testid="quick-fix-reject-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Discard
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setQuickFixResult(null); setQuickFixProgress({ status: '', progress: 0, message: '' }); }}
                  className="border-slate-600 text-slate-300 w-full sm:w-auto"
                >
                  Try Again
                </Button>
                <Button
                  onClick={handleAcceptQuickFix}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white w-full sm:w-auto"
                  data-testid="quick-fix-accept-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save to Story
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════
          CHAPTER EDITOR DIALOG — Direct text editing
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={showChapterEditor} onOpenChange={setShowChapterEditor}>
        <DialogContent className="sm:max-w-4xl bg-[#1F2937] border-cyan-500/20 flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-100">
              <Edit2 className="w-5 h-5 text-violet-400" />
              Edit Chapter
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Select a chapter and edit the text directly. Changes will be saved to your project.
            </DialogDescription>
          </DialogHeader>
          
          {/* Chapter Selector */}
          <div className="flex items-center gap-3 py-2">
            <span className="text-sm text-slate-400">Chapter:</span>
            <Select
              value={chapterEditorNum.toString()}
              onValueChange={(val) => openChapterEditor(parseInt(val))}
            >
              <SelectTrigger className="w-32 bg-slate-800 border-cyan-500/30">
                <SelectValue placeholder="Chapter" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-cyan-500/20">
                {Array.from({ length: (project.content?.match(/---\s*CHAPTER\s+\d+/gi) || []).length || 1 }, (_, i) => (
                  <SelectItem key={i + 1} value={(i + 1).toString()}>
                    Chapter {i + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1 min-h-0 py-2">
            <textarea
              value={chapterEditorText}
              onChange={(e) => setChapterEditorText(e.target.value)}
              className="w-full h-[55vh] bg-slate-800/80 border border-cyan-500/15 rounded-lg p-4 text-slate-200 text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500/40 font-mono"
              placeholder="Chapter content..."
              data-testid="chapter-editor-textarea"
            />
            
            <div className="flex justify-between items-center mt-2 text-xs text-slate-500">
              <span>{chapterEditorText.split(/\s+/).filter(w => w).length} words</span>
              <span>{chapterEditorText.length} characters</span>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowChapterEditor(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveChapterEdit}
              disabled={chapterEditorSaving}
              variant="gold"
              data-testid="chapter-editor-save-btn"
            >
              {chapterEditorSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Chapter
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Story Bible Modal */}
      <StoryBibleModal
        open={showStoryBible}
        onOpenChange={setShowStoryBible}
        storyBible={storyBible}
        setStoryBible={setStoryBible}
        project={project}
        setProject={setProject}
        projectId={id}
        API={API}
        loadStoryBible={loadStoryBible}
        tensionLevelColors={tensionLevelColors}
        tensionLevelNames={tensionLevelNames}
        tensionTemplates={tensionTemplates}
        onShowTensionModal={setShowTensionModal}
        onShowForeshadowingModal={setShowForeshadowingModal}
        loadTensionCurve={loadTensionCurve}
      />

      {/* Back Cover Blurb Modal */}
      <BackCoverBlurbModal
        open={showBackCover}
        onOpenChange={setShowBackCover}
        project={project}
        projectId={id}
        API={API}
        onProjectUpdate={(updates) => setProject(prev => ({ ...prev, ...updates }))}
      />

      {/* Book Cover Design Modal */}
      <BookCoverDesignModal
        open={showCoverDesign}
        onOpenChange={setShowCoverDesign}
        project={project}
        projectId={id}
        API={API}
      />

      {/* Writing Enhancement Tools Dialog */}
      <WritingToolsModal
        open={showWritingTools}
        onOpenChange={setShowWritingTools}
        selectedText={selectedText}
        setSelectedText={setSelectedText}
        rewriteResult={rewriteResult}
        setRewriteResult={setRewriteResult}
        rewriteLoading={rewriteLoading}
        handleRewrite={handleRewrite}
        applyRewrite={applyRewrite}
      />

      {/* Manuscript Analysis Dashboard Modal */}
      <ManuscriptAnalysisModal
        open={showAnalysisDashboard}
        onOpenChange={setShowAnalysisDashboard}
        project={project}
        projectId={id}
        API={API}
      />

      {/* Tension Curve Modal */}
      <TensionCurveModal
        open={showTensionModal}
        onOpenChange={setShowTensionModal}
        tensionCurve={tensionCurve}
        setTensionCurve={setTensionCurve}
        tensionLevelColors={tensionLevelColors}
        tensionLevelNames={tensionLevelNames}
        tensionTemplates={tensionTemplates}
        loadingTension={loadingTension}
        project={project}
        projectId={id}
        API={API}
        loadTensionCurve={loadTensionCurve}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        createTensionCurve={createTensionCurve}
      />

      {/* Chapter Planning Modal */}
      <ChapterPlanningModal
        open={showChapterPlanning}
        onOpenChange={setShowChapterPlanning}
        chapterPlan={chapterPlan}
        setChapterPlan={setChapterPlan}
        generatingChapter={generatingChapter}
        project={project}
        projectId={id}
        API={API}
        onStartWriting={(plan) => {
          setChapterPlan(plan);
          setShowChapterPlanning(false);
        }}
        approveAndGenerateChapter={approveAndGenerateChapter}
      />

      {/* Foreshadowing Planning Modal */}
      <Dialog open={showForeshadowingModal} onOpenChange={setShowForeshadowingModal}>
        <DialogContent className="w-[95vw] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-indigo-600" />
              Plan a Future Reveal
            </DialogTitle>
            <DialogDescription>
              Shweebum will automatically plant subtle hints in earlier chapters
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-foreground">What will be revealed?</label>
              <div className="relative">
                <textarea 
                  className="w-full border rounded p-2 pr-10 mt-1 text-sm"
                  rows={2}
                  placeholder="e.g., The CEO is secretly the villain"
                  value={newReveal.description}
                  onChange={(e) => setNewReveal(prev => ({ ...prev, description: e.target.value }))}
                />
                <SpeechToTextButton
                  onTranscript={(text, append) => {
                    setNewReveal(prev => ({ 
                      ...prev, 
                      description: append && prev.description ? prev.description + ' ' + text : text 
                    }));
                  }}
                  className="absolute right-2 top-2 h-6 w-6"
                  size="icon"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-foreground">Reveal in which chapter?</label>
              <select 
                className="w-full border rounded p-2 mt-1 text-sm"
                value={newReveal.target_chapter}
                onChange={(e) => setNewReveal(prev => ({ ...prev, target_chapter: e.target.value }))}
              >
                <option value="">Select chapter...</option>
                {project?.chapter_outline?.map((ch) => (
                  <option key={ch.number} value={ch.number}>Chapter {ch.number}: {ch.title}</option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground">Importance</label>
                <select 
                  className="w-full border rounded p-2 mt-1 text-sm"
                  value={newReveal.importance}
                  onChange={(e) => setNewReveal(prev => ({ ...prev, importance: e.target.value }))}
                >
                  <option value="major">Major reveal</option>
                  <option value="minor">Minor reveal</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Hint Style</label>
                <select 
                  className="w-full border rounded p-2 mt-1 text-sm"
                  value={newReveal.hint_style}
                  onChange={(e) => setNewReveal(prev => ({ ...prev, hint_style: e.target.value }))}
                >
                  <option value="subtle">Subtle (blink and miss)</option>
                  <option value="moderate">Moderate (attentive reader notices)</option>
                  <option value="obvious">Obvious (clear hints)</option>
                </select>
              </div>
            </div>
            
            <div className="bg-indigo-50 p-3 rounded border border-indigo-200">
              <p className="text-xs text-indigo-700">
                <strong>How it works:</strong> Shweebum will create 2-3 hints and automatically include them when you generate earlier chapters. The hints will be woven naturally into the narrative.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForeshadowingModal(false)}>Cancel</Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={planForeshadowing}
              disabled={planningForeshadow}
            >
              {planningForeshadow ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Planning...</> : 'Plan Foreshadowing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ask Before Adding Proposal Modal */}
      <Dialog open={!!pendingProposal} onOpenChange={() => setPendingProposal(null)}>
        <DialogContent className="w-[95vw] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShweebumCat size="sm" />
              Shweebum Asks...
            </DialogTitle>
          </DialogHeader>
          
          {pendingProposal && (
            <div className="py-4">
              <Card className="bg-slate-800/50 border-cyan-500/25">
                <CardContent className="p-4">
                  <p className="text-sm text-cyan-200 mb-2">
                    <strong>I'd like to introduce:</strong> {pendingProposal.element_name}
                  </p>
                  <p className="text-xs text-cyan-300">
                    <strong>Type:</strong> {pendingProposal.proposal_type}
                  </p>
                  <p className="text-xs text-cyan-300 mt-1">
                    <strong>Reason:</strong> {pendingProposal.reason}
                  </p>
                </CardContent>
              </Card>
              
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Should I add this to your story?
              </p>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleProposalResponse(false)}>
              No, Don't Add
            </Button>
            <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => handleProposalResponse(true)}>
              Yes, Add It
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Required Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="w-[95vw] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="font-literary text-xl flex items-center gap-2">
              <Zap className="w-5 h-5 text-cyan-400" />
              Upgrade to Unlock
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="text-center mb-4">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-cyan-900/20 to-blue-900/20 rounded-full flex items-center justify-center mb-3">
                <Lock className="w-8 h-8 text-cyan-400" />
              </div>
              <p className="text-foreground">
                <span className="font-semibold">{upgradeFeature}</span> is a premium feature.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Upgrade to <span className="font-semibold text-cyan-300">Pro</span> or <span className="font-semibold text-purple-700">Author</span> for unlimited AI generation, full chapters, and all of Shweebum's magic.
              </p>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span>Unlimited AI generations</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span>Full chapter generation (~2,000 words)</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span>Story Doctor, Plot Suggestions & more</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowUpgradeModal(false)}
              >
                Maybe Later
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/pricing');
                }}
                data-testid="upgrade-modal-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                See Plans
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Chapter Outline Dialog */}
      <Dialog open={showAddChapter} onOpenChange={setShowAddChapter}>
        <DialogContent className="max-w-lg" data-testid="add-chapter-dialog">
          <DialogHeader>
            <DialogTitle>Add Chapter {(project?.chapter_outline?.length || 0) + 1 > 0 ? `${Math.max(0, ...(project?.chapter_outline || []).map(c => c.number || 0)) + 1}` : ""} to Your Outline</DialogTitle>
            <DialogDescription>
              Write the chapter outline exactly as you want it — multiple paragraphs are fine.
              It's saved as the next chapter and generation will follow it verbatim.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="add-ch-title">Chapter title (optional)</Label>
              <Input
                id="add-ch-title"
                value={addChapterTitle}
                onChange={(e) => setAddChapterTitle(e.target.value)}
                placeholder="e.g. THE ROAD NORTH"
                data-testid="add-chapter-title-input"
              />
            </div>
            <div>
              <Label htmlFor="add-ch-summary">Chapter outline</Label>
              <Textarea
                id="add-ch-summary"
                value={addChapterSummary}
                onChange={(e) => setAddChapterSummary(e.target.value)}
                placeholder="What happens in this chapter — events, reveals, how it ends. Write as much detail as you want."
                className="min-h-[180px]"
                data-testid="add-chapter-summary-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddChapter(false)} data-testid="add-chapter-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleAddChapterOutline}
              disabled={addingChapter}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="add-chapter-save-btn"
            >
              {addingChapter ? "Adding..." : "Add Chapter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direction Choice Dialog - Add to chapter or start new */}
      <Dialog open={showDirectionChoice} onOpenChange={(open) => { if (!open) { setShowDirectionChoice(false); setDirectionMode(null); } }}>
        <DialogContent className="w-[95vw] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-600" />
              How should this be added?
            </DialogTitle>
            <DialogDescription>
              {selectedDirection?.title && <span className="font-medium text-foreground">"{selectedDirection.title}"</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <button
              className="w-full p-4 rounded-lg border-2 border-border hover:border-cyan-500/40 hover:bg-slate-800/50 transition-all text-left group"
              onClick={() => handleGenerateFromDirection('continue')}
              data-testid="direction-add-to-chapter"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-800/60 flex items-center justify-center group-hover:bg-cyan-900/30 transition-colors">
                  <PenTool className="w-5 h-5 text-cyan-300" />
                </div>
                <div>
                  <p className="font-semibold text-slate-200">Add to current chapter</p>
                  <p className="text-xs text-muted-foreground">Continue where you left off</p>
                </div>
              </div>
            </button>
            <button
              className="w-full p-4 rounded-lg border-2 border-border hover:border-purple-400 hover:bg-purple-50 transition-all text-left group"
              onClick={() => { setShowDirectionChoice(false); setShowChapterNameChoice(true); }}
              data-testid="direction-new-chapter"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <BookOpen className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <p className="font-semibold text-slate-200">Begin a new chapter</p>
                  <p className="text-xs text-muted-foreground">Start a fresh chapter with this direction</p>
                </div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chapter Name Choice Dialog */}
      <Dialog open={showChapterNameChoice} onOpenChange={(open) => { if (!open) setShowChapterNameChoice(false); }}>
        <DialogContent className="w-[95vw] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              Name your chapter
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <button
              className="w-full p-4 rounded-lg border-2 border-border hover:border-purple-400 hover:bg-purple-50 transition-all text-left group"
              onClick={() => handleGenerateFromDirection('new_chapter', '')}
              data-testid="let-shweebum-name"
            >
              <div className="flex items-center gap-3">
                <ShweebumCat size="sm" />
                <div>
                  <p className="font-semibold text-slate-200">Let Shweebum name it</p>
                  <p className="text-xs text-muted-foreground">Shweebum will pick a title that fits the direction</p>
                </div>
              </div>
            </button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">or</span></div>
            </div>
            <div>
              <Label className="text-sm font-medium">Name it yourself</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={customChapterName}
                  onChange={(e) => setCustomChapterName(e.target.value)}
                  placeholder="Chapter title..."
                  className="flex-1"
                  data-testid="custom-chapter-name-input"
                />
                <Button 
                  onClick={() => {
                    if (customChapterName.trim()) {
                      handleGenerateFromDirection('new_chapter', customChapterName.trim());
                    } else {
                      toast.error("Enter a chapter name");
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="custom-chapter-name-submit"
                >
                  Go
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Story Direction Modal */}
      <StoryDirectionModal
        open={showDirectionModal}
        onOpenChange={setShowDirectionModal}
        directionType={directionType}
        setDirectionType={setDirectionType}
        assistantLoading={assistantLoading}
        assistantResults={assistantResults}
        setAssistantResults={setAssistantResults}
        handleStoryDirection={handleStoryDirection}
        setGenerateOptions={setGenerateOptions}
      />

      {/* Story Architect Modal (feature-flagged, read-only) */}
      {storyArchitectEnabled && (
        <StoryArchitectModal
          open={showStoryArchitect}
          onOpenChange={setShowStoryArchitect}
          projectId={id}
        />
      )}

      {/* Story Navigator Modal */}
      <StoryNavigatorModal
        open={showStoryNavigator}
        onOpenChange={setShowStoryNavigator}
        project={project}
        projectId={id}
        API={API}
        storyAnalysis={storyAnalysis}
        analyzingStory={analyzingStory}
        weaving={weaving}
        weaveDirection={weaveDirection}
        setWeaveDirection={setWeaveDirection}
        weaveLength={weaveLength}
        setWeaveLength={setWeaveLength}
        handleAnalyzeStoryPosition={handleAnalyzeStoryPosition}
        handleWeaveContent={handleWeaveContent}
      />

      {/* Suggest Names Modal */}
      <Dialog open={showNamesModal} onOpenChange={setShowNamesModal}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[80vh] overflow-hidden flex flex-col mx-auto">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-600" />
              Character Names
            </DialogTitle>
            <DialogDescription>
              Get name suggestions or rename characters throughout your story
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 flex-1 overflow-y-auto">
            {/* Existing characters from story - click to select for replacement */}
            {project?.characters && project.characters.length > 0 && (
              <div className="bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg p-4">
                <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">Your Characters (click to replace)</h4>
                <div className="flex flex-wrap gap-2">
                  {project.characters.map((char, idx) => (
                    <Button
                      key={idx}
                      variant={nameToReplace === char.name ? "default" : "outline"}
                      size="sm"
                      className={nameToReplace === char.name ? "bg-purple-600" : ""}
                      onClick={() => {
                        setNameToReplace(char.name);
                        toast.info(`Selected "${char.name}" - now pick a new name below or type one`);
                      }}
                    >
                      {char.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Name replacement tool */}
            <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">Replace a Name Throughout Your Book</h4>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Current Name (in your story)</Label>
                  <Input 
                    placeholder="Click a character above or type name" 
                    value={nameToReplace}
                    onChange={(e) => setNameToReplace(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">New Name</Label>
                  <Input 
                    placeholder="Click a suggestion or type new name"
                    value={newNameValue}
                    onChange={(e) => setNewNameValue(e.target.value)}
                  />
                </div>
              </div>
              <Button 
                onClick={() => handleReplaceName(nameToReplace, newNameValue)}
                className="mt-2 w-full bg-purple-600 hover:bg-purple-700"
                disabled={!nameToReplace || !newNameValue}
              >
                Replace All Instances in Entire Book
              </Button>
            </div>

            {/* Suggested names */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-foreground">Suggested Names (click to use)</h4>
                <Button variant="outline" size="sm" onClick={handleSuggestNames} disabled={assistantLoading}>
                  {assistantLoading ? "Loading..." : "Refresh"}
                </Button>
              </div>
              
              {assistantLoading && (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Generating American-style names...</p>
                </div>
              )}
              
              {suggestedNames.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {suggestedNames.map((name, idx) => (
                    <Card key={idx} className="bg-card cursor-pointer hover:border-purple-400 transition-colors" onClick={() => {
                      if (nameToReplace) {
                        setNewNameValue(name.name);
                        toast.success(`"${name.name}" selected as replacement for "${nameToReplace}"`);
                      } else {
                        setNewNameValue(name.name);
                        toast.info(`Selected "${name.name}" - now select a character to replace above`);
                      }
                    }}>
                      <CardContent className="p-3">
                        <p className="font-semibold text-foreground">{name.name}</p>
                        <p className="text-xs text-muted-foreground">{name.gender} • {name.vibe}</p>
                        <p className="text-xs text-muted-foreground mt-1">{name.good_for}</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-2 w-full text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (nameToReplace) {
                              setNewNameValue(name.name);
                              toast.success(`Ready to replace "${nameToReplace}" with "${name.name}"`);
                            } else {
                              setNewNameValue(name.name);
                              toast.info(`Selected "${name.name}" - enter the name to replace above`);
                            }
                          }}
                        >
                          Use This Name
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suggest Characters Modal */}
      <SuggestCharactersModal
        open={showCharacterModal}
        onOpenChange={setShowCharacterModal}
        characterMode={characterMode}
        setCharacterMode={setCharacterMode}
        characterChapter={characterChapter}
        setCharacterChapter={setCharacterChapter}
        customCharacter={customCharacter}
        setCustomCharacter={setCustomCharacter}
        handleSuggestCharacters={handleSuggestCharacters}
        handleInsertCharacter={handleInsertCharacter}
        handleInsertCustomCharacter={handleInsertCustomCharacter}
        assistantLoading={assistantLoading}
        assistantResults={assistantResults}
        characterInsertProgress={characterInsertProgress}
        project={project}
        projectId={id}
        API={API}
      />

      {/* Plot Points Modal */}
      <Dialog open={showPlotModal} onOpenChange={setShowPlotModal}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[80vh] overflow-hidden flex flex-col mx-auto">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-cyan-400" />
              Plot Point Suggestions
            </DialogTitle>
            <DialogDescription>
              Click a plot point to insert it into your story
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {assistantLoading && (
              <div className="text-center py-8">
                <ShweebumCat size="md" speaking />
                <p className="text-muted-foreground mt-4">Analyzing your story and crafting plot ideas...</p>
              </div>
            )}
            
            {assistantResults && Array.isArray(assistantResults) && assistantResults.map((plot, idx) => (
              <Card key={idx} className="bg-card hover:border-cyan-500/40 transition-colors cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-cyan-400">{idx + 1}. {plot.title}</h4>
                      <p className="text-sm text-foreground mt-1">{plot.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        <span className="font-medium">Type:</span> {plot.type} | <span className="font-medium">Impact:</span> {plot.impact}
                      </p>
                    </div>
                    <Button 
                      size="sm"
                      className="bg-cyan-600 hover:bg-cyan-700 shrink-0"
                      onClick={async () => {
                        if (plot.insert_text) {
                          const newContent = (project?.content || '') + '\n\n' + plot.insert_text;
                          setProject(prev => ({ ...prev, content: newContent }));
                          try {
                            await axios.put(`${API}/project/${id}`, { content: newContent });
                            toast.success(`"${plot.title}" added to your story!`);
                            setShowPlotModal(false);
                          } catch (e) {
                            toast.error("Failed to save");
                          }
                        } else {
                          toast.info("This plot point needs to be written - use it as inspiration!");
                        }
                      }}
                    >
                      Insert
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {assistantResults && typeof assistantResults === 'string' && (
              <div className="prose prose-sm text-foreground whitespace-pre-wrap">
                {assistantResults}
              </div>
            )}
            
            <Button 
              variant="outline" 
              className="mt-4 w-full"
              onClick={handleSuggestPlotPoints}
              disabled={assistantLoading}
            >
              Generate New Plot Points
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Character Editor Dialog */}
      <Dialog open={showCharacterEditor} onOpenChange={setShowCharacterEditor}>
        <DialogContent className="sm:max-w-4xl flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              Edit Characters
            </DialogTitle>
            <DialogDescription>
              Add or edit character physical descriptions. These details will be used when generating chapters.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            <CharacterEditor
              characters={project?.characters || []}
              onChange={async (newCharacters) => {
                // Update local state
                setProject(prev => ({ ...prev, characters: newCharacters }));
                
                // Save to database
                try {
                  await axios.patch(`${API}/projects/${id}`, {
                    characters: newCharacters
                  });
                  toast.success("Characters saved!");
                } catch (error) {
                  console.error("Failed to save characters:", error);
                  toast.error("Failed to save characters");
                }
              }}
              maxCharacters={30}
              showTitle={false}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCharacterEditor(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fidelity Report Modal (P0 #3) */}
      <Dialog open={showFidelityReport} onOpenChange={setShowFidelityReport}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Faithfulness Report</DialogTitle>
          </DialogHeader>
          {(() => {
            const fid = project?.story_bible?.fidelity_report || {};
            const canon = project?.story_bible?.canon || {};
            const score = fid.overall_fidelity_score || 0;
            const issues = fid.issues_found;
            const ScoreColor = score >= 95 ? 'text-emerald-400' : score >= 80 ? 'text-amber-400' : 'text-rose-400';
            return (
              <div className="space-y-4 py-2" data-testid="fidelity-report-modal">
                <div className="flex items-center gap-4">
                  <div className={`text-5xl font-bold ${ScoreColor}`}>{score}%</div>
                  <div>
                    <div className="text-lg text-white">{issues ? 'Some canon was altered' : 'All canon honored'}</div>
                    <div className="text-xs text-zinc-400">Compared against the Story DNA extracted from your idea.</div>
                  </div>
                </div>
                <ReportLine ok={(canon.characters || []).length} label="characters preserved" issues={fid.missing_characters} renamed={fid.renamed_characters} />
                <ReportLine ok={(canon.required_events || []).length} label="required events included" issues={fid.missing_events} />
                <ReportLine ok={canon.required_ending && canon.required_ending !== 'open' ? 1 : 0} label="required ending preserved" issues={fid.ending_changed ? [fid.ending_change_reason || 'changed'] : []} />
                <ReportLine ok={(canon.exact_phrases || []).length} label="exact phrases preserved" issues={fid.missing_exact_phrases} />
                <ReportLine ok={(canon.forbidden_changes || []).length} label="forbidden changes respected" issues={fid.violated_forbidden} />
                {(fid.invented_major_characters || []).length > 0 && (
                  <div className="text-sm text-rose-300">
                    ⚠ Invented characters not in your idea: {fid.invented_major_characters.join(', ')}
                  </div>
                )}
                {!issues && <div className="text-sm text-emerald-300">No violations detected.</div>}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFidelityReport(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ReportLine = ({ ok, label, issues = [], renamed = [] }) => {
  const failures = [...(issues || []), ...(renamed || [])];
  const hasFail = failures.length > 0;
  return (
    <div className="text-sm">
      <div className={hasFail ? 'text-amber-300' : 'text-emerald-300'}>
        {hasFail ? '⚠' : '✓'} {ok - failures.length}/{ok || 0} {label}
      </div>
      {hasFail && (
        <ul className="text-xs text-rose-300 ml-5 mt-1 list-disc">
          {failures.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
    </div>
  );
};

// Import Dialog


export default WritingPage;
